import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, getFileUrl } from "@/lib/api";
import { copyForSpreadsheet, copyDigits } from "@/lib/clipboard";
import CopyChip from "@/components/CopyChip";
import StaffSheetsModal from "@/components/StaffSheetsModal";
import ResendModal from "@/components/ResendModal";
import LpgSearchModal from "@/components/LpgSearchModal";
import ResendCreateModal from "@/components/ResendCreateModal";
import { MessageContextMenu } from "@/components/MessageContextMenu";
import { MessageInfoModal, BubbleTicks } from "@/components/MessageInfoModal";
import GroupDescriptionEditor from "@/components/GroupDescriptionEditor";
import DeliveryLoginTab from "@/components/DeliveryLoginTab";
import { openChatSocket } from "@/lib/ws";
import { useNameSort } from "@/lib/useNameSort";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ResendBulkPasteModal } from "@/components/ResendBulkPaste";
import {
  Send,
  Paperclip,
  LogOut,
  Plus,
  Users,
  MessageCircle,
  Shield,
  Hash,
  Loader2,
  Trash2,
  ArrowDown,
  Menu,
  ArrowLeft,
  Reply,
  Smile,
  X,
  Clock,
  AlertCircle,
  WifiOff,
  CheckCircle2,
  XCircle,
  RotateCw,
  Flame,
  Eraser,
  UserPlus,
  UserMinus,
  Copy,
  Download,
  Circle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ClipboardPaste,
  Inbox,
  Flag,
  FileSpreadsheet,
  Search,
  Home,
  Radio,
  ClipboardList,
  BellRing,
  PackageCheck,
  Camera,
  Info,
  Check,
  CheckCheck,
  Landmark,
  IdCard,
  Wallet,
  CalendarClock,
  Pin,
  Star,
  Target,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥"];

/**
 * WhatsApp-style attachment download — fetches the file as a blob using
 * the authenticated URL (token in query param), then triggers a
 * download via a hidden anchor with `download` attribute so the file
 * saves directly instead of opening in a new tab. Falls back to a
 * plain new-tab open if fetch fails (offline / CORS).
 */
async function downloadAttachment(att, authedUrl) {
  const name = att?.name || "file";
  try {
    const res = await fetch(authedUrl, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke after a tick so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
  } catch (e) {
    // Fallback: let the browser try to open the URL directly. Users on
    // very old browsers will still get a working download.
    try {
      window.open(authedUrl, "_blank", "noopener,noreferrer");
    } catch {
      /* ignore */
    }
  }
}


// Only Bharat Gas / BPCL groups get the Resend DAC + New Resend workflow —
// Indane and HPCL groups follow a different reconciliation path. Detected
// via keyword match on the group name (case-insensitive). Keywords chosen
// to cover the common variants agencies use: "bharat", "bpcl", "bharat gas",
// "bpcl gas".
function isBharatGasGroup(group) {
  if (!group) return false;
  const name = String(group.name || group.title || "").toLowerCase();
  if (!name) return false;
  return /(^|[^a-z])(bharat(\s*gas)?|bpcl(\s*gas)?)([^a-z]|$)/.test(name);
}

// Tap-to-copy — strip non-digits and cap at 17 chars (LPG-ID max length).
// Used by the agency drawer so a one-tap paste flows straight into the
// gas portal without any manual cleanup.
// CopyChip + copyDigits helpers were extracted to shared files (Feb 2026):
//   /app/frontend/src/components/CopyChip.jsx
//   /app/frontend/src/lib/clipboard.js (copyDigits)



function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// IST day key — "YYYY-MM-DD" in Asia/Kolkata. Used to group messages for the
// WhatsApp-style date separator between bubble runs.
function istDateKey(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const ist = new Date(d.getTime() + (5.5 * 60 + d.getTimezoneOffset()) * 60000);
    return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}-${String(ist.getDate()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

// Pretty date for the separator pill — "Today" / "Yesterday" / "10 Jun 2026".
function prettyDateLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = istDateKey(new Date().toISOString());
    const yest = istDateKey(new Date(Date.now() - 86400000).toISOString());
    const key = istDateKey(iso);
    if (key === today) return "Today";
    if (key === yest) return "Yesterday";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
}

// Robust clipboard write — sends BOTH plain text (\t \n) AND a styled HTML
// table. Excel / Google Sheets prefer the HTML payload, which preserves
// per-cell text formatting so 16-digit LPG IDs don't get converted to
// scientific notation (the "1.23E+15" / "e" bug the user reported). Falls
// back to text/plain when ClipboardItem isn't available.
// Feb 2026 refactor: `copyForSpreadsheet` lives in /app/frontend/src/lib/clipboard.js
// so extracted subcomponents (StaffSheetsModal, etc.) can share the same
// implementation. Re-exported from the module top-level `import` above.
// Callers inside Chat.jsx still say `copyForSpreadsheet(rows)` unchanged.

function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || name[0].toUpperCase();
}

const AVATAR_COLORS = [
  "#385C4B", // moss
  "#C8835A", // terracotta
  "#7B6BA1", // amethyst
  "#3E7FA0", // ocean blue
  "#A07444", // bronze
  "#5C8B6E", // sage
  "#B45C7C", // dusty rose
  "#4F6B8C", // dusty indigo
];

function avatarColor(seed) {
  if (!seed) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function MessageActions({ mine, canDelete, canFlag, isComplaint, onReply, onPick, onDelete, onFlag }) {
  return (
    <div
      className={`flex items-center gap-0.5 mt-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity ${
        mine ? "mr-1.5" : "ml-1.5"
      }`}
      data-testid="chat-message-actions"
    >
      <button
        onClick={onReply}
        title="Reply"
        data-testid="chat-message-reply-button"
        className="p-1.5 rounded-full text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors"
      >
        <Reply className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      <button
        onClick={onPick}
        title="React"
        data-testid="chat-message-react-button"
        className="p-1.5 rounded-full text-[#6B6B61] hover:text-[#C8835A] hover:bg-[#C8835A]/10 transition-colors"
      >
        <Smile className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      {canFlag && (
        <button
          onClick={onFlag}
          title={isComplaint ? "Un-flag complaint" : "Mark as complaint"}
          data-testid="chat-message-flag-button"
          className={`p-1.5 rounded-full transition-colors ${
            isComplaint
              ? "text-[#C8553D] bg-[#C8553D]/10 hover:bg-[#C8553D]/20"
              : "text-[#6B6B61] hover:text-[#C8553D] hover:bg-[#C8553D]/10"
          }`}
        >
          <Flag className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
      {canDelete && (
        <button
          onClick={onDelete}
          title="Delete for everyone"
          data-testid="chat-message-delete-button"
          className="p-1.5 rounded-full text-[#6B6B61] hover:text-[#8A6046] hover:bg-[#8A6046]/10 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}

export default function Chat() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  // WhatsApp-style filter pills (Feb 2026): "all" | "unread" | "favourite".
  const [groupFilter, setGroupFilter] = useState("all");
  // Iter75: sidebar group search (typeahead, substring, case-insensitive).
  const [groupSearch, setGroupSearch] = useState("");
  // Iter77: WhatsApp-style message search — when the sidebar query has
  // 4+ chars we also probe `/api/lpg/search` for LPG IDs / mobile matches
  // living inside chat history. Hits merge into the sidebar list with a
  // subtitle badge; clicking jumps + highlights the exact message.
  // Map<group_id, {message_id, lpg_id, dac_code, mobile}>
  const [lpgSearchHits, setLpgSearchHits] = useState({});
  const [lpgSearching, setLpgSearching] = useState(false);
  // EKYC tab visibility: admin always; agency member only if active in last 7 days.
  const [ekycVisible, setEkycVisible] = useState(false);
  useEffect(() => {
    api.get("/agency-ekyc/visible").then(({ data }) => setEkycVisible(!!data.visible)).catch(() => {});
  }, []);
  // Pinned + latest-activity sort — pinned groups always float to the
  // top, then everything else is ordered by last_message.created_at DESC
  // so the noisiest chats surface first. The existing `useNameSort`
  // hook is kept for future toggling but replaced as the default view.
  const sortedGroups = useMemo(() => {
    const list = [...groups];
    list.sort((a, b) => {
      const ap = a.is_pinned ? 1 : 0;
      const bp = b.is_pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;
      const ta = a.last_message?.created_at || a.created_at || "";
      const tb = b.last_message?.created_at || b.created_at || "";
      return tb.localeCompare(ta);
    });
    return list;
  }, [groups]);
  const filteredGroups = useMemo(() => {
    let list = sortedGroups;
    if (groupFilter === "unread")
      list = list.filter((g) => (g.unread_mentions || 0) > 0);
    else if (groupFilter === "favourite")
      list = list.filter((g) => g.is_favourite);
    // Iter75: live substring search over group name — case-insensitive,
    // trims whitespace, applies AFTER the status filter so counts stay
    // meaningful (e.g. Unread + "hp" narrows to unread HP groups).
    const q = (groupSearch || "").trim().toLowerCase();
    if (q) {
      const nameMatchIds = new Set(
        list.filter((g) => (g.name || "").toLowerCase().includes(q)).map((g) => g.id)
      );
      // Iter77: also include groups whose chat history contains this LPG
      // ID / mobile (from /api/lpg/search). Preserve sortedGroups order.
      const lpgMatchIds = new Set(Object.keys(lpgSearchHits || {}));
      list = sortedGroups.filter(
        (g) => nameMatchIds.has(g.id) || lpgMatchIds.has(g.id)
      );
      // Re-apply the status filter after union (so Unread + LPG match
      // still narrows to unread only).
      if (groupFilter === "unread")
        list = list.filter((g) => (g.unread_mentions || 0) > 0);
      else if (groupFilter === "favourite")
        list = list.filter((g) => g.is_favourite);
    }
    return list;
  }, [sortedGroups, groupFilter, groupSearch, lpgSearchHits]);
  const unreadGroupCount = useMemo(
    () => groups.filter((g) => (g.unread_mentions || 0) > 0).length,
    [groups]
  );
  const favouriteGroupCount = useMemo(
    () => groups.filter((g) => g.is_favourite).length,
    [groups]
  );

  // Iter77: debounced backend probe for LPG ID / mobile inside chat
  // history. Only fires when the sidebar search has 4+ chars — anything
  // shorter is served purely from local name matching. Backend already
  // scopes results to the viewer's accessible groups.
  useEffect(() => {
    const q = (groupSearch || "").trim();
    if (q.length < 4) {
      setLpgSearchHits({});
      setLpgSearching(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLpgSearching(true);
      try {
        const r = await api.get(
          `/lpg/search?lpg_id=${encodeURIComponent(q)}`
        );
        if (cancelled) return;
        // Keep the newest hit per group (results are already sorted
        // created_at DESC) so a click jumps to the most recent occurrence.
        const byGroup = {};
        for (const h of r.data?.hits || []) {
          if (!h.group_id) continue;
          if (!byGroup[h.group_id]) {
            byGroup[h.group_id] = {
              message_id: h.message_id,
              lpg_id: h.lpg_id,
              dac_code: h.dac_code,
              mobile: h.mobile,
            };
          }
        }
        setLpgSearchHits(byGroup);
      } catch {
        if (!cancelled) setLpgSearchHits({});
      } finally {
        if (!cancelled) setLpgSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [groupSearch]);

  const [activeGroupId, setActiveGroupId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [error, setError] = useState("");
  const [nearBottom, setNearBottom] = useState(true);
  // Per-message "Pending only" filter — agency toggles this on a
  // 200-entry card to instantly see which 50 are still waiting. Keyed
  // by message id. When on, only entries with status=correct AND no
  // marked_by (i.e. never acknowledged by the agency) are rendered.
  const [pendingOnlyMsgs, setPendingOnlyMsgs] = useState(() => new Set());
  const togglePendingOnly = (mid) =>
    setPendingOnlyMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  const [newCount, setNewCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const [replyingTo, setReplyingTo] = useState(null);
  // Local "Delete for me" — hides messages just in this browser without
  // touching the server. WhatsApp-style: the message stays visible for
  // everyone else until sender picks "Delete for everyone".
  // Declared HERE (before displayedMessages useMemo) to avoid TDZ.
  const [hiddenIds, setHiddenIds] = useState(() => {
    try {
      const raw = localStorage.getItem("gc_hidden_msg_ids") || "[]";
      return new Set(JSON.parse(raw));
    } catch {
      return new Set();
    }
  });
  const [deleteDialogMsg, setDeleteDialogMsg] = useState(null);
  // Pending file attachments — uploaded to backend but not yet posted as
  // a chat message. Cleared on send. Requested by user (Feb 2026).
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // Live upload progress per in-flight file: {name, pct, size}.
  // Cleared as each file finishes. Rendered inline above the message
  // composer so the user sees "photo1.jpg — 62%" while it uploads.
  const [uploadProgress, setUploadProgress] = useState([]);
  const attachInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handlePickFile = async (e) => {
    // Multi-file support (Feb 2026): user picks 1..N files, we upload each
    // one sequentially so the backend GridFS doesn't get slammed and the
    // per-file progress bar stays sensible. Any single file that fails is
    // toasted but doesn't abort the rest of the batch.
    const files = Array.from(e.target?.files || []);
    e.target.value = ""; // allow re-selecting same file(s)
    if (files.length === 0) return;
    setUploadingAttachment(true);
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" is too large. Max 10 MB per file.`);
        continue;
      }
      // Seed the progress row so the chip appears immediately.
      const progressKey = `${file.name}-${Date.now()}-${i}`;
      setUploadProgress((prev) => [
        ...prev,
        { key: progressKey, name: file.name, pct: 0, size: file.size },
      ]);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const pct = Math.min(100, Math.round((evt.loaded * 100) / evt.total));
            setUploadProgress((prev) =>
              prev.map((p) => (p.key === progressKey ? { ...p, pct } : p))
            );
          },
        });
        setPendingAttachments((prev) => [...prev, data]);
        successCount += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err)}`);
      } finally {
        setUploadProgress((prev) => prev.filter((p) => p.key !== progressKey));
      }
    }
    // Only fire the batch success toast if at least one file made it
    // through — a 3-file batch where all 3 failed already surfaced 3
    // per-file error toasts, so a bogus "3 files processed" is worse
    // than silence.
    if (files.length > 1 && successCount > 0) {
      toast.success(`${successCount} of ${files.length} file${files.length > 1 ? "s" : ""} attached`);
    } else if (files.length === 1 && successCount === 1) {
      toast.success(`Attached: ${files[0].name}`);
    }
    setUploadingAttachment(false);
  };

  const removePendingAttachment = (idx) => {
    // Confirm before removing so accidental taps don't drop the file.
    const a = pendingAttachments[idx];
    const label = a?.name || "this file";
    if (!window.confirm(`Remove "${label}"?\n\nAbhi tak send nahi hui — remove karne pe wapas laani hogi.`)) {
      return;
    }
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));
  };
  const [emojiPickerFor, setEmojiPickerFor] = useState(null);
  // Context-menu state — long-press or right-click on a message opens
  // a portal-mounted popup with all actions (Feb 2026). {message, x, y,
  // selection?}.
  const [ctxMenu, setCtxMenu] = useState(null);
  // Message Info modal — showing read/delivered/pending recipients for
  // a single message id.
  const [infoMsgId, setInfoMsgId] = useState(null);
  // Group description editor modal — admin taps the description under
  // the group name in the header to open (Feb 2026, WhatsApp parity).
  const [descEditorOpen, setDescEditorOpen] = useState(false);
  // Per-message toggle: whether the "Duplicate Auto-Removed · N" list
  // is expanded. Collapsed by default so a 50-row duplicate paste does
  // not push the fresh-entries message out of the visible window and
  // force the user to scroll back up. Keyed by message id.
  const [expandedDupLists, setExpandedDupLists] = useState(() => new Set());
  const toggleDupList = (mid) =>
    setExpandedDupLists((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  const [groupMembers, setGroupMembers] = useState([]);
  const [mentionState, setMentionState] = useState(null); // {query, candidates}
  const [complaintCount, setComplaintCount] = useState(0);
  const [hasCooling, setHasCooling] = useState(false);
  const [hideLpgIds, setHideLpgIds] = useState(() => localStorage.getItem("delivery.hideLpgIds") === "1");
  useEffect(() => {
    localStorage.setItem("delivery.hideLpgIds", hideLpgIds ? "1" : "0");
  }, [hideLpgIds]);
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(
    () => localStorage.getItem("chat.sidebarMenuOpen") === "1"
  );
  useEffect(() => {
    localStorage.setItem("chat.sidebarMenuOpen", sidebarMenuOpen ? "1" : "0");
  }, [sidebarMenuOpen]);
  const [coolingGroupMap, setCoolingGroupMap] = useState({});
  useEffect(() => {
    if (!user) return;
    api.get("/cooling/my-agencies")
      .then(({ data }) => {
        setHasCooling((data.agencies || []).length > 0);
        setCoolingGroupMap(data.group_map || {});
      })
      .catch(() => {});
  }, [user]);
  const [staffSheets, setStaffSheets] = useState([]);
  const [showStaffSheets, setShowStaffSheets] = useState(false);
  const [showLpgSearch, setShowLpgSearch] = useState(false);
  // Feb 2026 Global Search: message id we want to highlight + auto-scroll
  // to after a "Jump to card" click. Cleared once the flash animation
  // finishes so the same message doesn't keep re-flashing on re-render.
  const [highlightMsgId, setHighlightMsgId] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [showResendCreate, setShowResendCreate] = useState(false);
  const [showBulkComplaint, setShowBulkComplaint] = useState(false);
  // "Delivered by us" filter — when on, chat only renders messages
  // that contain at least one green (delivered) entry. Requested by
  // admin (Feb 2026): "DELIVERED BY US K SATH SARA GREEN DATA KO
  // DIKHA DENA".
  const [deliveredFilter, setDeliveredFilter] = useState(false);
  // Pending portal-resend count for the currently-active group — drives the
  // red badge on the "Resend DAC" sidebar button. Refreshed on group
  // switch + whenever a WS `resend.new` / `resend.confirmed` fires.
  const [resendPendingCount, setResendPendingCount] = useState(0);
  // Admin-only bulk-resend paste modal — accessed via the sidebar shortcut.
  const [bulkResendOpen, setBulkResendOpen] = useState(false);
  const membersCacheRef = useRef({}); // group_id -> members array
  const msgEndRef = useRef(null);
  const msgContainerRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const refetchActiveRef = useRef(null);
  // Set of message ids we've already marked as read on the server, so
  // the IntersectionObserver doesn't spam /mark-read on scroll.
  const markedReadRef = useRef(new Set());

  // Offline outbox: messages queued while offline / API failed.
  // Persisted to localStorage so they survive reloads.
  const OUTBOX_KEY = `gc_outbox_${user?.id || "anon"}`;
  const [outbox, setOutbox] = useState(() => {
    try {
      const raw = localStorage.getItem(`gc_outbox_${user?.id || "anon"}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(OUTBOX_KEY, JSON.stringify(outbox));
    } catch {}
  }, [outbox, OUTBOX_KEY]);

  const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;
  const selectGroup = (id) => {
    if (id === activeGroupId) {
      if (isMobile()) setSidebarOpen(false);
      return;
    }
    setActiveGroupId(id);
    // Clear stale state immediately so the user sees instant feedback,
    // not the previous group's messages with a spinner on top.
    setMessages([]);
    setReplyingTo(null);
    setEmojiPickerFor(null);
    setMentionState(null);
    // Hydrate members from cache instantly if we have them
    setGroupMembers(membersCacheRef.current[id] || []);
    if (isMobile()) setSidebarOpen(false);
    // Optimistically clear unread + tell server
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, unread_mentions: 0 } : g))
    );
    api.post(`/groups/${id}/read`).catch(() => {});
  };

  // WhatsApp-style pin/favourite toggles — optimistic update, roll back on error.
  const toggleGroupPin = async (g) => {
    const next = !g.is_pinned;
    setGroups((prev) =>
      prev.map((x) => (x.id === g.id ? { ...x, is_pinned: next } : x))
    );
    try {
      if (next) await api.post(`/groups/${g.id}/pin`);
      else await api.delete(`/groups/${g.id}/pin`);
    } catch (e) {
      setGroups((prev) =>
        prev.map((x) => (x.id === g.id ? { ...x, is_pinned: !next } : x))
      );
      toast.error(formatApiError(e) || "Could not update pin");
    }
  };

  const toggleGroupFavourite = async (g) => {
    const next = !g.is_favourite;
    setGroups((prev) =>
      prev.map((x) => (x.id === g.id ? { ...x, is_favourite: next } : x))
    );
    try {
      if (next) await api.post(`/groups/${g.id}/favourite`);
      else await api.delete(`/groups/${g.id}/favourite`);
    } catch (e) {
      setGroups((prev) =>
        prev.map((x) => (x.id === g.id ? { ...x, is_favourite: !next } : x))
      );
      toast.error(formatApiError(e) || "Could not update favourite");
    }
  };

  const toggleReaction = async (msg, emoji) => {
    try {
      const { data } = await api.post(
        `/groups/${activeGroupId}/messages/${msg.id}/react`,
        { emoji }
      );
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, reactions: data.reactions } : m))
      );
      setEmojiPickerFor(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const clearChatHistory = async () => {
    if (!activeGroupId) return;
    const ok = window.confirm(
      "Clear chat history for you?\n\n" +
        "• This only clears YOUR view — other members keep seeing the chat.\n" +
        "• LPG Report data is NOT affected.\n" +
        "• This cannot be undone."
    );
    if (!ok) return;
    try {
      await api.post(`/groups/${activeGroupId}/clear-history`);
      setMessages([]);
      // Refresh sidebar previews
      loadGroups();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const toggleLpgStatus = async (msg, entryIndex, nextOverride) => {
    const current = msg.lpg_entries?.[entryIndex]?.status || "correct";
    // Default cycle: correct → wrong → correct. "resent" is only set via the
    // explicit "Confirm Resend" action (nextOverride).
    const next =
      nextOverride ||
      (current === "correct" ? "wrong" : "correct");
    // Optimistic UI
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msg.id) return m;
        const entries = [...(m.lpg_entries || [])];
        entries[entryIndex] = { ...entries[entryIndex], status: next };
        return { ...m, lpg_entries: entries };
      })
    );
    try {
      const { data } = await api.post(
        `/groups/${activeGroupId}/messages/${msg.id}/lpg/${entryIndex}`,
        { status: next }
      );
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, lpg_entries: data.lpg_entries } : m))
      );
    } catch (e) {
      // Revert on failure
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== msg.id) return m;
          const entries = [...(m.lpg_entries || [])];
          entries[entryIndex] = { ...entries[entryIndex], status: current };
          return { ...m, lpg_entries: entries };
        })
      );
      setError(formatApiError(e));
    }
  };

  // Mark EVERY LPG entry in a message with the same status in one action.
  // User request (Feb 2026): "200 number ka dac hai to sab 200 ek saath
  // hi rahega...single single nhi. Delivery jo agency karega, full
  // visual k saath karega." So the agency taps ONCE → all N consumers
  // flip to `correct` (delivered). Google Sheet syncs in real-time via
  // the existing per-entry webhook fired by the backend on each POST.
  const markAllEntries = async (msg, newStatus) => {
    if (!msg?.lpg_entries?.length) return;
    const entries = msg.lpg_entries;
    // Optimistic UI — flip everything locally first so the tick appears
    // instant even when the agency is on a slow connection.
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? {
              ...m,
              lpg_entries: entries.map((e) => ({ ...e, status: newStatus })),
            }
          : m
      )
    );
    // Fire per-entry POSTs in parallel — each POST also broadcasts to
    // Google Sheet via the existing webhook, so the sheet gets 200
    // rows updated near-simultaneously without a special new endpoint.
    // If ANY fail we don't rollback (server is the source of truth on
    // next refresh) — but surface the last error to the user.
    const failures = [];
    await Promise.all(
      entries.map(async (_e, idx) => {
        // Skip entries already at the target status — cuts server load
        // when the user marks-all on a partially-completed message.
        if ((entries[idx].status || "correct") === newStatus) return;
        try {
          const { data } = await api.post(
            `/groups/${activeGroupId}/messages/${msg.id}/lpg/${idx}`,
            { status: newStatus }
          );
          // Trust the server response — replaces the optimistic entry
          // with the server's authoritative version (fills marked_by /
          // marked_at fields the client can't fabricate).
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msg.id
                ? { ...m, lpg_entries: data.lpg_entries }
                : m
            )
          );
        } catch (e) {
          failures.push(idx);
        }
      })
    );
    if (failures.length > 0) {
      setError(
        `${failures.length} of ${entries.length} entries could not be marked — refresh to see actual state.`
      );
    }
  };

  const activeGroup = useMemo(
    () => groups.find((g) => g.id === activeGroupId),
    [groups, activeGroupId]
  );

  // Ref mirror of activeGroup so WS handlers (registered once) can read the
  // current selection without re-binding on every group switch.
  const activeGroupRef = useRef(null);
  useEffect(() => {
    activeGroupRef.current = activeGroup || null;
  }, [activeGroup]);

  // Refresh the pending resend count whenever the active group changes —
  // this drives the red badge on the "Resend DAC" sidebar button. WS
  // events (resend.new / resend.confirmed) keep it accurate in real time.
  useEffect(() => {
    let cancelled = false;
    setResendPendingCount(0);
    if (!activeGroupId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/resend/entries?group_id=${activeGroupId}&status=pending&days=14`
        );
        if (!cancelled) setResendPendingCount((data.entries || []).length);
      } catch (_e) {
        // silent — badge stays at 0
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId]);

  // ── Per-group live delivery status ("Delivered vs Pending") strip ─
  // Distributor-facing summary at the top of chat. Uses the existing
  // status fields (marked_by + status) — no schema change. Refreshed
  // on activeGroup change and whenever `messages` mutates (WebSocket
  // pushes a new message OR any tick / resend fires).
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  // Full-list modal — opens on strip click. Agency scrolls all 100+
  // sent LPG IDs, ticks ✓ (delivered) or ✗ (DAC incorrect → complaint)
  // per row. Chat cards below remain UNTOUCHED as historical audit.
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryModalFilter, setDeliveryModalFilter] = useState("pending"); // pending|all|delivered|complaint
  // Oct 2026: Delivery Dashboard date filter — default "today" so the
  // modal opens scoped to today's run (admin ki most-common use case).
  // Date pick localStorage mein save hoti hai so F5 ke baad bhi same
  // chip restore ho. Values: "all" | "today" | "yesterday" | "last7" | "custom"
  const [deliveryDateFilter, setDeliveryDateFilter] = useState(() => {
    try {
      const v = localStorage.getItem("delivery_date_filter");
      if (v && ["all", "today", "yesterday", "last7", "custom"].includes(v)) return v;
    } catch { /* localStorage blocked */ }
    return "today";
  });
  const [deliveryDateCustom, setDeliveryDateCustom] = useState(() => {
    try {
      const v = localStorage.getItem("delivery_date_custom");
      if (v) return JSON.parse(v);
    } catch {}
    return { from: "", to: "" };
  });
  // Celebration banner dismissal — in-session only. Re-opens if user
  // revisits the modal in a fresh session and today is still 100%.
  const [deliveryDoneBannerDismissed, setDeliveryDoneBannerDismissed] = useState(false);
  // Reset "today done" banner dismissal every time the modal re-opens so
  // the admin sees fresh celebration if today is still 100% complete.
  useEffect(() => {
    if (showDeliveryModal) setDeliveryDoneBannerDismissed(false);
  }, [showDeliveryModal]);
  // Persist delivery date-filter picks across page reloads.
  useEffect(() => {
    try { localStorage.setItem("delivery_date_filter", deliveryDateFilter); } catch {}
  }, [deliveryDateFilter]);
  useEffect(() => {
    try { localStorage.setItem("delivery_date_custom", JSON.stringify(deliveryDateCustom)); } catch {}
  }, [deliveryDateCustom]);

  // Iter73: 3-mode Excel dropdown (Today / Range / Whole).
  const [showDeliveryExcelMenu, setShowDeliveryExcelMenu] = useState(false);
  // Per-group Complaint Box — full-transparency modal that shows every
  // wrong DAC / cancellation / text complaint for THIS group along
  // with the admin's resolution. Agency + admin see the same view.
  const [showGroupComplaints, setShowGroupComplaints] = useState(false);
  // Feb 2026: Customer Details modal (LPG ID / Mobile / DAC per row)
  // A read-only aggregation of every LPG entry in this group over the
  // last 30 days — admin can quickly look up a customer by LPG ID or
  // mobile without scrolling the chat.
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [customerDetailsSearch, setCustomerDetailsSearch] = useState("");
  const [groupComplaints, setGroupComplaints] = useState(null); // {items, totals, ...}
  const [groupComplaintFilter, setGroupComplaintFilter] = useState("all"); // all|open|resolved|cancelled
  const [groupComplaintsLoading, setGroupComplaintsLoading] = useState(false);
  // Resolve dialog — user asked for a 2-option flow: "new DAC" OR
  // "delivered from our side" before a complaint can be marked resolved.
  const [resolveTarget, setResolveTarget] = useState(null); // {message_id, entry_index, lpg_id, dac_code}
  const [resolveChoice, setResolveChoice] = useState("new_dac"); // new_dac | delivered
  const [resolveNewDac, setResolveNewDac] = useState("");
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const fetchGroupComplaints = React.useCallback(async () => {
    if (!activeGroupId) return;
    setGroupComplaintsLoading(true);
    try {
      const { data } = await api.get(`/groups/${activeGroupId}/complaints-box`);
      setGroupComplaints(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setGroupComplaintsLoading(false);
    }
  }, [activeGroupId]);
  useEffect(() => {
    if (showGroupComplaints) fetchGroupComplaints();
  }, [showGroupComplaints, fetchGroupComplaints]);
  // Refetch complaint box also when messages update (so admin's
  // resend / resolve flip is instantly visible in the modal).
  useEffect(() => {
    if (showGroupComplaints) fetchGroupComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);
  // Per-group urgent (>24h pending) map — powers the red bell alert
  // in the header + the red-dot badge on each sidebar group item.
  // Refreshed on mount + every 90s + after any tick/paste.
  const [urgentSummary, setUrgentSummary] = useState({ groups: {}, totals: { urgent_pending: 0, pending: 0 } });
  const refreshUrgentSummary = React.useCallback(async () => {
    try {
      const { data } = await api.get("/delivery/urgent-summary");
      setUrgentSummary(data || { groups: {}, totals: { urgent_pending: 0, pending: 0 } });
    } catch (_e) {
      // silent — no-op if endpoint transiently unavailable
    }
  }, []);
  useEffect(() => {
    refreshUrgentSummary();
    const t = setInterval(refreshUrgentSummary, 90_000);
    return () => clearInterval(t);
  }, [refreshUrgentSummary]);
  useEffect(() => {
    let cancelled = false;
    setDeliveryStatus(null);
    if (!activeGroupId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/groups/${activeGroupId}/delivery-status`
        );
        if (!cancelled) {
          setDeliveryStatus(data);
          // Piggyback: whenever the active group's status refreshes,
          // also refresh the cross-group urgent summary so the sidebar
          // red dots stay in sync after ticks / pastes / cancellations.
          refreshUrgentSummary();
        }
      } catch (_e) {
        // silent — panel hides if unavailable
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId, messages.length, refreshUrgentSummary]);

  // Reset the marked-read cache when the active group changes so
  // scrolling into a different group re-fires /mark-read for its own
  // messages the next time they enter the viewport.
  useEffect(() => {
    markedReadRef.current = new Set();
  }, [activeGroupId]);

  // IntersectionObserver: whenever a message bubble is >=50% visible
  // for the current group, fire POST /messages/{id}/mark-read (once
  // per session per msg). The observer is rebuilt on every messages
  // update so freshly rendered bubbles get watched too. Skips own
  // bubbles + deleted bubbles + optimistic outbox rows.
  useEffect(() => {
    if (!activeGroupId || !user?.id) return;
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const mid = entry.target.getAttribute("data-msg-id");
          const sender = entry.target.getAttribute("data-msg-sender");
          if (!mid || !sender) return;
          if (sender === user.id) return;
          if (markedReadRef.current.has(mid)) return;
          markedReadRef.current.add(mid);
          api.post(`/messages/${mid}/mark-read`).catch(() => {
            // Roll back cache on failure so a retry can happen on
            // next scroll into view.
            markedReadRef.current.delete(mid);
          });
        });
      },
      { threshold: 0.5 }
    );
    // Attach to every currently-rendered bubble carrying a msg id.
    const nodes = document.querySelectorAll("[data-msg-id][data-msg-sender]");
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [activeGroupId, messages.length, user?.id]);

  // Merge confirmed messages with pending/failed outbox items for the active
  // group so the sender always sees their own message instantly (WhatsApp-style).
  const displayedMessages = useMemo(() => {
    const pending = outbox
      .filter((o) => o.group_id === activeGroupId)
      .map((o) => ({ ...o, id: o.temp_id, _pending: true }));
    // Filter out any message the current user has "Deleted for me" —
    // WhatsApp-style client-side hide (server row untouched).
    let out = [...messages, ...pending].filter((m) => !hiddenIds.has(m.id));
    // "Delivered by us" filter (Feb 2026 admin ask). When active, only
    // messages that contain at least one GREEN (delivered) LPG entry
    // survive. An entry is considered green if `status ∈ {correct,
    // resent}` AND `marked_by` is a non-empty string — mirrors the
    // per-card render rule used in DeliveryDashboard / chat bubbles.
    if (deliveredFilter) {
      out = out.filter((m) => {
        if (m.import_source === "delivered_excel") return true;
        const entries = m.lpg_entries || [];
        return entries.some(
          (e) =>
            (e.status === "correct" || e.status === "resent") &&
            (e.marked_by || "").trim() !== ""
        );
      });
    }
    return out;
  }, [messages, outbox, activeGroupId, hiddenIds, deliveredFilter]);

  // Online/offline indicator + outbox drain on reconnect
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      // Flush queued messages immediately when the network comes back
      drainOutbox();
      // And resync the chat
      refetchActiveRef.current && refetchActiveRef.current();
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGroups = async () => {
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
      // Use ref to read CURRENT activeGroupId — otherwise a stale closure
      // (from the WS / polling refetch) will keep seeing null and force-switch
      // the user back to data[0] every time loadGroups fires.
      if (data.length > 0 && !activeGroupIdRef.current) {
        setActiveGroupId(data[0].id);
      }
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const loadComplaintCount = async () => {
    try {
      const { data } = await api.get("/complaints");
      setComplaintCount(data.total_open || 0);
    } catch {
      // best-effort — sidebar badge can fail silently
    }
  };

  const loadMessages = async (gid) => {
    if (!gid) return;
    setLoading(true);
    try {
      // Lower default limit (50 vs 100) — fits the viewport, halves initial latency
      const { data } = await api.get(`/groups/${gid}/messages?limit=50`);
      setMessages(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    loadComplaintCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link support: /chat?group=<id> auto-selects that group on load.
  // Used by the Complaint Box "Open chat" button.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const gid = params.get("group");
      if (gid) {
        setActiveGroupId(gid);
        // Strip query so refreshes don't keep re-selecting
        window.history.replaceState(null, "", "/chat");
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeGroupId) {
      loadMessages(activeGroupId);
      // Members: serve from cache instantly, refresh in background
      api
        .get(`/groups/${activeGroupId}/members`)
        .then(({ data }) => {
          membersCacheRef.current[activeGroupId] = data;
          setGroupMembers(data);
        })
        .catch(() => {});
      // Load any staff sheets linked to this group
      api
        .get(`/groups/${activeGroupId}/staff-sheets`)
        .then(({ data }) => setStaffSheets(data || []))
        .catch(() => setStaffSheets([]));
    }
  }, [activeGroupId]);

  // WebSocket
  useEffect(() => {
    if (!token) return;
    const refetchActive = () => {
      // Refetch groups list + currently-open group's messages so the user
      // never sees a stale/empty chat after a connection blip or tab wakeup.
      loadGroups();
      const gid = activeGroupIdRef.current;
      if (gid) loadMessages(gid);
      // Also try to flush anything queued offline
      drainOutbox();
    };
    refetchActiveRef.current = refetchActive;
    const ws = openChatSocket(
      token,
      (data) => {
        if (data.type === "message.new") {
        setMessages((prev) => {
          if (data.group_id !== activeGroupIdRef.current) return prev;
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        setGroups((prev) =>
          prev.map((g) => {
            if (g.id !== data.group_id) return g;
            const updates = { ...g, last_message: data.message };
            // If I'm mentioned and this isn't the active group, bump unread
            const meMentioned =
              Array.isArray(data.message.mentioned_user_ids) &&
              data.message.mentioned_user_ids.includes(user?.id) &&
              data.message.sender_id !== user?.id &&
              g.id !== activeGroupIdRef.current;
            if (meMentioned) {
              updates.unread_mentions = (g.unread_mentions || 0) + 1;
            }
            return updates;
          })
        );
      } else if (data.type === "message.deleted") {
        if (user?.role === "admin") {
          // Admin keeps the message visible with a "deleted" annotation
          setMessages((prev) =>
            prev.map((m) =>
              m.id === data.message_id
                ? {
                    ...m,
                    deleted: true,
                    deleted_at: data.deleted_at,
                    deleted_by_role: data.deleted_by_role,
                  }
                : m
            )
          );
        } else {
          // Members see a tombstone — they know a message existed and was
          // deleted, but the content is hidden.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === data.message_id
                ? {
                    ...m,
                    deleted: true,
                    text: "",
                    reply_to: null,
                    reactions: {},
                    deleted_at: data.deleted_at,
                    deleted_by_role: data.deleted_by_role,
                  }
                : m
            )
          );
        }
      } else if (data.type === "message.reaction") {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === data.message_id ? { ...m, reactions: data.reactions } : m
          )
        );
      } else if (data.type === "group.description_updated") {
        // Live update group description across every open Chat session
        // so the header refreshes without a page reload.
        setGroups((prev) =>
          prev.map((g) =>
            g.id === data.group_id ? { ...g, description: data.description } : g
          )
        );
      } else if (data.type === "bank_details.updated") {
        // Nudge any open BankDetails page to refetch its singleton doc.
        window.dispatchEvent(new CustomEvent("bank_details.updated"));
      } else if (data.type === "message.receipts_updated") {
        // Sender's bubble tick nudge — bulk `delivered` (no message_id,
        // covers all recent msgs) or single `read` (specific msg id).
        if (data.message_id) {
          // Individual read event → increment read count on that msg.
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== data.message_id) return m;
              const already = m._read_by_uids && m._read_by_uids[data.by_user_id];
              if (already) return m;
              return {
                ...m,
                read_by_count: (m.read_by_count || 0) + 1,
                _read_by_uids: { ...(m._read_by_uids || {}), [data.by_user_id]: true },
              };
            })
          );
        } else if (data.kind === "delivered") {
          // A user just came online — bump delivered_to_count on every
          // NON-read own-bubble in the active group so ticks flip live.
          setMessages((prev) =>
            prev.map((m) => {
              if (m.sender_id !== user?.id) return m;
              // If already read by this user, don't double-count.
              const alreadyDelivered =
                m._delivered_to_uids && m._delivered_to_uids[data.by_user_id];
              if (alreadyDelivered) return m;
              return {
                ...m,
                delivered_to_count: (m.delivered_to_count || 0) + 1,
                _delivered_to_uids: {
                  ...(m._delivered_to_uids || {}),
                  [data.by_user_id]: true,
                },
              };
            })
          );
        }
      } else if (data.type === "message.lpg_updated") {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === data.message_id ? { ...m, lpg_entries: data.lpg_entries } : m
          )
        );
        loadComplaintCount();
        // Feb 2026 cross-mobile sync fix: the useEffect that refetches
        // /delivery-status only fires when `messages.length` changes,
        // but an lpg toggle mutates inner entries — length is stable,
        // so Mobile-2 saw stale Pending/Delivered counts until close+
        // reopen. Force a refetch here so the strip + dashboard modal
        // both stay live when another user (or the same user's second
        // device) mutates an entry.
        if (activeGroupRef.current && data.group_id === activeGroupRef.current.id) {
          api
            .get(`/groups/${data.group_id}/delivery-status`)
            .then(({ data: fresh }) => setDeliveryStatus(fresh))
            .catch(() => {});
          refreshUrgentSummary();
          // Also notify any open dashboard/modal that watches this
          // window event to re-fetch its own tab data (Pending/
          // Delivered/Complaint/Cancelled tables).
          window.dispatchEvent(
            new CustomEvent("delivery.status.refresh", {
              detail: { group_id: data.group_id },
            })
          );
        }
      } else if (data.type === "delivery_status_refresh") {
        // Backend fires this alongside message.lpg_updated whenever an
        // LPG entry is mutated (bulk mark done, delete-today, delivered
        // commit, undo, individual toggle). It's a lightweight nudge —
        // no payload — telling the client "your aggregate counts are
        // stale, refetch". Chat.jsx wasn't listening for it before, so
        // the Delivery Dashboard modal's Pending count stayed frozen on
        // the second mobile until manual close+reopen.
        if (activeGroupRef.current && data.group_id === activeGroupRef.current.id) {
          api
            .get(`/groups/${data.group_id}/delivery-status`)
            .then(({ data: fresh }) => setDeliveryStatus(fresh))
            .catch(() => {});
          refreshUrgentSummary();
          window.dispatchEvent(
            new CustomEvent("delivery.status.refresh", {
              detail: { group_id: data.group_id },
            })
          );
        }
      } else if (
        data.type === "message.complaint_updated" ||
        data.type === "message.complaint_resolved"
      ) {
        const incoming = data.message;
        if (incoming) {
          setMessages((prev) =>
            prev.map((m) => (m.id === incoming.id ? { ...m, ...incoming } : m))
          );
        }
        loadComplaintCount();
      } else if (data.type === "delivery.confirmed") {
        // Real-Time delivery tick — re-broadcast into a window event so the
        // open StaffSheetsModal (if any) can patch the affected row.
        window.dispatchEvent(new CustomEvent("delivery.confirmed", { detail: data }));
      } else if (data.type === "resend.new") {
        // New resend request filed by a team member — toast every member of
        // the agency + every staff user so the team sees live activity.
        const e = data.data || {};
        toast.info(
          `📩 Resend filed · ${e.agency_resolved || ""} · LPG ${e.lpg_id || ""}`,
          { description: `by ${e.created_by || "team"}`, duration: 6000 },
        );
        // Bump the active group's pending badge if it matches.
        if (activeGroupRef.current && e.group_id === activeGroupRef.current.id) {
          setResendPendingCount((n) => n + 1);
        }
        // Push a live signal into any open ResendModal so it re-fetches.
        window.dispatchEvent(new CustomEvent("resend.sync", { detail: data }));
      } else if (data.type === "resend.confirmed") {
        const e = data.data || {};
        toast.success(
          `✓ Resend confirmed · ${e.agency_resolved || ""} · LPG ${e.lpg_id || ""}`,
          {
            description: `by ${e.confirmed_by || "agency"} — sheet updated`,
            duration: 6000,
          },
        );
        if (activeGroupRef.current && e.group_id === activeGroupRef.current.id) {
          setResendPendingCount((n) => Math.max(0, n - 1));
        }
        window.dispatchEvent(new CustomEvent("resend.sync", { detail: data }));
      } else if (
        data.type === "resend.received" ||
        data.type === "resend.retried"
      ) {
        // Staff ticked "received", or a duplicate re-file bumped the attempts
        // counter. Either way surface a subtle toast + re-sync any open modal.
        const e = data.data || {};
        if (data.type === "resend.received") {
          toast.success(
            `✓ Staff received DAC · LPG ${e.lpg_id || ""}`,
            { description: `by ${e.received_by || "staff"}`, duration: 4500 },
          );
        }
        window.dispatchEvent(new CustomEvent("resend.sync", { detail: data }));
      } else if (data.type === "group.pin_updated") {
        // Cross-device sync (e.g., pinned on mobile → desktop reflects instantly).
        setGroups((prev) =>
          prev.map((g) =>
            g.id === data.group_id ? { ...g, is_pinned: !!data.is_pinned } : g
          )
        );
      } else if (data.type === "group.favourite_updated") {
        setGroups((prev) =>
          prev.map((g) =>
            g.id === data.group_id ? { ...g, is_favourite: !!data.is_favourite } : g
          )
        );
      } else if (data.type === "groups.renamed") {
        // Admin renamed a group (via user display_name auto-sync). Reload
        // the sidebar so the new name shows up instantly for every open
        // client — no refresh needed.
        loadGroups();
      }
    }, refetchActive);
    wsRef.current = ws;

    // When the tab becomes visible again (mobile background → foreground),
    // immediately resync messages instead of waiting for the next WS event.
    const onVisibility = () => {
      if (document.visibilityState === "visible") refetchActive();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refetchActive);
    window.addEventListener("online", refetchActive);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refetchActive);
      window.removeEventListener("online", refetchActive);
      try {
        ws.close();
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Keep a ref for activeGroupId so ws callback uses current value
  const activeGroupIdRef = useRef(activeGroupId);
  useEffect(() => {
    activeGroupIdRef.current = activeGroupId;
  }, [activeGroupId]);

  // Safety-net polling: every 20s while the tab is visible, force a refetch.
  // This catches cases where neither WebSocket reconnect nor visibility/online
  // events fire reliably (common on mobile after long sleep).
  useEffect(() => {
    if (!token) return;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      refetchActiveRef.current && refetchActiveRef.current();
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [token]);

  // On mount, try to drain any messages that were queued in a prior session
  // (e.g., user composed a message, network died, tab was closed before retry).
  useEffect(() => {
    if (!token) return;
    if (outboxRef.current && outboxRef.current.some((o) => o.status === "pending")) {
      drainOutbox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Smart scroll: only auto-scroll if user is near bottom; otherwise show "new messages" pill.
  useEffect(() => {
    if (nearBottom) {
      msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setNewCount(0);
    } else {
      // Last message arrived while scrolled up -> bump counter (only for "real" new ones)
      setNewCount((c) => c + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedMessages.length]);

  // Reset on group change: jump to bottom
  useEffect(() => {
    setNewCount(0);
    setNearBottom(true);
    setTimeout(() => {
      msgEndRef.current?.scrollIntoView({ behavior: "auto" });
    }, 50);
  }, [activeGroupId]);

  // Feb 2026 Global Search — after the messages of a jumped-to group
  // are loaded, scroll the specific message into view and clear the
  // highlight after 2.5s (matches the amber flash animation window).
  useEffect(() => {
    if (!highlightMsgId) return;
    const el = document.getElementById(`msg-${highlightMsgId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const t = setTimeout(() => setHighlightMsgId(""), 2500);
    return () => clearTimeout(t);
  }, [highlightMsgId, displayedMessages.length]);

  const scrollRafRef = useRef(false);
  const handleScroll = () => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = true;
    requestAnimationFrame(() => {
      scrollRafRef.current = false;
      const el = msgContainerRef.current;
      if (!el) return;
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const isNear = distance < 120;
      setNearBottom((prev) => (prev !== isNear ? isNear : prev));
      if (isNear) setNewCount((c) => (c !== 0 ? 0 : c));
    });
  };

  const scrollToBottom = () => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setNewCount(0);
    setNearBottom(true);
  };

  // Local "Delete for me" helper — hidden set already declared above.
  const hideMessageForMe = (msgId) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.add(msgId);
      try {
        localStorage.setItem("gc_hidden_msg_ids", JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  const handleDeleteMessage = async (msg) => {
    if (msg.deleted) return;
    // Anyone can pick "Delete for me". Only sender sees "Delete for everyone".
    setDeleteDialogMsg(msg);
  };

  // Open the WhatsApp-style context menu for a message. Captures any
  // active text selection so a "Reply to selection" quotes just the
  // highlighted chunk (WhatsApp parity, Feb 2026).
  const openMessageContextMenu = (msg, evt) => {
    if (msg.deleted) return;
    let selection = "";
    try {
      const sel = window.getSelection?.();
      const raw = sel ? String(sel.toString() || "").trim() : "";
      // Only treat the selection as intentional if it actually sits
      // inside this message bubble — a stray highlight elsewhere on
      // the page should not leak into the reply.
      if (raw && sel && sel.rangeCount > 0) {
        const bubbleEl = document.getElementById(`msg-${msg.id}`);
        if (bubbleEl && bubbleEl.contains(sel.anchorNode)) {
          selection = raw.slice(0, 200);
        }
      }
    } catch (_e) {
      /* ignore */
    }
    // Get the click coords from either mouse or the first touch.
    const x = evt?.clientX ?? evt?.touches?.[0]?.clientX ?? 200;
    const y = evt?.clientY ?? evt?.touches?.[0]?.clientY ?? 200;
    setCtxMenu({ message: msg, x, y, selection });
  };

  const handleCopyMessage = async (msg, selection) => {
    const text = (selection || msg.text || "").trim();
    if (!text) {
      toast.error("Nothing to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(selection ? "Copied selection ✓" : "Copied ✓");
    } catch {
      // Older browsers / iOS Safari when not in a secure context. Use
      // the legacy hack so the user still gets something useful.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Copied ✓");
      } catch {
        toast.error("Copy blocked by browser");
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const openReplyWithSelection = (msg, selection) => {
    // When a user selects text and picks "Reply to selection", stash
    // the highlighted chunk as a quote-only payload. The composer's
    // reply banner reads `reply_selection_text` when present and shows
    // it instead of the full parent text.
    if (selection && selection.trim()) {
      setReplyingTo({ ...msg, reply_selection_text: selection.trim() });
    } else {
      setReplyingTo(msg);
    }
  };

  const doDeleteForEveryone = async (msg) => {
    setDeleteDialogMsg(null);
    if (msg.sender_id !== user?.id) return;
    try {
      await api.delete(`/groups/${activeGroupId}/messages/${msg.id}`);
      if (user?.role === "admin") {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msg.id ? { ...m, deleted: true, deleted_by_role: user?.role } : m
          )
        );
      } else {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msg.id
              ? {
                  ...m,
                  deleted: true,
                  text: "",
                  reply_to: null,
                  reactions: {},
                  deleted_by_role: user?.role,
                }
              : m
          )
        );
      }
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleToggleComplaint = async (msg) => {
    const next = !msg.is_complaint;
    // Optimistic UI
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? {
              ...m,
              is_complaint: next,
              complaint_resolved_at: next ? null : m.complaint_resolved_at,
            }
          : m
      )
    );
    try {
      await api.post(`/messages/${msg.id}/toggle-complaint`, {
        is_complaint: next,
      });
      // Refresh sidebar badge count
      loadComplaintCount();
    } catch (e) {
      // 409 = duplicate complaint (same LPG already open). Show a clear
      // toast + rollback quietly — no red error banner.
      const status = e?.response?.status;
      if (status === 409) {
        toast.warning(
          e?.response?.data?.detail ||
            "Duplicate complaint — is LPG ke liye complaint pehle se open hai."
        );
      } else {
        setError(formatApiError(e));
      }
      // Rollback
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, is_complaint: !next } : m))
      );
    }
  };

  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    // Auto-grow the textarea up to its max-height
    if (e.target.tagName === "TEXTAREA") {
      e.target.style.height = "auto";
      e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
    }
    const cursor = e.target.selectionStart || val.length;
    const before = val.slice(0, cursor);
    const m = before.match(/@([a-zA-Z0-9._-]*)$/);
    if (m && groupMembers.length > 0) {
      const q = m[1].toLowerCase();
      const candidates = groupMembers
        .filter((u) => u.id !== user?.id)
        .filter(
          (u) =>
            !q ||
            u.username.toLowerCase().startsWith(q) ||
            u.display_name.toLowerCase().includes(q)
        )
        .slice(0, 6);
      setMentionState({ query: q, candidates });
    } else {
      setMentionState(null);
    }
  };

  const insertMention = (member) => {
    if (!inputRef.current) return;
    const cursor = inputRef.current.selectionStart || text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const newBefore = before.replace(/@([a-zA-Z0-9._-]*)$/, `@${member.username} `);
    const newText = newBefore + after;
    setText(newText);
    setMentionState(null);
    setTimeout(() => {
      inputRef.current?.focus();
      const pos = newBefore.length;
      inputRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const renderMessageText = (rawText) => {
    if (!rawText) return null;
    const parts = [];
    const re = /(@[a-zA-Z0-9._-]{3,30})/g;
    let last = 0;
    let match;
    let key = 0;
    while ((match = re.exec(rawText)) !== null) {
      if (match.index > last) parts.push(rawText.slice(last, match.index));
      const handle = match[0].slice(1).toLowerCase();
      const isMe = user?.username && handle === user.username.toLowerCase();
      parts.push(
        <span
          key={`m-${key++}`}
          className={`font-semibold rounded px-1 py-0.5 ${
            isMe
              ? "bg-[#C8835A] text-[#F5F5F0]"
              : "bg-[#C8835A]/15 text-[#C8835A]"
          }`}
        >
          {match[0]}
        </span>
      );
      last = match.index + match[0].length;
    }
    if (last < rawText.length) parts.push(rawText.slice(last));
    return parts;
  };

  // Keep a ref of outbox so async drain doesn't capture stale state
  const outboxRef = useRef(outbox);
  useEffect(() => {
    outboxRef.current = outbox;
  }, [outbox]);

  // Per-temp_id "in flight" lock so the same outbox item is never POSTed twice
  // (multiple drain triggers — online, visibility, focus, poll — fire in parallel).
  const inFlightRef = useRef(new Set());

  // Try to POST one outbox item. Returns true if successfully sent.
  const trySendOutboxItem = async (item) => {
    if (inFlightRef.current.has(item.temp_id)) return false;
    inFlightRef.current.add(item.temp_id);
    try {
      const { data } = await api.post(`/groups/${item.group_id}/messages`, {
        text: item.text,
        reply_to_id: item.reply_to_id || null,
        client_temp_id: item.temp_id,
        attachments: item.attachments || null,
      });
      // Server returned the persisted message. Insert into messages if it
      // belongs to the active group (WS broadcast will dedup via id).
      setMessages((prev) => {
        if (data.group_id !== activeGroupIdRef.current) return prev;
        if (prev.some((m) => m.id === data.id)) return prev;
        return [...prev, data];
      });
      // Remove the outbox entry on success
      setOutbox((prev) => prev.filter((o) => o.temp_id !== item.temp_id));
      return true;
    } catch (e) {
      // Permanent rejections (4xx that are NOT network errors): mark failed
      // so user sees the error and we don't keep retrying forever.
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500) {
        const reason = formatApiError(e);
        setOutbox((prev) =>
          prev.map((o) =>
            o.temp_id === item.temp_id ? { ...o, status: "failed", error: reason } : o
          )
        );
        setError(reason);
        return false;
      }
      // Network / 5xx — keep as pending, will retry on next online/poll
      return false;
    } finally {
      inFlightRef.current.delete(item.temp_id);
    }
  };

  // Drain the outbox: try to send everything pending. Called on mount, on
  // `online` event, on WS reconnect, and on a 20s safety-net poll.
  const drainingRef = useRef(false);
  const drainOutbox = async () => {
    if (drainingRef.current) return; // single-flight drain
    drainingRef.current = true;
    try {
      const items = outboxRef.current.filter(
        (o) => o.status === "pending" && o.group_id && !inFlightRef.current.has(o.temp_id)
      );
      for (const item of items) {
        // Sequential so order is preserved within a group
        // eslint-disable-next-line no-await-in-loop
        const ok = await trySendOutboxItem(item);
        if (!ok && !navigator.onLine) break;
      }
    } finally {
      drainingRef.current = false;
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if ((!t && pendingAttachments.length === 0) || !activeGroupId) return;
    setSending(true);
    // Optimistic: build a temp message and push to outbox immediately.
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const replyTo = replyingTo
      ? {
          id: replyingTo.id,
          sender_display_name: replyingTo.sender_display_name || "",
          // If the user chose "Reply to selection", quote ONLY the
          // highlighted chunk instead of the full parent text — WhatsApp
          // parity. Falls back to full text on a plain Reply.
          text: (
            replyingTo.reply_selection_text ||
            replyingTo.text ||
            ""
          ).slice(0, 200),
        }
      : null;
    const tempMsg = {
      temp_id: tempId,
      group_id: activeGroupId,
      sender_id: user?.id,
      sender_username: user?.username,
      sender_display_name: user?.display_name || user?.username,
      text: t,
      reply_to_id: replyingTo?.id || null,
      reply_to: replyTo,
      created_at: new Date().toISOString(),
      reactions: {},
      status: "pending",
      attachments: pendingAttachments.length ? pendingAttachments : null,
    };
    setOutbox((prev) => [...prev, tempMsg]);
    setText("");
    setReplyingTo(null);
    setPendingAttachments([]);
    setSending(false);
    // Reset textarea height after clearing text
    if (inputRef.current && inputRef.current.tagName === "TEXTAREA") {
      inputRef.current.style.height = "auto";
    }
    // Fire-and-forget attempt; drainOutbox handles retries
    trySendOutboxItem(tempMsg);
  };

  const retryFailed = (item) => {
    setOutbox((prev) =>
      prev.map((o) =>
        o.temp_id === item.temp_id ? { ...o, status: "pending", error: null } : o
      )
    );
    setTimeout(() => drainOutbox(), 0);
  };

  const discardFailed = (item) => {
    setOutbox((prev) => prev.filter((o) => o.temp_id !== item.temp_id));
  };

  const handleLogout = () => {
    try {
      wsRef.current?.close();
    } catch (_) {}
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="h-screen w-full flex bg-gradient-to-br from-[#F5F5F0] via-[#F0F1EC] to-[#EBE8E0] relative overflow-hidden"
      style={{ fontFamily: "Work Sans, sans-serif" }}
    >
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-20"
          onClick={() => setSidebarOpen(false)}
          data-testid="chat-sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 fixed md:relative inset-y-0 left-0 z-30 w-[85vw] max-w-[340px] md:w-80 flex-shrink-0 border-r border-[#DCDCD3] bg-gradient-to-b from-[#EFEEE8] to-[#E4E2D8] h-[100dvh] md:h-screen flex flex-col transition-transform duration-300 ease-out shadow-xl md:shadow-none`}
      >
        {/* Profile header */}
        <div className="p-4 md:p-5 border-b border-[#DCDCD3] bg-gradient-to-r from-[#385C4B] to-[#2D4A3C] text-[#F5F5F0] flex-shrink-0 max-h-[60dvh] overflow-y-auto md:max-h-none md:overflow-visible">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center text-[#F5F5F0] font-semibold shadow-sm border border-white/15"
              style={{ background: avatarColor(user?.username || user?.display_name) }}
            >
              {initialsOf(user?.display_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-sm font-semibold text-[#F5F5F0] truncate"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="chat-current-user"
              >
                {user?.display_name}
              </div>
              <div className="text-[11px] font-mono tracking-wide uppercase text-[#F5F5F0]/70 flex items-center gap-1.5">
                {user?.role === "admin" && <Shield className="w-3 h-3" strokeWidth={1.75} />}
                {user?.role}
              </div>
            </div>
            <button
              onClick={() => navigate("/home")}
              data-testid="chat-goto-home-button"
              className="p-2 rounded-md hover:bg-white/10 transition-colors text-[#F5F5F0]/80 hover:text-[#F5F5F0]"
              title="Home"
            >
              <Home className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="chat-logout-button"
              className="p-2 rounded-md hover:bg-white/10 transition-colors text-[#F5F5F0]/80 hover:text-[#F5F5F0]"
              title="Logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <button
            onClick={() => setSidebarMenuOpen((v) => !v)}
            data-testid="chat-sidebar-menu-toggle"
            className="md:hidden mt-3 w-full text-[11px] tracking-[0.2em] uppercase font-mono text-[#F5F5F0] bg-white/10 border border-[#F5F5F0]/30 py-2 rounded-md flex items-center justify-center gap-2"
          >
            Menu
            {!sidebarMenuOpen && complaintCount > 0 && (
              <span className="bg-[#C8553D] text-white rounded-full text-[9px] px-1.5 py-0.5 tracking-normal">{complaintCount}</span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${sidebarMenuOpen ? "rotate-180" : ""}`} />
          </button>
          <div
            data-testid="chat-sidebar-menu"
            className={`${sidebarMenuOpen ? "grid" : "hidden"} md:block grid-cols-2 gap-1.5 mt-2 md:mt-0 [&>button]:mt-0 [&>button]:text-[10px] [&>button]:tracking-wider [&>button]:px-1.5 [&>button]:leading-tight [&>button]:gap-1 md:[&>button]:mt-2 md:[&>button]:text-xs md:[&>button]:tracking-[0.2em] md:[&>button]:px-0 md:[&>button]:gap-2 md:[&>button:first-child]:mt-4`}
          >
          {user?.role === "admin" && (
            <button
              onClick={() => navigate("/admin")}
              data-testid="chat-goto-admin-button"
              className="mt-4 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md"
            >
              Admin Console
            </button>
          )}
          <button
            onClick={() => navigate("/lpg-report")}
            data-testid="chat-goto-lpg-report-button"
            className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
          >
            <Flame className="w-3.5 h-3.5" strokeWidth={1.75} />
            LPG Report
          </button>
          {ekycVisible && (
            <button
              onClick={() => navigate("/ekyc")}
              data-testid="chat-goto-ekyc-button"
              className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
            >
              <IdCard className="w-3.5 h-3.5" strokeWidth={1.75} />
              EKYC
            </button>
          )}
          <button
            onClick={() => navigate("/bank-details")}
            data-testid="chat-goto-bank-details-button"
            className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
          >
            <Landmark className="w-3.5 h-3.5" strokeWidth={1.75} />
            Bank Details
          </button>
          <button
            onClick={() =>
              navigate(user?.role === "admin" ? "/admin?tab=wallet" : "/agency-ledger")
            }
            data-testid="chat-goto-agency-ledger-button"
            className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
          >
            <Wallet className="w-3.5 h-3.5" strokeWidth={1.75} />
            {user?.role === "admin" ? "Wallet & Ledger" : "My Agency Ledger"}
          </button>
          {(user?.role === "admin" || user?.auto_join_groups) && (
            <button
              onClick={() => navigate("/bulk-paste")}
              data-testid="chat-goto-bulk-paste-button"
              className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
            >
              <ClipboardPaste className="w-3.5 h-3.5" strokeWidth={1.75} />
              Bulk Paste
            </button>
          )}
          {user?.role === "admin" && (
            <button
              onClick={() => setBulkResendOpen(true)}
              data-testid="chat-goto-bulk-resend-button"
              className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
              title="Bharat Gas · rows staff-attributed, agency posted real-time"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
              Bulk Resend
            </button>
          )}
          {(user?.role === "admin" || hasCooling) && (
            <button
              onClick={() => navigate(user?.role === "admin" ? "/cooling-tracker" : "/my-cooling")}
              data-testid="chat-goto-cooling-button"
              className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
            >
              <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />
              Cooling
            </button>
          )}
          <button
            onClick={() => navigate("/complaints")}
            data-testid="chat-goto-complaints-button"
            className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2 relative"
          >
            <Inbox className="w-3.5 h-3.5" strokeWidth={1.75} />
            Complaints
            {complaintCount > 0 && (
              <span
                data-testid="chat-complaint-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#C8553D] text-[10px] font-bold flex items-center justify-center"
              >
                {complaintCount > 99 ? "99+" : complaintCount}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              if (!activeGroup) {
                window.alert("Pehle koi agency select karein (left list se).");
                return;
              }
              if (!staffSheets.length) {
                window.alert(
                  `${activeGroup.name} ke liye abhi koi real-time sheet assigned nahi hai.\n\nAdmin se kahein: Admin Console → Real Time tab → Add Real-Time Sheet → Scope me ${activeGroup.name} tick karein.`,
                );
                return;
              }
              setShowStaffSheets(true);
            }}
            data-testid="chat-goto-realtime-button"
            title="Real-time delivery sheet view (today + previous dates · LPG ID · DAC)"
            className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#385C4B] transition-colors py-2 rounded-md flex items-center justify-center gap-2 relative"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8553D] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C8553D]"></span>
            </span>
            <Radio className="w-3.5 h-3.5" strokeWidth={1.75} />
            Real Time
            {staffSheets.length > 0 && activeGroup && (
              <span
                data-testid="chat-realtime-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#385C4B] border border-[#F5F5F0] text-[10px] font-bold flex items-center justify-center"
              >
                {staffSheets.length}
              </span>
            )}
          </button>
          {activeGroup && isBharatGasGroup(activeGroup) && (
            <button
              onClick={() => setShowResend(true)}
              data-testid="chat-resend-button"
              title="Resend DAC · pending requests dekho aur agency tick lagao"
              className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-amber-400 hover:text-[#1C1C19] hover:border-amber-400 transition-colors py-2 rounded-md flex items-center justify-center gap-2 relative"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
              Resend DAC
              {resendPendingCount > 0 && (
                <span
                  data-testid="chat-resend-pending-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {resendPendingCount}
                </span>
              )}
            </button>
          )}
          {user?.role === "admin" && (
            <button
              onClick={() => navigate("/delivery-logins")}
              data-testid="chat-delivery-logins-button"
              title="Delivery Man Logins · Indane agencies ki credentials central page"
              className="mt-2 w-full text-xs tracking-[0.2em] uppercase font-mono text-[#F5F5F0] border border-[#F5F5F0]/30 hover:bg-[#F5F5F0] hover:text-[#1C1C19] hover:border-[#F5F5F0] transition-colors py-2 rounded-md flex items-center justify-center gap-2"
            >
              <KeyRound className="w-3.5 h-3.5" strokeWidth={1.75} />
              Delivery Logins
            </button>
          )}
          </div>
        </div>

        {/* Groups header */}
        <div className="px-5 pt-5 pb-2 flex items-center justify-between">
          <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-[#6B6B61]">
            Groups · {groups.length}
          </div>
          <div className="flex items-center gap-1">
            {user?.role === "admin" && (
              <button
                onClick={() => setShowCreate(true)}
                data-testid="create-group-button"
                className="p-1.5 rounded-md bg-[#385C4B]/10 hover:bg-[#385C4B] hover:text-[#F5F5F0] text-[#385C4B] transition-colors"
                title="New group"
              >
                <Plus className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
          </div>
        </div>

        {/* Iter75: Group name search — filters live above pills */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#385C4B]/60 pointer-events-none"
              strokeWidth={2}
            />
            <input
              type="text"
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search groups…"
              data-testid="chat-sidebar-search-input"
              className="w-full pl-8 pr-8 py-1.5 text-xs bg-white border border-[#DCDCD3] rounded-md outline-none focus:ring-2 focus:ring-[#385C4B]/30 focus:border-[#385C4B] placeholder:text-[#6B6B61] transition-colors"
            />
            {groupSearch && (
              <button
                onClick={() => setGroupSearch("")}
                data-testid="chat-sidebar-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[#F5F5F0] text-[#6B6B61] hover:text-[#1C1C19] transition-colors"
                title="Clear search"
              >
                {lpgSearching ? (
                  <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />
                ) : (
                  <X className="w-3 h-3" strokeWidth={2.5} />
                )}
              </button>
            )}
          </div>
        </div>

        {/* WhatsApp-style filter pills */}
        <div className="px-4 pb-2 flex items-center gap-1.5 flex-wrap" data-testid="chat-sidebar-filters">
          {[
            { id: "all", label: "All", count: groups.length },
            { id: "unread", label: "Unread", count: unreadGroupCount },
            { id: "favourite", label: "Favourites", count: favouriteGroupCount },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setGroupFilter(f.id)}
              data-testid={`chat-filter-${f.id}`}
              className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                groupFilter === f.id
                  ? "bg-[#385C4B] text-white"
                  : "bg-white text-[#6B6B61] hover:text-[#385C4B] border border-[#DCDCD3]"
              }`}
            >
              {f.label}
              {f.count > 0 && (
                <span className={`ml-1 ${groupFilter === f.id ? "opacity-70" : ""}`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Groups list */}
        <div className="flex-1 overflow-y-auto">
          {groups.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-[#6B6B61]">
              <Hash className="w-6 h-6 mx-auto mb-2 opacity-50" strokeWidth={1.25} />
              No groups yet.
              {user?.role === "admin" && " Create one to start chatting."}
            </div>
          )}
          {filteredGroups.length === 0 && groups.length > 0 && (
            <div
              className="px-5 py-8 text-center text-xs text-[#6B6B61] italic"
              data-testid="chat-sidebar-search-empty"
            >
              {groupSearch.trim()
                ? lpgSearching
                  ? "Chat history search kar rahe hain…"
                  : `Kisi group ya message me nahi mila "${groupSearch.trim()}" ke liye`
                : groupFilter === "unread"
                ? "No unread groups"
                : groupFilter === "favourite"
                ? "No favourites yet — tap the ⭐ on any group"
                : "No matching groups"}
            </div>
          )}
          {filteredGroups.map((g) => {
            const active = g.id === activeGroupId;
            const lpgHit = lpgSearchHits[g.id];
            return (
              <button
                key={g.id}
                onClick={() => {
                  selectGroup(g.id);
                  if (lpgHit?.message_id) {
                    setHighlightMsgId(lpgHit.message_id);
                  }
                }}
                data-testid="chat-sidebar-group-item"
                className={`w-full text-left px-5 py-3 border-l-2 transition-colors group ${
                  active
                    ? "border-[#385C4B] bg-white shadow-sm"
                    : "border-transparent hover:bg-black/5"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold text-sm flex-shrink-0 shadow-sm relative"
                    style={{ background: avatarColor(g.id) }}
                  >
                    {initialsOf(g.name)}
                    {g.unread_mentions > 0 && (
                      <span
                        data-testid="chat-sidebar-unread-badge"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#C8835A] text-white text-[10px] font-mono font-bold flex items-center justify-center px-1 border-2 border-[#EFEEE8]"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        @{g.unread_mentions}
                      </span>
                    )}
                    {(urgentSummary.groups?.[g.id]?.urgent_pending || 0) > 0 && (
                      <span
                        data-testid="chat-sidebar-urgent-badge"
                        title={`${urgentSummary.groups[g.id].urgent_pending} delivery pending for 24h+ · URGENT`}
                        className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#C8553D] text-white text-[10px] font-mono font-bold flex items-center justify-center px-1 border-2 border-[#EFEEE8] animate-pulse"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        !{urgentSummary.groups[g.id].urgent_pending > 99 ? "99+" : urgentSummary.groups[g.id].urgent_pending}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div
                        className={`text-sm truncate flex items-center gap-1 ${
                          g.unread_mentions > 0
                            ? "font-bold text-[#1C1C19]"
                            : "font-semibold text-[#1C1C19]"
                        }`}
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {g.name}
                        {g.is_pinned && (
                          <Pin
                            className="w-3 h-3 text-[#385C4B] flex-shrink-0"
                            strokeWidth={2.5}
                            data-testid={`group-pin-icon-${g.id}`}
                          />
                        )}
                        {g.is_favourite && (
                          <Star
                            className="w-3 h-3 text-[#E0A020] fill-[#E0A020] flex-shrink-0"
                            strokeWidth={2}
                            data-testid={`group-fav-icon-${g.id}`}
                          />
                        )}
                      </div>
                      {g.last_message && (
                        <div className="text-[10px] font-mono text-[#6B6B61] flex-shrink-0">
                          {formatTime(g.last_message.created_at)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="text-xs text-[#6B6B61] truncate flex-1">
                        {lpgHit ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-mono text-[#385C4B] bg-[#385C4B]/10 px-1.5 py-0.5 rounded"
                            data-testid={`sidebar-lpg-match-${g.id}`}
                            title="Chat history me match mila — click karo to jump"
                          >
                            <Target className="w-2.5 h-2.5" strokeWidth={2.5} />
                            LPG match: {lpgHit.lpg_id || lpgHit.mobile || "—"}
                          </span>
                        ) : g.last_message ? (
                          `${g.last_message.sender_display_name}: ${
                            g.last_message.deleted ? "🗑 deleted" : g.last_message.text
                          }`
                        ) : (
                          `${g.member_ids.length} members`
                        )}
                      </div>
                      {/* Hover-only inline pin/fav toggles */}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroupPin(g);
                        }}
                        data-testid={`group-pin-btn-${g.id}`}
                        title={g.is_pinned ? "Unpin" : "Pin to top"}
                        className={`opacity-0 group-hover:opacity-100 focus:opacity-100 text-[#6B6B61] hover:text-[#385C4B] rounded p-0.5 transition-opacity flex-shrink-0 cursor-pointer ${g.is_pinned ? "text-[#385C4B] opacity-100" : ""}`}
                      >
                        <Pin className="w-3 h-3" strokeWidth={2} />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroupFavourite(g);
                        }}
                        data-testid={`group-fav-btn-${g.id}`}
                        title={g.is_favourite ? "Remove favourite" : "Add to favourites"}
                        className={`opacity-0 group-hover:opacity-100 focus:opacity-100 text-[#6B6B61] hover:text-[#E0A020] rounded p-0.5 transition-opacity flex-shrink-0 cursor-pointer ${g.is_favourite ? "text-[#E0A020] opacity-100" : ""}`}
                      >
                        <Star className={`w-3 h-3 ${g.is_favourite ? "fill-[#E0A020]" : ""}`} strokeWidth={2} />
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Main pane */}
      <section className="flex-1 flex flex-col h-screen relative">
        {activeGroup ? (
          <>
            {/* Header */}
            <div className="p-4 md:p-5 border-b border-[#DCDCD3] bg-white flex items-center gap-3 shadow-sm">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 -ml-1 rounded-md hover:bg-black/5 text-[#385C4B]"
                data-testid="chat-open-sidebar-button"
                aria-label="Open sidebar"
              >
                <Menu className="w-5 h-5" strokeWidth={1.75} />
              </button>
              <div
                className="w-10 h-10 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold shadow-sm flex-shrink-0"
                style={{ background: avatarColor(activeGroup.id) }}
              >
                {initialsOf(activeGroup.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-base font-semibold text-[#1C1C19] tracking-tight truncate"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="chat-active-group-name"
                >
                  {activeGroup.name}
                </div>
                {/* Description sits directly under the name — same slot as
                    WhatsApp's group subtitle. Tap opens the editor for
                    admins; regular members get a read-only tooltip. */}
                {activeGroup.description ? (
                  <button
                    type="button"
                    onClick={() => user?.role === "admin" && setDescEditorOpen(true)}
                    data-testid="chat-active-group-description"
                    title={
                      user?.role === "admin"
                        ? "Tap to edit description"
                        : activeGroup.description
                    }
                    className={`text-[11px] text-[#6B6B61] truncate max-w-full text-left block ${
                      user?.role === "admin" ? "hover:text-[#385C4B] cursor-pointer" : "cursor-default"
                    }`}
                  >
                    {activeGroup.description}
                  </button>
                ) : (
                  user?.role === "admin" && (
                    <button
                      type="button"
                      onClick={() => setDescEditorOpen(true)}
                      data-testid="chat-active-group-description-empty"
                      className="text-[11px] text-[#385C4B]/70 hover:text-[#385C4B] italic block text-left"
                    >
                      + Add description
                    </button>
                  )
                )}
                <div className="text-xs text-[#6B6B61] flex items-center gap-1.5 mt-0.5">
                  <Users className="w-3 h-3" strokeWidth={1.5} />
                  {activeGroup.member_ids.length} members
                </div>
              </div>
              {coolingGroupMap[activeGroup.id] && (
                <button
                  onClick={() => navigate(`/my-cooling?agency=${coolingGroupMap[activeGroup.id]}`)}
                  data-testid="chat-group-cooling-button"
                  title="Cooling — kis date me kitne consumer ready honge"
                  className="flex-shrink-0 p-2 rounded-md text-[#C8553D] hover:bg-[#C8553D]/10 transition-colors"
                >
                  <CalendarClock className="w-4 h-4" strokeWidth={1.75} />
                </button>
              )}
              <button
                onClick={() => setShowLpgSearch(true)}
                data-testid="chat-lpg-search-button"
                title="Global Search — find any LPG ID, Consumer ID or Mobile across every group"
                className="flex-shrink-0 p-2 rounded-md text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors"
              >
                <Search className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <button
                onClick={() => setDeliveredFilter((v) => !v)}
                data-testid="chat-delivered-filter-button"
                title={
                  deliveredFilter
                    ? "Delivered filter ON — showing green cards only. Click to show all."
                    : "Delivered by us — sirf green (delivered) cards dikhao"
                }
                className={`flex-shrink-0 p-2 rounded-md transition-colors ${
                  deliveredFilter
                    ? "bg-[#385C4B] text-white"
                    : "text-[#385C4B] hover:bg-[#385C4B]/10"
                }`}
              >
                <PackageCheck className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <button
                onClick={() => setShowBulkComplaint(true)}
                data-testid="chat-bulk-complaint-button"
                title="Bulk Complaint — paste LPG IDs with wrong DAC, auto-flag those that match this agency's data"
                className="flex-shrink-0 p-2 rounded-md text-red-700 hover:bg-red-500/10 transition-colors"
              >
                <Flag className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <button
                onClick={() => setShowDeliveryModal(true)}
                data-testid="chat-delivery-dashboard-button"
                title="Delivery Dashboard — mark deliveries Done / DAC Incorrect / Cancelled"
                className="flex-shrink-0 p-2 rounded-md text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors relative"
              >
                <ClipboardList className="w-4 h-4" strokeWidth={1.75} />
                {deliveryStatus && deliveryStatus.totals.pending > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#C8553D] text-[9px] font-mono font-semibold text-white tabular-nums leading-none"
                    data-testid="chat-delivery-dashboard-badge"
                  >
                    {deliveryStatus.totals.pending > 99
                      ? "99+"
                      : deliveryStatus.totals.pending}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowGroupComplaints(true)}
                data-testid="chat-group-complaints-button"
                title="Complaint Box — is group ki complaints + admin ke resolution ek saath dekhein"
                className="flex-shrink-0 p-2 rounded-md text-[#C8553D] hover:bg-[#C8553D]/10 transition-colors relative"
              >
                <Inbox className="w-4 h-4" strokeWidth={1.75} />
                {deliveryStatus && ((deliveryStatus.totals.complaint || 0) + (deliveryStatus.totals.cancelled || 0)) > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#C8553D] text-[9px] font-mono font-semibold text-white tabular-nums leading-none"
                    data-testid="chat-group-complaints-badge"
                  >
                    {((deliveryStatus.totals.complaint || 0) + (deliveryStatus.totals.cancelled || 0)) > 99
                      ? "99+"
                      : (deliveryStatus.totals.complaint || 0) + (deliveryStatus.totals.cancelled || 0)}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowCustomerDetails(true)}
                data-testid="chat-customer-details-button"
                title="Customer Details — is group ki saari LPG ID / Mobile / DAC ek saath dekhein"
                className="flex-shrink-0 p-2 rounded-md text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors relative"
              >
                <Users className="w-4 h-4" strokeWidth={1.75} />
                {deliveryStatus && (deliveryStatus.totals.total || 0) > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#385C4B] text-[9px] font-mono font-semibold text-white tabular-nums leading-none"
                    data-testid="chat-customer-details-badge"
                  >
                    {deliveryStatus.totals.total > 99 ? "99+" : deliveryStatus.totals.total}
                  </span>
                )}
              </button>
              <button
                onClick={clearChatHistory}
                data-testid="chat-clear-history-button"
                title="Clear chat history (only for you · LPG report unaffected)"
                className="flex-shrink-0 p-2 rounded-md text-[#6B6B61] hover:bg-[#F5F5F0] hover:text-[#1C1C19] transition-colors"
              >
                <Eraser className="w-4 h-4" strokeWidth={1.75} />
              </button>
              {staffSheets.length > 0 && (
                <button
                  onClick={() => setShowStaffSheets(true)}
                  data-testid="chat-staff-sheets-button"
                  title={`Real Time · ${staffSheets.length} sheet${staffSheets.length > 1 ? "s" : ""}`}
                  className="flex-shrink-0 p-2 rounded-md text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors relative"
                >
                  <Radio className="w-4 h-4" strokeWidth={1.75} />
                  <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8553D] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C8553D]"></span>
                  </span>
                </button>
              )}
              {user?.role === "admin" && (
                <button
                  onClick={() => setShowAddMember(true)}
                  data-testid="chat-add-member-button"
                  title="Manage members (add / remove)"
                  className="flex-shrink-0 p-2 rounded-md text-[#6B6B61] hover:bg-[#F5F5F0] hover:text-[#1C1C19] transition-colors"
                >
                  <UserPlus className="w-4 h-4" strokeWidth={1.75} />
                </button>
              )}
            </div>

            {/* URGENT — pending deliveries older than 24 hours. Per-group.
                Requested by user (Jul 2026): "24 hours me delivery marks
                na kare to group me upar notification icon me dikha de,
                urgent basis pe delivery kare, 45 pending delivery hai".
                Clicking opens the Delivery Dashboard filtered to Pending. */}
            {deliveryStatus && (deliveryStatus.totals.urgent_pending || 0) > 0 && (
              <button
                type="button"
                onClick={() => {
                  setDeliveryModalFilter("pending");
                  setShowDeliveryModal(true);
                }}
                data-testid="chat-urgent-banner"
                className="w-full border-b border-[#C8553D]/40 bg-gradient-to-r from-[#C8553D]/15 via-[#C8553D]/10 to-transparent px-2 sm:px-6 md:px-8 py-2 flex items-center gap-1.5 sm:gap-2 text-[#7A2E1F] hover:bg-[#C8553D]/20 transition-colors text-left overflow-hidden"
              >
                <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8553D] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-[#C8553D]"></span>
                </span>
                <BellRing className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" strokeWidth={2} />
                <span className="text-[11px] sm:text-xs font-semibold flex-shrink-0" style={{ fontFamily: "Outfit, sans-serif" }}>
                  URGENT
                </span>
                {/* Mobile: compact "N pending (24h+)". Desktop: full Hinglish sentence. */}
                <span className="text-[11px] sm:text-xs sm:hidden truncate min-w-0">
                  <span className="font-mono font-bold tabular-nums text-[#C8553D]">
                    {deliveryStatus.totals.urgent_pending}
                  </span>{" "}
                  pending · 24h+
                </span>
                <span className="hidden sm:inline text-xs truncate min-w-0">
                  <span className="font-mono font-bold tabular-nums text-[#C8553D]">
                    {deliveryStatus.totals.urgent_pending}
                  </span>{" "}
                  pending delivery {deliveryStatus.totals.urgent_pending === 1 ? "hai" : "hain"} · 24h se zyada purani · tap karke tick karein
                </span>
              </button>
            )}

            {/* Per-group live delivery status strip — surfaces the
                "distributor jitna delivery karega usko pata chalna
                chahiye" ask. Uses existing status fields (marked_by +
                status). One click expands the pending LPG-id list so
                the agency knows EXACTLY which entries still need
                acknowledgement. */}
            {deliveryStatus && deliveryStatus.totals.sent > 0 && (
              <div
                className="border-b border-[#DCDCD3] bg-[#FEFEFC] cursor-pointer hover:bg-[#F7F6EE] transition-colors"
                data-testid="chat-delivery-status-strip"
                onClick={() => setShowDeliveryModal(true)}
                title="Click to open the full delivery dashboard — tick ✓ delivered or ✗ DAC incorrect per row"
              >
                <div className="px-2 sm:px-6 md:px-8 py-2.5 flex items-center flex-wrap gap-x-2 sm:gap-x-3 gap-y-1 overflow-hidden">
                  <span className="text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.15em] text-[#6B6B61] font-mono flex-shrink-0">
                    Delivery
                  </span>
                  <div className="flex items-center gap-2 text-xs" data-testid="delivery-status-sent">
                    <span className="text-[#6B6B61]">Sent</span>
                    <span className="font-mono font-semibold text-[#1C1C19] tabular-nums">
                      {deliveryStatus.totals.sent}
                    </span>
                  </div>
                  <span className="w-px h-3 bg-[#DCDCD3]" />
                  <div className="flex items-center gap-2 text-xs" data-testid="delivery-status-delivered">
                    <span className="text-emerald-700">Delivered</span>
                    <span className="font-mono font-semibold text-emerald-700 tabular-nums">
                      {deliveryStatus.totals.delivered}
                    </span>
                  </div>
                  <span className="w-px h-3 bg-[#DCDCD3]" />
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (deliveryStatus.totals.pending > 0) {
                        setDeliveryModalFilter("pending");
                        setShowDeliveryModal(true);
                      }
                    }}
                    disabled={deliveryStatus.totals.pending === 0}
                    data-testid="delivery-status-pending-toggle"
                    className={`flex items-center gap-2 text-xs ${
                      deliveryStatus.totals.pending > 0
                        ? "cursor-pointer hover:text-[#8A6046]"
                        : "cursor-default"
                    }`}
                    title={
                      deliveryStatus.totals.pending > 0
                        ? "Open Delivery Dashboard · view + tick pending LPG IDs"
                        : "No pending — all sent entries have been acknowledged ✓"
                    }
                  >
                    <span className="text-[#8A6046]">Pending</span>
                    <span className="font-mono font-semibold text-[#8A6046] tabular-nums">
                      {deliveryStatus.totals.pending}
                    </span>
                  </button>
                  {deliveryStatus.totals.complaint > 0 && (
                    <>
                      <span className="w-px h-3 bg-[#DCDCD3]" />
                      <div className="flex items-center gap-2 text-xs" data-testid="delivery-status-complaint">
                        <span className="text-red-700">Complaint</span>
                        <span className="font-mono font-semibold text-red-700 tabular-nums">
                          {deliveryStatus.totals.complaint}
                        </span>
                      </div>
                    </>
                  )}
                  {(deliveryStatus.totals.cancelled || 0) > 0 && (
                    <>
                      <span className="w-px h-3 bg-[#DCDCD3]" />
                      <div className="flex items-center gap-2 text-xs" data-testid="delivery-status-cancelled">
                        <span className="text-slate-600">Cancelled</span>
                        <span className="font-mono font-semibold text-slate-700 tabular-nums">
                          {deliveryStatus.totals.cancelled}
                        </span>
                      </div>
                    </>
                  )}
                  {/* Progress bar — proportion delivered so the eye
                      catches "kitna kar liya" at a glance. */}
                  <div className="ml-auto flex items-center gap-2 min-w-[100px]">
                    <div className="flex-1 h-1.5 bg-[#EFEEE6] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.round(
                            (deliveryStatus.totals.delivered /
                              Math.max(1, deliveryStatus.totals.sent)) * 100
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="text-[10px] font-mono text-[#6B6B61] tabular-nums flex-shrink-0">
                      {Math.round(
                        (deliveryStatus.totals.delivered /
                          Math.max(1, deliveryStatus.totals.sent)) * 100
                      )}%
                    </span>
                  </div>
                </div>
                {/* Inline pending list removed — clicking Pending N in the
                    strip opens the Delivery Dashboard modal instead. Keeps
                    the chat area from getting cut off on groups with 100+
                    pending entries. */}
              </div>
            )}

            {/* Full-list Delivery Dashboard modal — opens on strip click.
                Group member scrolls through every sent LPG ID, ticks
                ✓ (delivered) or ✗ (DAC incorrect → complaint) per row.
                Historical chat cards below stay untouched (audit
                trail). Each tick fires the SAME per-entry POST that
                the chat bubbles use, so the Google Sheet webhook
                updates in real-time. */}
            {showDeliveryModal && deliveryStatus && (() => {
              // Sep 2026: helpers for the new date filter + timestamp subline.
              // All timestamps are stored UTC ISO; we convert to IST for display.
              const IST_TZ = "Asia/Kolkata";
              const istDateKey = (iso) => {
                // "YYYY-MM-DD" in IST — used for equality checks (today/yesterday).
                if (!iso) return "";
                try {
                  return new Date(iso).toLocaleDateString("en-CA", { timeZone: IST_TZ });
                } catch { return ""; }
              };
              const istPretty = (iso) => {
                if (!iso) return "";
                try {
                  const d = new Date(iso);
                  return d.toLocaleString("en-GB", {
                    timeZone: IST_TZ,
                    day: "2-digit", month: "short",
                    hour: "2-digit", minute: "2-digit", hour12: true,
                  });
                } catch { return ""; }
              };
              const relative = (iso) => {
                if (!iso) return "";
                try {
                  const ms = Date.now() - new Date(iso).getTime();
                  if (ms < 60_000) return "just now";
                  const mins = Math.floor(ms / 60_000);
                  if (mins < 60) return `${mins}m ago`;
                  const hrs = Math.floor(mins / 60);
                  if (hrs < 24) return `${hrs}h ago`;
                  const days = Math.floor(hrs / 24);
                  return `${days}d ago`;
                } catch { return ""; }
              };
              const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: IST_TZ });
              const yesterdayKey = (() => {
                const d = new Date();
                d.setUTCDate(d.getUTCDate() - 1);
                return d.toLocaleDateString("en-CA", { timeZone: IST_TZ });
              })();
              const dateRangeMatch = (iso) => {
                if (deliveryDateFilter === "all") return true;
                const k = istDateKey(iso);
                if (!k) return false;
                if (deliveryDateFilter === "today") return k === todayKey;
                if (deliveryDateFilter === "yesterday") return k === yesterdayKey;
                if (deliveryDateFilter === "last7") {
                  const d = new Date(iso);
                  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
                  return d.getTime() >= cutoff;
                }
                if (deliveryDateFilter === "custom") {
                  const { from, to } = deliveryDateCustom;
                  if (from && k < from) return false;
                  if (to && k > to) return false;
                  return true;
                }
                return true;
              };
              const filtered = (deliveryStatus.entries || []).filter((r) => {
                // Status filter (existing)
                if (deliveryModalFilter !== "all") {
                  if (deliveryModalFilter === "delivered") {
                    if (r.status !== "delivered" && r.status !== "resent") return false;
                  } else if (r.status !== deliveryModalFilter) return false;
                }
                // Date filter (new) — key off `created_at` (dispatch date)
                // because that's the ledger-aligned semantic. Delivered rows
                // are still included if their dispatch date matches — same
                // as how LPG Report scopes by uploader/dispatch date.
                if (!dateRangeMatch(r.created_at)) return false;
                return true;
              });

              // Oct 2026: date-aware status chip counts. When filter is
              // "all" we show the server's global totals (same as before).
              // Otherwise compute counts from entries that match the date
              // range so admin sees "Today 44 delivered" instead of global
              // 520. Mark All Done ka count + action bhi iss `dateFilteredPending`
              // ko hi use karta hai so bulk flip sirf visible range affect kare.
              const dateFilteredEntries = deliveryDateFilter === "all"
                ? (deliveryStatus.entries || [])
                : (deliveryStatus.entries || []).filter((r) => dateRangeMatch(r.created_at));
              const dateCounts = deliveryDateFilter === "all"
                ? deliveryStatus.totals
                : {
                    sent:      dateFilteredEntries.length,
                    pending:   dateFilteredEntries.filter((r) => r.status === "pending").length,
                    delivered: dateFilteredEntries.filter((r) => r.status === "delivered" || r.status === "resent").length,
                    complaint: dateFilteredEntries.filter((r) => r.status === "complaint").length,
                    cancelled: dateFilteredEntries.filter((r) => r.status === "cancelled").length,
                    urgent_pending: deliveryStatus.totals.urgent_pending || 0,
                  };
              const dateFilteredPending = dateFilteredEntries.filter((r) => r.status === "pending");
              const setEntryStatus = async (row, newStatus) => {
                try {
                  const { data } = await api.post(
                    `/groups/${activeGroupId}/messages/${row.message_id}/lpg/${row.entry_index}`,
                    { status: newStatus }
                  );
                  // Reflect in local `messages` so the historical chat
                  // card updates too — same immutable update pattern.
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === row.message_id
                        ? { ...m, lpg_entries: data.lpg_entries }
                        : m
                    )
                  );
                  // Optimistic dashboard flip
                  setDeliveryStatus((prev) => {
                    if (!prev) return prev;
                    const label = newStatus === "correct"
                      ? "delivered"
                      : newStatus === "wrong"
                        ? "complaint"
                        : newStatus === "cancelled"
                          ? "cancelled"
                          : newStatus;
                    const next_entries = prev.entries.map((r) =>
                      r.lpg_id === row.lpg_id
                        ? { ...r, status: label, raw_status: newStatus,
                            marked_by: user?.display_name || user?.username || "me" }
                        : r
                    );
                    return {
                      ...prev,
                      entries: next_entries,
                      totals: {
                        ...prev.totals,
                        delivered: next_entries.filter((r) => r.status === "delivered" || r.status === "resent").length,
                        pending: next_entries.filter((r) => r.status === "pending").length,
                        complaint: next_entries.filter((r) => r.status === "complaint").length,
                        cancelled: next_entries.filter((r) => r.status === "cancelled").length,
                      },
                    };
                  });
                } catch (e) {
                  setError(formatApiError(e));
                }
              };
              const badgeCls = (s) => ({
                pending: "bg-[#F0DFA5] text-[#7A5A1F]",
                delivered: "bg-emerald-500/20 text-emerald-800",
                resent: "bg-blue-500/20 text-blue-800",
                complaint: "bg-red-500/20 text-red-800",
                cancelled: "bg-slate-400/20 text-slate-700",
              }[s] || "bg-gray-200 text-gray-700");
              // Display labels — user asked to surface "DAC WRONG" for
              // complaint rows and "CANCELLED" for cancelled ones so
              // the dashboard reads exactly like the complaint sheet.
              const statusLabel = (s) => ({
                pending: "PENDING",
                delivered: "DELIVERED",
                resent: "RESENT",
                complaint: "DAC WRONG",
                cancelled: "CANCELLED",
              }[s] || (s || "").toUpperCase());
              return (
                <div
                  className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center md:p-4"
                  onClick={() => setShowDeliveryModal(false)}
                  data-testid="delivery-modal"
                >
                  <div
                    className="bg-white md:rounded-lg shadow-2xl w-full h-full md:h-auto max-w-full md:max-w-3xl md:max-h-[90vh] flex flex-col"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <div className="px-3 md:px-5 py-3 md:py-4 border-b border-[#DCDCD3] flex items-center gap-2 md:gap-3 flex-wrap md:flex-nowrap">
                      <div className="flex-1 min-w-[150px] md:min-w-0">
                        <h2 className="text-sm md:text-lg font-semibold text-[#1C1C19] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                          <span className="md:hidden">{activeGroup?.name || "Delivery"}</span>
                          <span className="hidden md:inline">{activeGroup?.name || "Delivery"} · Delivery Dashboard</span>
                        </h2>
                        <div className="text-[10px] md:text-[11px] font-mono text-[#6B6B61] mt-0.5 whitespace-nowrap overflow-x-auto scrollbar-hide">
                          Sent {deliveryStatus.totals.sent} · Delivered{" "}
                          <span className="text-emerald-700 font-semibold">{deliveryStatus.totals.delivered}</span> ·{" "}
                          Pending{" "}
                          <span className="text-[#8A6046] font-semibold">{deliveryStatus.totals.pending}</span>
                          {deliveryStatus.totals.complaint > 0 && (
                            <>
                              {" "}· Complaint{" "}
                              <span className="text-red-700 font-semibold">{deliveryStatus.totals.complaint}</span>
                            </>
                          )}
                          {(deliveryStatus.totals.cancelled || 0) > 0 && (
                            <>
                              {" "}· Cancelled{" "}
                              <span className="text-slate-700 font-semibold">{deliveryStatus.totals.cancelled}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {dateCounts.pending > 0 && (
                        <button
                          onClick={async () => {
                            const n = dateCounts.pending;
                            // When viewing "all" we use the server's bulk
                            // mark-all endpoint (same as before). For a
                            // date-scoped view we flip each date-filtered
                            // pending row individually so purani stuck
                            // pending untouched rahe — admin ne spec kiya.
                            if (deliveryDateFilter === "all") {
                              if (!window.confirm(
                                `Mark all ${n} pending deliver${n === 1 ? "y" : "ies"} as DONE?\n\n` +
                                "Ye action sabhi pending entries ko delivered mark kar dega. " +
                                "Baad me har row ko individually change kar sakte ho."
                              )) return;
                              try {
                                const { data } = await api.post(
                                  `/groups/${activeGroupId}/lpg/mark-all-pending-done`
                                );
                                toast.success(
                                  `${data.entries_marked_delivered} deliveries marked as done ✓`
                                );
                                const { data: fresh } = await api.get(
                                  `/groups/${activeGroupId}/delivery-status`
                                );
                                setDeliveryStatus(fresh);
                                refreshUrgentSummary();
                                setMessages((prev) =>
                                  prev.map((m) => {
                                    if (!m.lpg_entries || !m.lpg_entries.length) return m;
                                    const next = m.lpg_entries.map((e) =>
                                      e.status === "correct" && !(e.marked_by || "").trim()
                                        ? { ...e, marked_by: user?.display_name || user?.username || "me", marked_at: new Date().toISOString() }
                                        : e
                                    );
                                    return { ...m, lpg_entries: next };
                                  })
                                );
                              } catch (e) {
                                setError(formatApiError(e));
                              }
                            } else {
                              // Date-scoped mark-all — iterate only visible
                              // pending rows (today / yesterday / etc.).
                              const dateLabel = {
                                today: "today's",
                                yesterday: "yesterday's",
                                last7: "last 7 days'",
                                custom: "selected range's",
                              }[deliveryDateFilter] || "visible";
                              if (!window.confirm(
                                `Mark all ${n} ${dateLabel} pending deliver${n === 1 ? "y" : "ies"} as DONE?\n\n` +
                                "Purani stuck pending (jo iss date range ke bahar hain) ko touch nahi kiya jaayega."
                              )) return;
                              try {
                                // Sequential — most groups me N chhota rahega
                                // (dekha 45 max). Parallel karne pe optimistic
                                // state race-condition hoti hai.
                                for (const row of dateFilteredPending) {
                                  await setEntryStatus(row, "correct");
                                }
                                toast.success(`${n} ${dateLabel} deliveries marked as done ✓`);
                                refreshUrgentSummary();
                              } catch (e) {
                                setError(formatApiError(e));
                              }
                            }
                          }}
                          data-testid="delivery-modal-mark-all-done"
                          title={deliveryDateFilter === "all"
                            ? "Mark ALL pending deliveries as done in one tap"
                            : `Mark all ${deliveryDateFilter} pending deliveries as done`}
                          className="px-2.5 py-1.5 text-[11px] font-mono font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5"
                        >
                          <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                          <span className="hidden md:inline">Mark All Done · {dateCounts.pending}</span>
                          <span className="md:hidden">Mark All · {dateCounts.pending}</span>
                        </button>
                      )}
                      <button
                        onClick={async (ev) => {
                          const rows = [
                            ["LPG Consumer ID", "DAC Code", "Status"],
                            ...filtered.map((r) => [
                              r.lpg_id,
                              r.dac_code || "",
                              statusLabel(r.status),
                            ]),
                          ];
                          const btn = ev.currentTarget;
                          const ok = await copyForSpreadsheet(rows);
                          btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                          setTimeout(() => {
                            btn.removeAttribute("data-copied");
                            btn.removeAttribute("data-copy-failed");
                          }, 1800);
                        }}
                        disabled={filtered.length === 0}
                        data-testid="delivery-modal-copy-list"
                        title="Copy the current list (LPG ID · DAC · Status) — paste into Excel / Complaint Sheet"
                        className="px-2.5 py-1.5 text-[11px] font-mono font-semibold rounded-md border border-[#DCDCD3] bg-white hover:bg-[#F7F6EE] text-[#385C4B] flex items-center gap-1.5 disabled:opacity-40 data-[copied=1]:bg-emerald-100 data-[copied=1]:border-emerald-400 data-[copied=1]:text-emerald-800 data-[copy-failed=1]:bg-red-100"
                      >
                        <Copy className="w-3 h-3" strokeWidth={2} />
                        <span>Copy · {filtered.length}</span>
                      </button>
                      {/* Iter73 · Split-button Excel dropdown: Today (default) / Range / Whole */}
                      {(() => {
                        const runExcel = async (mode, btn) => {
                          if (!btn) return;
                          btn.setAttribute("data-loading", "1");
                          try {
                            let url = `/lpg/full-day-excel?group_id=${encodeURIComponent(activeGroupId)}`;
                            if (mode === "today") {
                              const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                              url += `&date=${today}`;
                            } else if (mode === "range") {
                              // Reuse Iter70 date filter state — if user has
                              // Custom picker set, use those; otherwise derive
                              // from the active date preset (today/yesterday/last7).
                              let from = deliveryDateCustom.from;
                              let to = deliveryDateCustom.to;
                              if (deliveryDateFilter === "today") {
                                from = to = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                              } else if (deliveryDateFilter === "yesterday") {
                                const d = new Date();
                                d.setUTCDate(d.getUTCDate() - 1);
                                from = to = d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                              } else if (deliveryDateFilter === "last7") {
                                to = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                                const d = new Date();
                                d.setUTCDate(d.getUTCDate() - 6);
                                from = d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                              }
                              if (!from || !to) {
                                toast.error("Pehle date range set karo (Custom / Today / Last 7d)");
                                btn.removeAttribute("data-loading");
                                return;
                              }
                              url += `&date_from=${from}&date_to=${to}`;
                            } else if (mode === "whole") {
                              if (!window.confirm(
                                "Kya aap is agency ke SABHI entries export karna chahte hain?\n\n" +
                                "Bade groups ke liye 10-30 second lag sakte hain (max 50,000 rows)."
                              )) {
                                btn.removeAttribute("data-loading");
                                return;
                              }
                              url += `&all=1`;
                            }
                            const resp = await api.get(url, { responseType: "blob" });
                            const cd = resp.headers?.["content-disposition"] || "";
                            const m = /filename="?([^";]+)"?/.exec(cd);
                            const fname = m ? m[1] : `full_day_${mode}.xlsx`;
                            const objectUrl = URL.createObjectURL(resp.data);
                            const a = document.createElement("a");
                            a.href = objectUrl;
                            a.download = fname;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                            btn.setAttribute("data-copied", "1");
                            toast.success(`Downloaded ${fname}`);
                          } catch (e) {
                            btn.setAttribute("data-copy-failed", "1");
                            toast.error(formatApiError(e) || "Excel download failed");
                          } finally {
                            setTimeout(() => {
                              btn.removeAttribute("data-loading");
                              btn.removeAttribute("data-copied");
                              btn.removeAttribute("data-copy-failed");
                            }, 1800);
                            setShowDeliveryExcelMenu(false);
                          }
                        };
                        return (
                          <div className="relative inline-flex" data-testid="delivery-modal-excel-split">
                            <button
                              onClick={(ev) => runExcel("today", ev.currentTarget)}
                              data-testid="delivery-modal-full-day-excel"
                              title="Download TODAY's combined Excel"
                              className="px-2.5 py-1.5 text-[11px] font-mono font-semibold rounded-l-md bg-[#385C4B] text-white hover:bg-[#2c4a3c] flex items-center gap-1.5 data-[loading=1]:opacity-60 data-[copied=1]:bg-emerald-600 data-[copy-failed=1]:bg-red-600 border-r border-[#2c4a3c]/40"
                            >
                              <Download className="w-3 h-3" strokeWidth={2.5} />
                              <span>Full Day Excel</span>
                            </button>
                            <button
                              onClick={() => setShowDeliveryExcelMenu((s) => !s)}
                              data-testid="delivery-modal-excel-menu"
                              title="More export options"
                              className="px-1.5 py-1.5 rounded-r-md bg-[#385C4B] text-white hover:bg-[#2c4a3c] flex items-center"
                              aria-expanded={showDeliveryExcelMenu}
                            >
                              <ChevronDown className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                            {showDeliveryExcelMenu && (
                              <>
                                {/* Click-outside catcher */}
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setShowDeliveryExcelMenu(false)}
                                />
                                <div
                                  className="absolute right-0 top-full mt-1 w-64 bg-white border border-[#DCDCD3] rounded-md shadow-lg z-50 py-1"
                                  data-testid="delivery-modal-excel-menu-popup"
                                >
                                  <button
                                    onClick={(ev) => runExcel("today", ev.currentTarget)}
                                    data-testid="delivery-modal-excel-today"
                                    className="w-full px-3 py-2 text-left text-xs hover:bg-[#F5F5F0] flex items-start gap-2"
                                  >
                                    <Download className="w-3.5 h-3.5 mt-0.5 text-[#385C4B] flex-shrink-0" strokeWidth={2} />
                                    <div>
                                      <div className="font-semibold text-[#1C1C19]">Today only</div>
                                      <div className="text-[10px] text-[#6B6B61]">Aaj ki (IST) sabhi entries</div>
                                    </div>
                                  </button>
                                  <button
                                    onClick={(ev) => runExcel("range", ev.currentTarget)}
                                    data-testid="delivery-modal-excel-range"
                                    className="w-full px-3 py-2 text-left text-xs hover:bg-[#F5F5F0] flex items-start gap-2"
                                  >
                                    <Download className="w-3.5 h-3.5 mt-0.5 text-[#8A6046] flex-shrink-0" strokeWidth={2} />
                                    <div>
                                      <div className="font-semibold text-[#1C1C19]">Selected date range</div>
                                      <div className="text-[10px] text-[#6B6B61]">
                                        {deliveryDateFilter === "custom" && (deliveryDateCustom.from || deliveryDateCustom.to)
                                          ? `${deliveryDateCustom.from || "—"} → ${deliveryDateCustom.to || "—"}`
                                          : deliveryDateFilter === "today" ? "Aaj (IST)"
                                          : deliveryDateFilter === "yesterday" ? "Kal (IST)"
                                          : deliveryDateFilter === "last7" ? "Pichhle 7 din"
                                          : "Upar 'Date' pill se range choose karo pehle"}
                                      </div>
                                    </div>
                                  </button>
                                  <button
                                    onClick={(ev) => runExcel("whole", ev.currentTarget)}
                                    data-testid="delivery-modal-excel-whole"
                                    className="w-full px-3 py-2 text-left text-xs hover:bg-[#F5F5F0] flex items-start gap-2 border-t border-[#DCDCD3]/60 mt-0.5 pt-2"
                                  >
                                    <Download className="w-3.5 h-3.5 mt-0.5 text-[#1C1C19] flex-shrink-0" strokeWidth={2} />
                                    <div>
                                      <div className="font-semibold text-[#1C1C19]">Whole (all entries)</div>
                                      <div className="text-[10px] text-[#6B6B61]">Poori history · max 50,000 rows</div>
                                    </div>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()}
                      <button
                        onClick={() => setHideLpgIds((v) => !v)}
                        data-testid="delivery-modal-toggle-lpg-ids"
                        title={hideLpgIds ? "LPG IDs dikhao" : "LPG IDs chhupao (copy / Excel me poori ID hi jayegi)"}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border transition-colors ${hideLpgIds ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white text-[#385C4B] border-[#DCDCD3] hover:border-[#385C4B]"}`}
                      >
                        {hideLpgIds ? <EyeOff className="w-3.5 h-3.5" strokeWidth={2} /> : <Eye className="w-3.5 h-3.5" strokeWidth={2} />}
                        {hideLpgIds ? "Show IDs" : "Hide IDs"}
                      </button>
                      {(() => {
                        const n = (activeGroup?.name || "").toUpperCase();
                        if (!(n.includes("INDANE") || n.includes("IOCL") || n.includes("INDIAN OIL"))) return null;
                        const on = deliveryModalFilter === "delivery_login";
                        return (
                          <button
                            onClick={() => setDeliveryModalFilter(on ? "pending" : "delivery_login")}
                            data-testid="delivery-modal-filter-delivery_login"
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border transition-colors ${on ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white text-[#385C4B] border-[#DCDCD3] hover:border-[#385C4B]"}`}
                          >
                            🔐 Login
                          </button>
                        );
                      })()}
                      <button
                        onClick={() => setShowDeliveryModal(false)}
                        className="p-1.5 hover:bg-[#F7F6EE] rounded-md"
                        title="Close"
                        data-testid="delivery-modal-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-3 md:px-5 pt-3 pb-2 flex items-center gap-1.5 md:gap-2 border-b border-[#DCDCD3] overflow-x-auto scrollbar-hide md:overflow-visible md:flex-wrap">
                      {(() => {
                        const baseTabs = [
                          ["pending", "Pending", dateCounts.pending],
                          ["all", "All", dateCounts.sent],
                          ["delivered", "Delivered", dateCounts.delivered],
                          ["complaint", "Complaint", dateCounts.complaint],
                          ["cancelled", "Cancelled", dateCounts.cancelled || 0],
                        ];
                        return baseTabs.map(([key, label, count]) => (
                          <button
                            key={key}
                            onClick={() => setDeliveryModalFilter(key)}
                            data-testid={`delivery-modal-filter-${key}`}
                            className={`px-3 py-1.5 text-xs font-mono rounded-md transition-colors ${
                              deliveryModalFilter === key
                                ? "bg-[#385C4B] text-white"
                                : "bg-[#F7F6EE] text-[#1C1C19] hover:bg-[#EFEEE6]"
                            }`}
                          >
                            {label}
                            {count !== null && (
                              <span className="font-semibold ml-1 tabular-nums">
                                {count}
                              </span>
                            )}
                          </button>
                        ));
                      })()}
                    </div>
                    {/* Sep 2026: Date filter pills — layered on top of status filter */}
                    {deliveryModalFilter !== "delivery_login" && (
                    <div className="px-3 md:px-5 pt-2 pb-2 flex items-center gap-1.5 md:gap-2 flex-nowrap md:flex-wrap overflow-x-auto scrollbar-hide md:overflow-visible border-b border-[#DCDCD3] bg-[#F7F6EE]/40" data-testid="delivery-modal-date-filters">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61] mr-1">Date:</span>
                      {[
                        ["all", "All Time"],
                        ["today", "Today"],
                        ["yesterday", "Yesterday"],
                        ["last7", "Last 7d"],
                        ["custom", "Custom"],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setDeliveryDateFilter(key)}
                          data-testid={`delivery-modal-date-${key}`}
                          className={`px-2.5 py-1 text-[11px] font-mono rounded-md transition-colors ${
                            deliveryDateFilter === key
                              ? "bg-[#8A6046] text-white"
                              : "bg-white text-[#1C1C19] border border-[#DCDCD3] hover:bg-[#EFEEE6]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                      {deliveryDateFilter === "custom" && (
                        <div className="flex items-center gap-1.5 ml-2" data-testid="delivery-modal-date-custom-picker">
                          <input
                            type="date"
                            value={deliveryDateCustom.from}
                            onChange={(e) => setDeliveryDateCustom({ ...deliveryDateCustom, from: e.target.value })}
                            data-testid="delivery-modal-date-from"
                            className="px-2 py-1 text-[11px] font-mono border border-[#DCDCD3] rounded-md bg-white outline-none focus:ring-1 focus:ring-[#8A6046]/40"
                          />
                          <span className="text-[10px] text-[#6B6B61]">to</span>
                          <input
                            type="date"
                            value={deliveryDateCustom.to}
                            onChange={(e) => setDeliveryDateCustom({ ...deliveryDateCustom, to: e.target.value })}
                            data-testid="delivery-modal-date-to"
                            className="px-2 py-1 text-[11px] font-mono border border-[#DCDCD3] rounded-md bg-white outline-none focus:ring-1 focus:ring-[#8A6046]/40"
                          />
                        </div>
                      )}
                      <span className="ml-auto text-[11px] font-mono text-[#6B6B61] tabular-nums" data-testid="delivery-modal-filtered-count">
                        Showing <span className="text-[#1C1C19] font-semibold">{filtered.length}</span> row{filtered.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    )}
                    {/* Oct 2026: date-filter hint + celebration banner. */}
                    {deliveryModalFilter !== "delivery_login" && deliveryDateFilter !== "all" && (
                      <div className="px-3 md:px-5 py-1.5 text-[10px] font-mono text-[#8A6046] bg-[#F7F6EE]/60 border-b border-[#DCDCD3]" data-testid="delivery-modal-date-hint">
                        📅 Date-filtered — showing {
                          deliveryDateFilter === "today" ? "today only"
                          : deliveryDateFilter === "yesterday" ? "yesterday only"
                          : deliveryDateFilter === "last7" ? "last 7 days"
                          : deliveryDateFilter === "custom" ? `${deliveryDateCustom.from || "—"} → ${deliveryDateCustom.to || "—"}`
                          : deliveryDateFilter
                        }. Counts above reflect this range · "All Time" chip ke global totals ke liye.
                      </div>
                    )}
                    {deliveryModalFilter !== "delivery_login"
                      && deliveryDateFilter === "today"
                      && dateCounts.sent > 0
                      && dateCounts.pending === 0
                      && !deliveryDoneBannerDismissed && (
                      <div
                        className="mx-3 md:mx-5 mt-2 mb-1 px-3 py-2.5 rounded-md border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-100/60 to-emerald-50 flex items-center gap-2 shadow-sm animate-in fade-in zoom-in-95 duration-300"
                        data-testid="delivery-modal-today-done-banner"
                      >
                        <span className="text-xl">🎉</span>
                        <div className="flex-1 text-[11px] md:text-xs text-emerald-900 font-semibold">
                          Shabash! Aaj ki saari {dateCounts.sent} deliver
                          {dateCounts.sent === 1 ? "y" : "ies"} complete ho gayi hain · 100%
                          {dateCounts.complaint > 0 && (
                            <span className="ml-1 font-normal text-emerald-800">
                              ({dateCounts.complaint} complaint pending)
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setDeliveryDoneBannerDismissed(true)}
                          className="text-emerald-700 hover:text-emerald-900 p-1 rounded"
                          title="Dismiss"
                          data-testid="delivery-modal-today-done-banner-dismiss"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {deliveryModalFilter === "delivery_login" ? (
                      <div className="flex-1 overflow-y-auto">
                        <DeliveryLoginTab
                          groupId={activeGroupId}
                          agencyName={activeGroup?.name || "Agency"}
                        />
                      </div>
                    ) : (
                    <div className="flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="delivery-modal-rows">
                      {filtered.length === 0 && (
                        <div className="text-center text-sm text-[#6B6B61] py-8 font-mono">
                          {deliveryDateFilter !== "all"
                            ? "Selected date range me is filter ka koi entry nahi mila"
                            : deliveryModalFilter === "pending"
                            ? "Sab entries acknowledge ho gayi ✓ — koi pending nahi"
                            : "No entries match this filter"}
                        </div>
                      )}
                      {filtered.map((row) => (
                        <div
                          key={row.lpg_id}
                          className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2.5 rounded-md border border-[#EFEEE6] hover:border-[#DCDCD3] bg-white"
                          data-testid="delivery-modal-row"
                        >
                          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={async (ev) => {
                                ev.stopPropagation();
                                const btn = ev.currentTarget;
                                const ok = await copyForSpreadsheet([[row.lpg_id]]);
                                btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                                setTimeout(() => {
                                  btn.removeAttribute("data-copied");
                                  btn.removeAttribute("data-copy-failed");
                                }, 1400);
                              }}
                              data-testid="delivery-modal-copy-lpg"
                              title="Copy Consumer / LPG ID"
                              className="group/copy inline-flex items-center gap-1 text-sm font-mono font-semibold text-[#1C1C19] tabular-nums px-1.5 py-0.5 rounded hover:bg-[#385C4B]/10 hover:text-[#385C4B] transition-colors data-[copied=1]:bg-emerald-100 data-[copied=1]:text-emerald-800 data-[copy-failed=1]:bg-red-100"
                            >
                              <span data-testid="delivery-modal-lpg-text">
                                {hideLpgIds ? "•".repeat((row.lpg_id || "").length || 10) : row.lpg_id}
                              </span>
                              <Copy className="w-3 h-3 opacity-40 group-hover/copy:opacity-100 transition-opacity" strokeWidth={2} />
                            </button>
                            {/* Feb 2026 admin ask: show Mob between LPG
                                and DAC so pending rows read
                                `LPG · Mob · DAC · STATUS`. Falls back to
                                the LPG ID when it's a 10-digit
                                Mobile+Code entry (`row.lpg_id == mobile`).
                                For Indane groups the chip is ALWAYS
                                rendered with `Mob —` when no mobile is
                                captured — keeps the 3-field format
                                uniform for legacy entries that were
                                dispatched before Feb 2026 mobile
                                extraction. Non-Indane groups keep the
                                opt-in behaviour (silent-hide when no
                                mobile), matching the chat-card rule. */}
                            {(() => {
                              const activeGroupName = groups.find(g => g.id === activeGroupId)?.name || "";
                              const isIndaneGroup = /INDANE/i.test(activeGroupName);
                              const explicitMob = row.mobile && row.mobile !== row.lpg_id ? row.mobile : "";
                              const lpgIsMob = (row.lpg_id || "").length === 10 && /^\d+$/.test(row.lpg_id) ? row.lpg_id : "";
                              const mob = explicitMob || lpgIsMob;
                              if (!mob && !isIndaneGroup) return null;
                              const display = mob || "—";
                              return (
                                <button
                                  type="button"
                                  onClick={async (ev) => {
                                    ev.stopPropagation();
                                    if (!mob) return;   // nothing to copy for `—` placeholder
                                    const btn = ev.currentTarget;
                                    const ok = await copyForSpreadsheet([[mob]]);
                                    btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                                    setTimeout(() => {
                                      btn.removeAttribute("data-copied");
                                      btn.removeAttribute("data-copy-failed");
                                    }, 1400);
                                  }}
                                  disabled={!mob}
                                  data-testid="delivery-modal-copy-mobile"
                                  title={mob ? "Copy customer mobile" : "Mobile not captured for this legacy entry"}
                                  className="group/copy inline-flex items-center gap-1 text-xs font-mono text-[#8A6046] px-1.5 py-0.5 rounded hover:bg-[#8A6046]/10 hover:text-[#8A6046] transition-colors data-[copied=1]:bg-emerald-100 data-[copied=1]:text-emerald-800 data-[copy-failed=1]:bg-red-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#8A6046] disabled:cursor-not-allowed"
                                >
                                  <span>Mob {display}</span>
                                  {mob && (
                                    <Copy className="w-3 h-3 opacity-40 group-hover/copy:opacity-100 transition-opacity" strokeWidth={2} />
                                  )}
                                </button>
                              );
                            })()}
                            <button
                              type="button"
                              onClick={async (ev) => {
                                ev.stopPropagation();
                                if (!row.dac_code) return;
                                const btn = ev.currentTarget;
                                const ok = await copyForSpreadsheet([[row.dac_code]]);
                                btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                                setTimeout(() => {
                                  btn.removeAttribute("data-copied");
                                  btn.removeAttribute("data-copy-failed");
                                }, 1400);
                              }}
                              disabled={!row.dac_code}
                              data-testid="delivery-modal-copy-dac"
                              title="Copy DAC code"
                              className="group/copy inline-flex items-center gap-1 text-xs font-mono text-[#6B6B61] px-1.5 py-0.5 rounded hover:bg-[#385C4B]/10 hover:text-[#385C4B] transition-colors data-[copied=1]:bg-emerald-100 data-[copied=1]:text-emerald-800 data-[copy-failed=1]:bg-red-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#6B6B61] disabled:cursor-not-allowed"
                            >
                              <span>DAC {row.dac_code || "—"}</span>
                              {row.dac_code && (
                                <Copy className="w-3 h-3 opacity-40 group-hover/copy:opacity-100 transition-opacity" strokeWidth={2} />
                              )}
                            </button>
                            <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${badgeCls(row.status)}`}>
                              {statusLabel(row.status)}
                            </span>
                            {/* Sep 2026 · Timestamp subline — Dispatched + Delivered.
                                Uses flex-basis:100% to force a new line on wrap while
                                remaining inside the parent flex-wrap container.
                                Urgent 24h+ pending rows get red highlight. */}
                            {(row.created_at || row.marked_at) && (
                              <div
                                className="w-full flex items-center gap-3 flex-wrap text-[10px] font-mono text-[#6B6B61] pt-0.5"
                                data-testid="delivery-modal-row-timestamps"
                              >
                                {row.created_at && (
                                  <span
                                    className={`inline-flex items-center gap-1 ${row.urgent ? "text-[#C8553D] font-semibold" : ""}`}
                                    title={istPretty(row.created_at)}
                                    data-testid="delivery-modal-row-dispatched"
                                  >
                                    {row.urgent && (
                                      <AlertCircle className="w-3 h-3" strokeWidth={2.5} />
                                    )}
                                    <Clock className="w-3 h-3 opacity-70" strokeWidth={2} />
                                    <span>
                                      Dispatched {istPretty(row.created_at)}
                                      <span className="opacity-70"> · {relative(row.created_at)}</span>
                                    </span>
                                  </span>
                                )}
                                {row.marked_at &&
                                  (row.status === "delivered" || row.status === "resent") && (
                                    <span
                                      className="inline-flex items-center gap-1 text-emerald-700"
                                      title={istPretty(row.marked_at)}
                                      data-testid="delivery-modal-row-delivered-at"
                                    >
                                      <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                                      <span>
                                        Delivered {istPretty(row.marked_at)}
                                        <span className="opacity-70"> · {relative(row.marked_at)}</span>
                                      </span>
                                    </span>
                                  )}
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-1 flex-shrink-0 w-full sm:w-auto sm:flex sm:items-center sm:ml-auto sm:self-auto">
                            <button
                              onClick={() => setEntryStatus(row, "correct")}
                              disabled={row.status === "delivered"}
                              data-testid="delivery-modal-tick-done"
                              title={row.status === "delivered" ? "Already delivered" : "Mark as delivered"}
                              className={`px-2.5 py-1 text-[11px] font-mono font-semibold rounded transition-colors flex items-center justify-center gap-1 whitespace-nowrap ${
                                row.status === "delivered"
                                  ? "opacity-40 cursor-not-allowed text-emerald-700 bg-emerald-100"
                                  : "text-emerald-800 bg-emerald-100 hover:bg-emerald-200"
                              }`}
                            >
                              <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> Done
                            </button>
                            <button
                              onClick={() => {
                                if (row.status === "complaint") return;
                                if (!window.confirm(
                                  `Mark ${hideLpgIds ? "•".repeat((row.lpg_id || "").length) : row.lpg_id} · DAC ${row.dac_code} as DAC INCORRECT?\n\nA complaint will be raised to the team.`
                                )) return;
                                setEntryStatus(row, "wrong");
                              }}
                              disabled={row.status === "complaint"}
                              data-testid="delivery-modal-tick-wrong"
                              title={row.status === "complaint" ? "Already raised" : "DAC incorrect — raise complaint"}
                              className={`px-2.5 py-1 text-[11px] font-mono font-semibold rounded transition-colors flex items-center justify-center gap-1 whitespace-nowrap ${
                                row.status === "complaint"
                                  ? "opacity-40 cursor-not-allowed text-red-700 bg-red-100"
                                  : "text-red-800 bg-red-100 hover:bg-red-200"
                              }`}
                            >
                              <XCircle className="w-3 h-3" strokeWidth={2.5} /> <span className="hidden sm:inline">DAC Incorrect</span><span className="sm:hidden">DAC ✗</span>
                            </button>
                            <button
                              onClick={() => {
                                if (row.status === "cancelled") return;
                                if (!window.confirm(
                                  `Mark ${hideLpgIds ? "•".repeat((row.lpg_id || "").length) : row.lpg_id} · DAC ${row.dac_code} as BOOKING CANCELLED?\n\nThe delivery will move to the Cancelled bucket.`
                                )) return;
                                setEntryStatus(row, "cancelled");
                              }}
                              disabled={row.status === "cancelled"}
                              data-testid="delivery-modal-tick-cancelled"
                              title={row.status === "cancelled" ? "Already cancelled" : "Booking cancelled by the customer / agency"}
                              className={`px-2.5 py-1 text-[11px] font-mono font-semibold rounded transition-colors flex items-center justify-center gap-1 whitespace-nowrap ${
                                row.status === "cancelled"
                                  ? "opacity-40 cursor-not-allowed text-slate-700 bg-slate-200"
                                  : "text-slate-800 bg-slate-200 hover:bg-slate-300"
                              }`}
                            >
                              <XCircle className="w-3 h-3" strokeWidth={2.5} /> Cancelled
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                    <div className="px-5 py-2.5 border-t border-[#DCDCD3] bg-[#F7F6EE] text-[10px] font-mono text-[#6B6B61] text-center">
                      Har tick real-time Google Sheet me sync ho jata hai · chat me bhi record rahega
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Per-Group Complaint Box — full-transparency modal.
                Requested by user (Jul 2026): "COMPLAIN BOX BANA DO
                GROUP ICON ME HI...AGENCY COMPLAIN KARE OR USKA
                RESULTION DE TO SAB WAHI EK SATH DIKHE". Agency + admin
                both see the same items with resolution info. */}
            {/* Customer Details modal (Feb 2026): read-only aggregate of
                every LPG entry in the group's loaded messages window.
                Shows LPG ID / Mobile / DAC / date / uploader with a
                search box. Ledger untouched — this is display-only. */}
            {showCustomerDetails && (() => {
              const allEntries = [];
              (messages || []).forEach((m) => {
                if (m.deleted) return;
                (m.lpg_entries || []).forEach((e, idx) => {
                  const lid = String(e.lpg_id || "");
                  // Treat 10-digit LPG IDs (Mobile+Code format) as
                  // both — mobile column shows the same digits.
                  const mobile = lid.length === 10 ? lid : "";
                  allEntries.push({
                    lpg_id: lid,
                    mobile,
                    dac: e.dac_code || "",
                    status: e.status || "correct",
                    date: (m.created_at || "").slice(0, 10),
                    uploaded_by: m.uploaded_by || e.uploaded_by || "",
                    msg_id: m.id,
                    idx,
                  });
                });
              });
              const q = customerDetailsSearch.trim().toLowerCase();
              const filtered = q
                ? allEntries.filter(
                    (r) =>
                      r.lpg_id.toLowerCase().includes(q) ||
                      r.mobile.toLowerCase().includes(q) ||
                      r.dac.toLowerCase().includes(q)
                  )
                : allEntries;
              return (
                <div
                  className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) setShowCustomerDetails(false);
                  }}
                  data-testid="chat-customer-details-modal"
                >
                  <div
                    className="bg-white w-full max-w-3xl rounded-lg shadow-2xl border border-[#DCDCD3] max-h-[85vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 py-3 border-b border-[#DCDCD3] flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Users className="w-4 h-4 text-[#385C4B]" />
                        Customer Details · {activeGroup?.name || ""} ({filtered.length} of {allEntries.length})
                      </div>
                      <button
                        onClick={() => setShowCustomerDetails(false)}
                        className="p-1 hover:bg-[#EDEDE5] rounded"
                        data-testid="chat-customer-details-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-4 py-2 border-b border-[#DCDCD3]">
                      <input
                        type="text"
                        value={customerDetailsSearch}
                        onChange={(e) => setCustomerDetailsSearch(e.target.value)}
                        placeholder="Search LPG ID / Mobile / DAC…"
                        className="w-full px-3 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono"
                        data-testid="chat-customer-details-search"
                      />
                    </div>
                    <div className="overflow-y-auto flex-1">
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5EE] sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2">LPG ID</th>
                            <th className="text-left px-3 py-2">Mobile</th>
                            <th className="text-left px-3 py-2">DAC</th>
                            <th className="text-left px-3 py-2">Date</th>
                            <th className="text-left px-3 py-2">By</th>
                            <th className="text-left px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((r, i) => (
                            <tr
                              key={`${r.msg_id}-${r.idx}`}
                              className="border-t border-[#EDEDE5] hover:bg-[#F5F5EE]/50"
                              data-testid={`chat-customer-details-row-${i}`}
                            >
                              <td className="px-3 py-1.5 font-mono">{r.lpg_id}</td>
                              <td className="px-3 py-1.5 font-mono text-[#6B6B61]">{r.mobile || "—"}</td>
                              <td className="px-3 py-1.5 font-mono font-semibold text-[#385C4B]">{r.dac}</td>
                              <td className="px-3 py-1.5 text-[#6B6B61]">{r.date}</td>
                              <td className="px-3 py-1.5 text-[#6B6B61]">{r.uploaded_by || "—"}</td>
                              <td className="px-3 py-1.5">
                                <span className={
                                  r.status === "wrong"
                                    ? "text-red-600 font-semibold"
                                    : r.status === "resent"
                                      ? "text-amber-700"
                                      : "text-emerald-700"
                                }>
                                  {r.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {filtered.length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-3 py-6 text-center text-[#6B6B61] italic">
                                {q ? "No matching customers" : "No LPG entries yet in this group"}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })()}

            {showGroupComplaints && (() => {
              const items = groupComplaints?.items || [];
              const gtotals = groupComplaints?.totals || { open: 0, resolved: 0, cancelled: 0, total: 0 };
              const gFiltered = items.filter((r) => {
                if (groupComplaintFilter === "all") return true;
                return r.state === groupComplaintFilter;
              });
              const cBadge = (state) => ({
                open: "bg-[#C8553D]/15 text-[#C8553D]",
                resolved: "bg-emerald-500/15 text-emerald-800",
                cancelled: "bg-slate-400/20 text-slate-700",
              }[state] || "bg-gray-200 text-gray-700");
              const cKindLabel = (r) => {
                if (r.kind === "lpg_wrong") return r.state === "resolved" ? "DAC WRONG · RESOLVED" : "DAC WRONG";
                if (r.kind === "lpg_cancelled") return "CANCELLED";
                if (r.kind === "text") return r.state === "resolved" ? "COMPLAINT · RESOLVED" : "COMPLAINT";
                return (r.kind || "").toUpperCase();
              };
              const canResolve = user?.role === "admin" || user?.is_owner;
              const openResolveDialog = (row) => {
                setResolveTarget({
                  message_id: row.message_id,
                  entry_index: row.entry_index,
                  lpg_id: row.lpg_id,
                  dac_code: row.dac_code,
                  kind: row.kind,
                  text: row.text,
                });
                setResolveChoice("new_dac");
                setResolveNewDac("");
              };
              return (
                <div
                  className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                  onClick={() => setShowGroupComplaints(false)}
                  data-testid="group-complaint-modal"
                >
                  <div
                    className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <div className="px-5 py-4 border-b border-[#DCDCD3] flex items-center gap-3">
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold text-[#1C1C19] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                          <Inbox className="w-4 h-4 text-[#C8553D]" strokeWidth={2} />
                          {activeGroup?.name || "Group"} · Complaint Box
                        </h2>
                        <div className="text-[11px] font-mono text-[#6B6B61] mt-0.5">
                          Total {gtotals.total} · Open{" "}
                          <span className="text-[#C8553D] font-semibold">{gtotals.open}</span> ·{" "}
                          Resolved{" "}
                          <span className="text-emerald-700 font-semibold">{gtotals.resolved}</span>
                          {gtotals.cancelled > 0 && (
                            <>
                              {" "}· Cancelled{" "}
                              <span className="text-slate-700 font-semibold">{gtotals.cancelled}</span>
                            </>
                          )}
                          <span className="ml-2 text-[#385C4B]">· Agency + admin dono ko same dikhta hai</span>
                        </div>
                      </div>
                      <button
                        onClick={() => fetchGroupComplaints()}
                        title="Refresh"
                        data-testid="group-complaint-refresh"
                        className="p-1.5 hover:bg-[#F7F6EE] rounded-md text-[#6B6B61] hover:text-[#385C4B]"
                      >
                        <RefreshCw className={`w-4 h-4 ${groupComplaintsLoading ? "animate-spin" : ""}`} strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => setShowGroupComplaints(false)}
                        className="p-1.5 hover:bg-[#F7F6EE] rounded-md"
                        title="Close"
                        data-testid="group-complaint-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-5 pt-3 pb-2 flex items-center gap-2 border-b border-[#DCDCD3] flex-wrap">
                      {[
                        ["all", "All", gtotals.total],
                        ["open", "Open", gtotals.open],
                        ["resolved", "Resolved", gtotals.resolved],
                        ["cancelled", "Cancelled", gtotals.cancelled],
                      ].map(([key, label, count]) => (
                        <button
                          key={key}
                          onClick={() => setGroupComplaintFilter(key)}
                          data-testid={`group-complaint-filter-${key}`}
                          className={`px-3 py-1.5 text-xs font-mono rounded-md transition-colors ${
                            groupComplaintFilter === key
                              ? "bg-[#385C4B] text-white"
                              : "bg-[#F7F6EE] text-[#1C1C19] hover:bg-[#EFEEE6]"
                          }`}
                        >
                          {label} <span className="font-semibold ml-1 tabular-nums">{count}</span>
                        </button>
                      ))}
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="group-complaint-rows">
                      {groupComplaintsLoading && !groupComplaints && (
                        <div className="text-center text-sm text-[#6B6B61] py-8 font-mono">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                          Loading complaints…
                        </div>
                      )}
                      {!groupComplaintsLoading && gFiltered.length === 0 && (
                        <div className="text-center text-sm text-[#6B6B61] py-8 font-mono">
                          {groupComplaintFilter === "open"
                            ? "Koi open complaint nahi ✓"
                            : "Is filter me kuch nahi hai"}
                        </div>
                      )}
                      {gFiltered.map((it) => (
                        <div
                          key={it.key}
                          data-testid="group-complaint-row"
                          className={`p-3 rounded-md border ${
                            it.state === "resolved"
                              ? "border-emerald-200 bg-emerald-50/40"
                              : it.state === "cancelled"
                                ? "border-slate-200 bg-slate-50"
                                : "border-[#C8553D]/25 bg-[#FFF5F2]"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded ${cBadge(it.state)}`}
                                >
                                  {cKindLabel(it)}
                                </span>
                                <span className="text-[10px] font-mono text-[#6B6B61]">
                                  {(() => {
                                    try {
                                      return new Date(it.filed_at).toLocaleString("en-IN", {
                                        day: "2-digit", month: "short",
                                        hour: "2-digit", minute: "2-digit",
                                        timeZone: "Asia/Kolkata", hour12: true,
                                      });
                                    } catch { return ""; }
                                  })()}
                                </span>
                              </div>
                              {it.kind !== "text" ? (
                                <div className="mt-1.5 font-mono text-sm text-[#1C1C19]">
                                  <span className="text-[#6B6B61] text-xs">LPG ID </span>
                                  <span className="font-semibold">{it.lpg_id}</span>
                                  <span className="text-[#6B6B61] text-xs ml-3">DAC </span>
                                  <span className={`font-semibold ${it.state === "resolved" ? "text-emerald-700" : it.state === "cancelled" ? "text-slate-700" : "text-[#C8553D]"}`}>
                                    {it.dac_code || "—"}
                                  </span>
                                </div>
                              ) : (
                                <div className="mt-1.5 text-sm text-[#1C1C19] whitespace-pre-wrap break-words">
                                  {it.text || "(no content)"}
                                  {it.reply_to && (
                                    <div className="mt-1 pl-2 border-l-2 border-[#385C4B] text-xs text-[#6B6B61]">
                                      ↩ {it.reply_to.sender_display_name}:{" "}
                                      <span className="italic">{it.reply_to.text}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="mt-1.5 text-[10px] font-mono text-[#6B6B61] space-y-0.5">
                                <div>
                                  Filed by <b className="text-[#1C1C19]">{it.marked_by || it.filed_by || "—"}</b>
                                </div>
                                {it.state === "resolved" && (
                                  <div className="text-emerald-800">
                                    <CheckCircle2 className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2.5} />
                                    Resolved by <b>{it.resolved_by || "admin"}</b>
                                    {it.resolved_at && (
                                      <>
                                        {" "}·{" "}
                                        {(() => {
                                          try {
                                            return new Date(it.resolved_at).toLocaleString("en-IN", {
                                              day: "2-digit", month: "short",
                                              hour: "2-digit", minute: "2-digit",
                                              timeZone: "Asia/Kolkata", hour12: true,
                                            });
                                          } catch { return ""; }
                                        })()}
                                      </>
                                    )}
                                    {it.resolution_note && (
                                      <div className="text-[10px] text-emerald-700 mt-0.5">
                                        {it.resolution_note}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {it.state === "open" && canResolve && (
                              <button
                                onClick={() => openResolveDialog(it)}
                                data-testid={it.kind === "text" ? "group-complaint-resolve-text" : "group-complaint-resolve"}
                                className="px-2.5 py-1 text-[10px] font-mono font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1 flex-shrink-0"
                              >
                                <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                                Resolve
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-5 py-2.5 border-t border-[#DCDCD3] bg-[#F7F6EE] text-[10px] font-mono text-[#6B6B61] text-center">
                      Har complaint + uska resolution ek jagah · agency + admin dono ko same dikhta hai
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Resolve dialog — pops on top of the Complaint Box.
                Admin picks either "New DAC" (must enter 4-8 digits) or
                "Delivered from our side". Complaint only resolves after
                one of these is chosen. */}
            {resolveTarget && (
              <div
                className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                onClick={() => !resolveSubmitting && setResolveTarget(null)}
                data-testid="resolve-dialog"
              >
                <div
                  className="bg-white rounded-lg shadow-2xl w-full max-w-md flex flex-col"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  <div className="px-5 py-4 border-b border-[#DCDCD3]">
                    <h3 className="text-base font-semibold text-[#1C1C19] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                      Resolve Complaint
                    </h3>
                    {resolveTarget.kind === "text" ? (
                      <div className="text-[11px] font-mono text-[#6B6B61] mt-1 line-clamp-2 whitespace-pre-wrap">
                        {resolveTarget.text || "(no content)"}
                      </div>
                    ) : (
                      <div className="text-[11px] font-mono text-[#6B6B61] mt-1">
                        LPG ID <b className="text-[#1C1C19]">{resolveTarget.lpg_id}</b>
                        <span className="mx-2">·</span>
                        Old DAC <b className="text-[#C8553D]">{resolveTarget.dac_code || "—"}</b>
                      </div>
                    )}
                  </div>
                  <div className="p-5 space-y-3">
                    <label className={`block p-3 border rounded-md cursor-pointer transition-colors ${resolveChoice === "new_dac" ? "border-emerald-500 bg-emerald-50/50" : "border-[#DCDCD3] hover:border-[#385C4B]/40"}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="resolve-choice"
                          value="new_dac"
                          checked={resolveChoice === "new_dac"}
                          onChange={() => setResolveChoice("new_dac")}
                          data-testid="resolve-choice-new-dac"
                        />
                        <span className="text-sm font-semibold text-[#1C1C19]">New DAC bhejna hai</span>
                      </div>
                      <div className="text-[11px] text-[#6B6B61] mt-1 ml-6">
                        Correct DAC issue karo · agency ke chat me update ho jayega
                      </div>
                      {resolveChoice === "new_dac" && (
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          maxLength={8}
                          value={resolveNewDac}
                          onChange={(ev) => setResolveNewDac(ev.target.value.replace(/\D/g, ""))}
                          placeholder="Enter new DAC (4-8 digits)"
                          data-testid="resolve-new-dac-input"
                          autoFocus
                          className="mt-2 ml-6 w-[calc(100%-1.5rem)] px-3 py-2 rounded-md border border-[#DCDCD3] font-mono text-sm tabular-nums focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        />
                      )}
                    </label>
                    <label className={`block p-3 border rounded-md cursor-pointer transition-colors ${resolveChoice === "delivered" ? "border-emerald-500 bg-emerald-50/50" : "border-[#DCDCD3] hover:border-[#385C4B]/40"}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="resolve-choice"
                          value="delivered"
                          checked={resolveChoice === "delivered"}
                          onChange={() => setResolveChoice("delivered")}
                          data-testid="resolve-choice-delivered"
                        />
                        <span className="text-sm font-semibold text-[#1C1C19]">Delivered from our side</span>
                      </div>
                      <div className="text-[11px] text-[#6B6B61] mt-1 ml-6">
                        Agency ne apne end se cylinder pahucha diya · complaint band karo
                      </div>
                    </label>
                  </div>
                  <div className="px-5 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2 bg-[#F7F6EE]">
                    <button
                      onClick={() => setResolveTarget(null)}
                      disabled={resolveSubmitting}
                      data-testid="resolve-cancel"
                      className="px-3 py-1.5 text-xs font-mono rounded-md border border-[#DCDCD3] bg-white hover:bg-[#EFEEE6] text-[#1C1C19] disabled:opacity-40"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (resolveChoice === "new_dac" && (resolveNewDac.length < 4 || resolveNewDac.length > 8)) {
                          toast.error("New DAC must be 4-8 digits");
                          return;
                        }
                        setResolveSubmitting(true);
                        try {
                          if (resolveTarget.kind === "text") {
                            // Text complaint → hit the message-level
                            // resolve endpoint with the same 2-choice
                            // payload; server stamps the note visible
                            // to the agency in the Resolved tab.
                            await api.post(
                              `/complaints/${resolveTarget.message_id}/resolve`,
                              {
                                action: resolveChoice,
                                new_dac: resolveChoice === "new_dac" ? resolveNewDac : undefined,
                              }
                            );
                          } else {
                            await api.post(
                              `/groups/${activeGroupId}/messages/${resolveTarget.message_id}/lpg/${resolveTarget.entry_index}/resolve`,
                              {
                                action: resolveChoice,
                                new_dac: resolveChoice === "new_dac" ? resolveNewDac : undefined,
                              }
                            );
                          }
                          toast.success(
                            resolveChoice === "new_dac"
                              ? `Correct DAC ${resolveNewDac} bhej diya ✓`
                              : "Delivered from your side · complaint resolved ✓"
                          );
                          setResolveTarget(null);
                          await fetchGroupComplaints();
                          try {
                            const { data: fresh } = await api.get(
                              `/groups/${activeGroupId}/delivery-status`
                            );
                            setDeliveryStatus(fresh);
                          } catch (_e) {
                            /* silent */
                          }
                        } catch (e) {
                          setError(formatApiError(e));
                        } finally {
                          setResolveSubmitting(false);
                        }
                      }}
                      disabled={
                        resolveSubmitting ||
                        (resolveChoice === "new_dac" && (resolveNewDac.length < 4 || resolveNewDac.length > 8))
                      }
                      data-testid="resolve-submit"
                      className="px-3 py-1.5 text-xs font-mono font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5"
                    >
                      {resolveSubmitting ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                      )}
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div
              ref={msgContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-6 md:p-8 space-y-3 md:space-y-4"
              data-testid="chat-messages-container"
            >
              {!isOnline && (
                <div
                  className="sticky top-0 z-10 -mx-4 sm:-mx-6 md:-mx-8 mb-2 bg-[#8A6046]/90 text-[#F5F5F0] text-xs px-4 py-1.5 flex items-center justify-center gap-2 backdrop-blur"
                  data-testid="chat-offline-banner"
                >
                  <WifiOff className="w-3.5 h-3.5" strokeWidth={2} />
                  <span>You're offline · messages will send when you're back</span>
                </div>
              )}
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
                </div>
              )}
              {!loading && displayedMessages.length === 0 && (
                <div className="text-center py-12">
                  <MessageCircle className="w-8 h-8 mx-auto text-[#6B6B61]/50 mb-3" strokeWidth={1.25} />
                  <div className="text-sm text-[#6B6B61]">No messages yet. Say hi 👋</div>
                </div>
              )}
              {displayedMessages.map((m, mIdx) => {
                const mine = m.sender_id === user?.id;
                // WhatsApp-style date separator — emit before any message
                // whose IST day differs from the previous one (or on the
                // very first rendered message).
                const prev = displayedMessages[mIdx - 1];
                const showDateSep =
                  !prev || istDateKey(prev.created_at) !== istDateKey(m.created_at);
                const dateSepEl = showDateSep ? (
                  <div
                    key={`sep-${m.id}`}
                    data-testid="chat-date-separator"
                    className="flex items-center justify-center my-3"
                  >
                    <span className="px-3 py-1 text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] bg-[#F5F5F0] border border-[#DCDCD3] rounded-full">
                      {prettyDateLabel(m.created_at)}
                    </span>
                  </div>
                ) : null;
                // Only the sender can delete their own message — admin cannot
                // delete other people's messages.
                // Delete button visible on any non-deleted message: sender
                // gets a 2-option dialog (Delete for me / Delete for everyone)
                // while non-senders only get "Delete for me" (client-side hide).
                const canDelete = !m.deleted;

                // For deleted messages we render two different views:
                //   • Admin: full original content with a "DELETED BY …" audit tag
                //   • Member: a WhatsApp-style tombstone — they know a message
                //     existed and was deleted, but the content is hidden.
                if (m.deleted) {
                  if (user?.role !== "admin") {
                    return (
                      <React.Fragment key={m.id}>
                      {dateSepEl}
                      <div
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        data-testid="chat-message-bubble"
                        data-deleted="true"
                      >
                        <div
                          className={
                            (mine
                              ? "ml-auto rounded-l-xl rounded-tr-xl"
                              : "mr-auto rounded-r-xl rounded-tl-xl") +
                            " bg-[#EBEBE6] border border-dashed border-[#DCDCD3] text-[#6B6B61] italic px-4 py-2.5 max-w-[80%] md:max-w-[65%] flex items-center gap-2"
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          <span className="text-xs">
                            {mine
                              ? "You deleted this message"
                              : "This message was deleted"}
                          </span>
                          <span
                            className="text-[10px] font-mono opacity-70 ml-2"
                            style={{ fontFamily: "Inter, sans-serif" }}
                          >
                            {formatTime(m.created_at)}
                          </span>
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={m.id}>
                    {dateSepEl}
                    <div
                      className={`flex group items-start ${
                        mine ? "justify-end" : "justify-start"
                      }`}
                      data-testid="chat-message-bubble"
                      data-deleted="true"
                    >
                      {!mine && (
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold text-xs flex-shrink-0 mr-2 mt-1 shadow-sm opacity-60"
                          style={{ background: avatarColor(m.sender_id) }}
                        >
                          {initialsOf(m.sender_display_name)}
                        </div>
                      )}
                      <div
                        className={`relative max-w-[80%] md:max-w-[65%] ${
                          mine
                            ? "bg-gradient-to-br from-[#3F6753]/55 to-[#2D4A3C]/55 text-[#F5F5F0] rounded-l-2xl rounded-tr-2xl"
                            : "bg-white/70 border border-dashed border-[#8A6046]/40 text-[#1C1C19] rounded-r-2xl rounded-tl-2xl"
                        } p-3 shadow-sm`}
                      >
                        {!mine && (
                          <div
                            className="text-[11px] font-semibold mb-1 opacity-70"
                            style={{
                              fontFamily: "Outfit, sans-serif",
                              color: avatarColor(m.sender_id),
                            }}
                          >
                            {m.sender_display_name}
                          </div>
                        )}
                        <div
                          className="text-sm whitespace-pre-wrap break-words line-through opacity-70"
                          style={{ fontFamily: "Work Sans, sans-serif" }}
                        >
                          {m.text}
                        </div>
                        <div
                          className={`mt-2 flex items-center gap-1.5 text-[10px] font-mono ${
                            mine ? "text-[#F5F5F0]/80" : "text-[#8A6046]"
                          }`}
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                          DELETED
                          {m.deleted_by_role
                            ? ` BY ${m.deleted_by_role.toUpperCase()}`
                            : ""}
                          {" · "}
                          {formatTime(m.deleted_at || m.created_at)}
                          {" · ADMIN-ONLY VIEW"}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }

                // Resend confirmation / duplicate auto-removal pill — shown
                // ONLY when the message has no fresh LPG entries (pure resend
                // or pure duplicate). Mixed cases render the LPG bubble with
                // an inline footer note instead.
                if (
                  (m.resend_confirmation || m.duplicate_removed) &&
                  !(m.lpg_entries && m.lpg_entries.length > 0)
                ) {
                  const resolvedIds = m.resolved_lpg_ids || [];
                  const duplicateIds = m.duplicate_lpg_ids || [];
                  return (
                    <React.Fragment key={m.id}>
                    {dateSepEl}
                    <div
                      className={`flex group items-start ${mine ? "justify-end" : "justify-start"}`}
                      data-testid="chat-resend-confirmation"
                    >
                      {!mine && (
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold text-xs flex-shrink-0 mr-2 mt-1 shadow-sm"
                          style={{ background: avatarColor(m.sender_id) }}
                        >
                          {initialsOf(m.sender_display_name)}
                        </div>
                      )}
                      <div
                        className={`max-w-md rounded-md px-3 py-2 border ${
                          mine
                            ? "bg-amber-500/15 border-amber-500/40 text-amber-900"
                            : "bg-amber-500/10 border-amber-500/30 text-amber-900"
                        }`}
                      >
                        {!mine && (
                          <div
                            className="text-[11px] font-semibold mb-1"
                            style={{
                              fontFamily: "Outfit, sans-serif",
                              color: avatarColor(m.sender_id),
                            }}
                          >
                            {m.sender_display_name}
                          </div>
                        )}
                        {resolvedIds.length > 0 && (
                          <>
                            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-amber-700">
                              <RefreshCw className="w-3 h-3" strokeWidth={2.5} />
                              Resend Confirmed · {resolvedIds.length}
                            </div>
                            <div className="mt-1 mb-1 space-y-0.5">
                              {resolvedIds.map((id) => (
                                <div
                                  key={id}
                                  className="text-xs font-mono text-[#1C1C19]"
                                  style={{ fontFamily: "Inter, sans-serif" }}
                                >
                                  {id}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        {duplicateIds.length > 0 && (() => {
                          const isExpanded = expandedDupLists.has(m.id);
                          // Sep 2026 admin ask ("dublicate removed dikha
                          // do only"): NEVER expand the ID list by
                          // default — every count clicks to reveal.
                          // Previously we auto-expanded lists ≤ 3 IDs
                          // which made post-`mark_delivered` cards
                          // spammy when the file had lots of duplicates.
                          const showList = isExpanded;
                          return (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleDupList(m.id)}
                                className="w-full flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-amber-700 mt-1 cursor-pointer hover:text-amber-800"
                                data-testid="chat-dup-toggle"
                                title={isExpanded ? "Hide the ID list" : "Tap to show all duplicate IDs"}
                              >
                                <Copy className="w-3 h-3" strokeWidth={2.5} />
                                <span>Duplicate Auto-Removed · {duplicateIds.length}</span>
                                <span className="ml-auto flex items-center gap-0.5 text-[10px] font-normal opacity-80">
                                  {isExpanded ? (
                                    <>hide <ChevronDown className="w-3 h-3" strokeWidth={2.5} /></>
                                  ) : (
                                    <>show list <ChevronRight className="w-3 h-3" strokeWidth={2.5} /></>
                                  )}
                                </span>
                              </button>
                              {showList && (
                                <div className="mt-1 space-y-0.5">
                                  {duplicateIds.map((id) => (
                                    <div
                                      key={id}
                                      className="text-xs font-mono text-[#1C1C19] line-through opacity-70"
                                      style={{ fontFamily: "Inter, sans-serif" }}
                                    >
                                      {id}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <div className="text-[9px] font-mono text-[#6B6B61] mt-1 text-right">
                          {formatTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }

                // LPG delivery codes — specialized privacy-safe bubble.
                if (m.lpg_entries && m.lpg_entries.length > 0) {
                  const isHighlighted = highlightMsgId && m.id === highlightMsgId;
                  return (
                    <React.Fragment key={m.id}>
                    {dateSepEl}
                    <div
                      id={`msg-${m.id}`}
                      data-msg-id={m.id}
                      data-msg-sender={m.sender_id}
                      className={`flex group items-start justify-start ${isHighlighted ? "ring-4 ring-amber-400 rounded-xl transition-shadow duration-500" : ""}`}
                      data-testid="chat-lpg-bubble"
                    >
                      {!mine && (
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold text-xs flex-shrink-0 mr-2 mt-1 shadow-sm"
                          style={{ background: avatarColor(m.sender_id) }}
                        >
                          {initialsOf(m.sender_display_name)}
                        </div>
                      )}
                      <div
                        className={`w-full max-w-[92%] sm:max-w-[80%] md:max-w-[70%] overflow-hidden rounded-xl p-2.5 sm:p-3 shadow-sm border cursor-default ${
                          mine
                            ? "bg-gradient-to-br from-[#3F6753] to-[#2D4A3C] border-[#2D4A3C] text-[#F5F5F0]"
                            : "bg-white border-[#DCDCD3] text-[#1C1C19]"
                        }`}
                        onContextMenu={(e) => {
                          // Right-click → WhatsApp-style context menu.
                          if (m.deleted) return;
                          e.preventDefault();
                          openMessageContextMenu(m, e);
                        }}
                        onTouchStart={(e) => {
                          // Long-press (600ms) → context menu on mobile.
                          if (m.deleted) return;
                          const target = e.currentTarget;
                          const touch0 = e.touches?.[0];
                          const startX = touch0?.clientX ?? 0;
                          const startY = touch0?.clientY ?? 0;
                          const timer = setTimeout(() => {
                            openMessageContextMenu(m, {
                              clientX: startX,
                              clientY: startY,
                            });
                          }, 600);
                          const clear = () => {
                            clearTimeout(timer);
                            target.removeEventListener("touchend", clear);
                            target.removeEventListener("touchmove", clear);
                            target.removeEventListener("touchcancel", clear);
                          };
                          target.addEventListener("touchend", clear);
                          target.addEventListener("touchmove", clear);
                          target.addEventListener("touchcancel", clear);
                        }}
                      >
                        {!mine && (
                          <div
                            className="text-[11px] font-semibold mb-2"
                            style={{
                              fontFamily: "Outfit, sans-serif",
                              color: avatarColor(m.sender_id),
                            }}
                          >
                            {m.sender_display_name}
                          </div>
                        )}
                        <div
                          className={`text-[10px] font-mono uppercase tracking-wider mb-2 flex items-center justify-between gap-2 ${
                            mine ? "text-[#F5F5F0]/70" : "text-[#6B6B61]"
                          }`}
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          <span>LPG Delivery</span>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const total = m.lpg_entries.length;
                              const correctOnly = m.lpg_entries.filter(
                                (e) => e.status === "correct"
                              ).length;
                              const wrong = m.lpg_entries.filter(
                                (e) => e.status === "wrong"
                              ).length;
                              const resent = m.lpg_entries.filter(
                                (e) => e.status === "resent"
                              ).length;
                              // Match LPG Report + Sheet: Done = correct + resent
                              const done = correctOnly + resent;
                              // Delivered = correct+resent WITH a non-empty
                              // marked_by. This lets the header render a
                              // live-updating "· 12 delivered" pip so the
                              // WS flip is instantly visible next to the
                              // static "Done X/Y" count.
                              const delivered = m.lpg_entries.filter(
                                (e) =>
                                  (e.status === "correct" || e.status === "resent") &&
                                  (e.marked_by || "").trim() !== ""
                              ).length;
                              return (
                                <span
                                  data-testid="chat-lpg-counter"
                                  className="font-bold tracking-wider"
                                >
                                  Done {done}/{total}
                                  {wrong > 0 ? ` · ${wrong} wrong` : ""}
                                  {resent > 0 ? ` · ${resent} resent` : ""}
                                  {delivered > 0 && (
                                    <span
                                      className={
                                        mine
                                          ? "ml-1 text-emerald-200 font-mono text-[10px]"
                                          : "ml-1 text-emerald-700 font-mono text-[10px]"
                                      }
                                      title="Entries acknowledged as delivered (marked_by set)"
                                      data-testid="chat-lpg-delivered-count"
                                    >
                                      · {delivered} delivered
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                            <button
                              onClick={async (ev) => {
                                ev.stopPropagation();
                                // HTML+plain dual-format copy → Excel /
                                // Sheets get clean per-cell text. The plain
                                // fallback is tab-separated.
                                const rows = [
                                  ["LPG Consumer ID", "DAC Code", "Status"],
                                  ...m.lpg_entries.map((e) => [
                                    e.lpg_id, e.dac_code, e.status,
                                  ]),
                                ];
                                const btn = ev.currentTarget;
                                const ok = await copyForSpreadsheet(rows);
                                btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                                setTimeout(() => {
                                  btn.removeAttribute("data-copied");
                                  btn.removeAttribute("data-copy-failed");
                                }, 1800);
                              }}
                              data-testid="chat-lpg-copy-bubble"
                              title="Copy this LPG list — paste into Excel / Google Sheets"
                              className={`p-1 rounded transition-colors data-[copied=1]:bg-emerald-500/30 data-[copy-failed=1]:bg-red-500/30 ${
                                mine
                                  ? "hover:bg-white/15 text-[#F5F5F0]/70 hover:text-[#F5F5F0]"
                                  : "hover:bg-[#385C4B]/10 text-[#6B6B61] hover:text-[#385C4B]"
                              }`}
                            >
                              <Copy className="w-3 h-3" strokeWidth={2} />
                            </button>
                            <button
                              onClick={async (ev) => {
                                ev.stopPropagation();
                                // Feb 2026: per-message Excel export.
                                // Backend builds `.xlsx` with smart
                                // filename `AGENCY_DATE_QTY.xlsx`.
                                // We fetch as blob and trigger the
                                // browser download.
                                const btn = ev.currentTarget;
                                try {
                                  const resp = await api.get(
                                    `/messages/${m.id}/excel`,
                                    { responseType: "blob" }
                                  );
                                  // Filename comes from Content-Disposition
                                  const cd = resp.headers["content-disposition"] || "";
                                  const match = cd.match(/filename="?([^"]+)"?/);
                                  const filename = match ? match[1] : `lpg_${m.id}.xlsx`;
                                  const url = URL.createObjectURL(resp.data);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = filename;
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                                  btn.setAttribute("data-copied", "1");
                                } catch {
                                  btn.setAttribute("data-copy-failed", "1");
                                }
                                setTimeout(() => {
                                  btn.removeAttribute("data-copied");
                                  btn.removeAttribute("data-copy-failed");
                                }, 1800);
                              }}
                              data-testid="chat-lpg-download-xlsx"
                              title="Download this delivery as Excel (agency_date_qty.xlsx)"
                              className={`p-1 rounded transition-colors data-[copied=1]:bg-emerald-500/30 data-[copy-failed=1]:bg-red-500/30 ${
                                mine
                                  ? "hover:bg-white/15 text-[#F5F5F0]/70 hover:text-[#F5F5F0]"
                                  : "hover:bg-[#385C4B]/10 text-[#6B6B61] hover:text-[#385C4B]"
                              }`}
                            >
                              <Download className="w-3 h-3" strokeWidth={2} />
                            </button>
                            {/* Bulk-tick buttons — mark EVERY LPG entry
                                in this message with one action. Fires
                                per-entry POSTs in parallel so each
                                row lands in the Google Sheet real-time
                                webhook without a special new endpoint.
                                Only shown when > 1 entry (single-entry
                                messages already have their own tick). */}
                            {m.lpg_entries.length > 1 && (() => {
                              const allCorrect = m.lpg_entries.every(
                                (e) => (e.status || "correct") === "correct"
                              );
                              const allWrong = m.lpg_entries.every(
                                (e) => e.status === "wrong"
                              );
                              // Pending = never-acknowledged (status
                              // correct + no marked_by). This is what
                              // the agency needs to focus on when
                              // 150/200 are already done.
                              const pendingCount = m.lpg_entries.filter(
                                (e) =>
                                  (e.status || "correct") === "correct" &&
                                  !(e.marked_by || "").trim()
                              ).length;
                              const showingPendingOnly = pendingOnlyMsgs.has(m.id);
                              return (
                                <>
                                  {pendingCount > 0 && pendingCount < m.lpg_entries.length && (
                                    <button
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        togglePendingOnly(m.id);
                                      }}
                                      data-testid="chat-lpg-pending-only-toggle"
                                      title={
                                        showingPendingOnly
                                          ? `Show all ${m.lpg_entries.length} entries`
                                          : `Show only the ${pendingCount} pending — agency ne abhi ✓ nahi kiya`
                                      }
                                      className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold transition-colors ${
                                        showingPendingOnly
                                          ? mine
                                            ? "bg-amber-400/30 text-amber-100"
                                            : "bg-amber-500/20 text-amber-800"
                                          : mine
                                            ? "hover:bg-amber-400/20 text-amber-200"
                                            : "hover:bg-amber-500/10 text-amber-700"
                                      }`}
                                    >
                                      {showingPendingOnly ? `Show all` : `Pending ${pendingCount}`}
                                    </button>
                                  )}
                                  <button
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      if (allCorrect) return;
                                      markAllEntries(m, "correct");
                                    }}
                                    disabled={allCorrect}
                                    data-testid="chat-lpg-mark-all-done"
                                    title={
                                      allCorrect
                                        ? "All already delivered ✓"
                                        : `Mark all ${m.lpg_entries.length} as delivered (agency acknowledges every LPG in one tap)`
                                    }
                                    className={`p-1 rounded transition-colors ${
                                      allCorrect
                                        ? "opacity-40 cursor-not-allowed text-emerald-600"
                                        : mine
                                          ? "hover:bg-emerald-500/25 text-emerald-200"
                                          : "hover:bg-emerald-500/15 text-emerald-700"
                                    }`}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.25} />
                                  </button>
                                  {/* Feb 2026: "Mark all Wrong DAC" (× icon)
                                      removed per admin request — team was
                                      accidentally raising bulk complaints and
                                      the individual row-level Wrong DAC
                                      button (below) is a safer alternative. */}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          {(() => {
                            const showPendingOnly = pendingOnlyMsgs.has(m.id);
                            // Preserve original index so `setEntryStatus`
                            // and `handleTickEntry(idx)` still target the
                            // right row after filtering.
                            const rows = m.lpg_entries
                              .map((entry, idx) => ({ entry, idx }))
                              .filter(({ entry }) => {
                                if (!showPendingOnly) return true;
                                return (
                                  (entry.status || "correct") === "correct" &&
                                  !(entry.marked_by || "").trim()
                                );
                              });
                            return rows.map(({ entry, idx }) => {
                            const isWrong = entry.status === "wrong";
                            const isResent = entry.status === "resent";
                            const isCorrect = !isWrong && !isResent;
                            const StatusIcon = isResent
                              ? RotateCw
                              : isWrong
                              ? XCircle
                              : CheckCircle2;
                            const btnColor = isResent
                              ? "bg-blue-500/90 hover:bg-blue-500"
                              : isWrong
                              ? "bg-red-500/90 hover:bg-red-500"
                              : "bg-emerald-500/90 hover:bg-emerald-500";
                            return (
                              <div
                                key={`${entry.lpg_id}-${entry.dac_code}-${idx}`}
                                data-testid="chat-lpg-entry"
                                data-status={entry.status}
                                className={`px-2.5 py-1.5 rounded-lg ${
                                  mine
                                    ? "bg-white/10 hover:bg-white/15"
                                    : "bg-[#F5F5F0] hover:bg-[#EBEBE6]"
                                } transition-colors`}
                              >
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleLpgStatus(m, idx)}
                                    data-testid="chat-lpg-toggle"
                                    title={
                                      isResent
                                        ? "Resent — click to mark correct"
                                        : isWrong
                                        ? "Marked wrong — click to mark correct"
                                        : "Marked correct — click to mark wrong"
                                    }
                                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all text-white ${btnColor}`}
                                  >
                                    <StatusIcon className="w-4 h-4" strokeWidth={2.5} />
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <div
                                      className={`text-xs font-mono truncate ${
                                        mine ? "text-[#F5F5F0]" : "text-[#1C1C19]"
                                      } ${isWrong ? "line-through opacity-60" : ""}`}
                                      style={{ fontFamily: "Inter, sans-serif" }}
                                    >
                                      {/* Bharat groups store the raw
                                          consumer number (1-11 digits).
                                          Prefix with "Consumer" so agencies
                                          instantly recognise it as the
                                          number printed on the gas card. */}
                                      {(() => {
                                        const raw = entry.lpg_id || "";
                                        const isShortConsumer =
                                          /^\d{1,11}$/.test(raw) &&
                                          !raw.startsWith("10000000");
                                        return isShortConsumer
                                          ? `Consumer ${raw}`
                                          : raw;
                                      })()}
                                    </div>
                                    <div
                                      className={`text-[11px] font-mono ${
                                        mine ? "text-[#F5F5F0]/80" : "text-[#6B6B61]"
                                      } ${isWrong ? "line-through opacity-60" : ""}`}
                                      style={{ fontFamily: "Inter, sans-serif" }}
                                    >
                                      {(() => {
                                        // Feb 2026: for Indane groups the user
                                        // wants a fixed `LPG ID · Mob · DAC`
                                        // format on every row so all 3 pieces
                                        // are visible at a glance — even when
                                        // the mobile column wasn't captured
                                        // (renders `—` in that case). For
                                        // non-Indane groups we keep the
                                        // opt-in behaviour (only show Mob
                                        // when the entry actually has one).
                                        const isIndaneGroup = /INDANE/i.test(activeGroup?.name || "");
                                        const showMob =
                                          isIndaneGroup ||
                                          (entry.mobile && entry.mobile !== entry.lpg_id);
                                        return (
                                          <>
                                            DAC{" "}
                                            <span className="font-bold tracking-wider">
                                              {entry.dac_code}
                                            </span>
                                            {showMob && (
                                              <>
                                                {" · "}
                                                <span
                                                  className={`font-mono ${
                                                    mine ? "text-[#F5F5F0]/70" : "text-[#8A6046]"
                                                  }`}
                                                  data-testid="chat-lpg-mobile"
                                                  title="Customer mobile (from bulk paste)"
                                                >
                                                  Mob {entry.mobile && entry.mobile !== entry.lpg_id ? entry.mobile : "—"}
                                                </span>
                                              </>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                  {entry.marked_by && !isCorrect && (
                                    <div
                                      className={`text-[9px] font-mono italic flex-shrink-0 ${
                                        mine ? "text-[#F5F5F0]/60" : "text-[#A7A79D]"
                                      }`}
                                      title={`marked by ${entry.marked_by}`}
                                      style={{
                                        fontFamily: "Inter, sans-serif",
                                      }}
                                    >
                                      by {entry.marked_by.split(" ")[0]}
                                    </div>
                                  )}
                                  {/* Delivered pill — visible whenever a
                                      "correct" entry has been acknowledged
                                      by an operator (marked_by set).
                                      Distinguishes true DELIVERED entries
                                      from plain pending ones at a glance;
                                      updates live via WS on flip so the
                                      user sees the state change without
                                      refreshing. */}
                                  {entry.marked_by && isCorrect && (
                                    <div
                                      className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold flex items-center gap-1 flex-shrink-0 ${
                                        mine
                                          ? "bg-emerald-400/25 text-emerald-100"
                                          : "bg-emerald-100 text-emerald-800"
                                      }`}
                                      title={
                                        entry.delivered_date
                                          ? `Delivered ${entry.delivered_date} · by ${entry.marked_by}`
                                          : `Delivered · by ${entry.marked_by}`
                                      }
                                      data-testid="chat-lpg-delivered-pill"
                                    >
                                      <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={3} />
                                      {entry.delivered_date
                                        ? entry.delivered_date.slice(5).replace("-", "/")
                                        : "Delivered"}
                                    </div>
                                  )}
                                  {/* Pending pill — mirrors the delivered
                                      pill above so admins can see AT A
                                      GLANCE which entries are still open.
                                      Sep 2026 admin ask ("pata chalna
                                      chahiye hua ki nhi hua"). */}
                                  {!entry.marked_by && isCorrect && (
                                    <div
                                      className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold flex items-center gap-1 flex-shrink-0 ${
                                        mine
                                          ? "bg-amber-400/25 text-amber-100"
                                          : "bg-amber-100 text-amber-800"
                                      }`}
                                      title="Pending — agency ne abhi acknowledge nahi kiya"
                                      data-testid="chat-lpg-pending-pill"
                                    >
                                      <Clock className="w-2.5 h-2.5" strokeWidth={3} />
                                      Pending
                                    </div>
                                  )}
                                  <button
                                    onClick={async (ev) => {
                                      ev.stopPropagation();
                                      const btn = ev.currentTarget;
                                      const ok = await copyForSpreadsheet([
                                        [entry.lpg_id, entry.dac_code],
                                      ]);
                                      btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                                      setTimeout(() => {
                                        btn.removeAttribute("data-copied");
                                        btn.removeAttribute("data-copy-failed");
                                      }, 1500);
                                    }}
                                    data-testid="chat-lpg-copy-entry"
                                    title="Copy this LPG + DAC (spreadsheet-safe)"
                                    className={`flex-shrink-0 p-1 rounded transition-colors data-[copied=1]:bg-emerald-500/30 data-[copy-failed=1]:bg-red-500/30 ${
                                      mine
                                        ? "hover:bg-white/15 text-[#F5F5F0]/60 hover:text-[#F5F5F0]"
                                        : "hover:bg-[#385C4B]/10 text-[#A7A79D] hover:text-[#385C4B]"
                                    }`}
                                  >
                                    <Copy className="w-3 h-3" strokeWidth={1.75} />
                                  </button>
                                </div>
                                {isWrong && (
                                  <div className="mt-1.5 ml-8 flex flex-wrap gap-1.5">
                                    <button
                                      onClick={() => toggleLpgStatus(m, idx, "resent")}
                                      data-testid="chat-lpg-confirm-resend"
                                      className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded-md transition-colors ${
                                        mine
                                          ? "bg-white/20 hover:bg-white/30 text-[#F5F5F0]"
                                          : "bg-blue-500/15 hover:bg-blue-500/25 text-blue-700 border border-blue-500/30"
                                      }`}
                                      style={{ fontFamily: "Inter, sans-serif" }}
                                    >
                                      Confirm Resend
                                    </button>
                                    <button
                                      onClick={() => {
                                        // Pre-fill the chat input with the same LPG ID + DAC code
                                        // and set this message as the reply target so the new
                                        // entry is linked back to the wrong one (transparency).
                                        const lpgLine = `${entry.lpg_id} DAC ${entry.dac_code}`;
                                        setText((prev) => {
                                          const t = prev?.trim();
                                          return t ? `${t}\n${lpgLine}` : lpgLine;
                                        });
                                        setReplyingTo(m);
                                        setTimeout(() => {
                                          inputRef.current?.focus();
                                          // Move cursor to end
                                          const el = inputRef.current;
                                          if (el && el.setSelectionRange) {
                                            const end = el.value.length;
                                            el.setSelectionRange(end, end);
                                          }
                                        }, 50);
                                      }}
                                      data-testid="chat-lpg-copy-resend"
                                      title="Copy this LPG ID + DAC to chat input as a reply — review then Send"
                                      className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded-md transition-colors flex items-center gap-1 ${
                                        mine
                                          ? "bg-amber-400/25 hover:bg-amber-400/35 text-amber-100"
                                          : "bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 border border-amber-500/40"
                                      }`}
                                      style={{ fontFamily: "Inter, sans-serif" }}
                                    >
                                      <Copy className="w-3 h-3" strokeWidth={2} />
                                      Copy &amp; Send Below
                                    </button>
                                  </div>
                                )}
                                {isResent && entry.resent_at && (
                                  <div
                                    className={`mt-1 ml-8 text-[9px] font-mono italic ${
                                      mine ? "text-[#F5F5F0]/70" : "text-blue-600"
                                    }`}
                                    style={{ fontFamily: "Inter, sans-serif" }}
                                  >
                                    Resent at{" "}
                                    {new Date(entry.resent_at).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}{" "}
                                    by {entry.resent_by || entry.marked_by}
                                  </div>
                                )}
                              </div>
                            );
                          });
                          })()}
                        </div>
                        {((m.resolved_lpg_ids || []).length > 0 ||
                          (m.duplicate_lpg_ids || []).length > 0) && (
                          <div
                            className={`mt-2 px-2 py-1.5 rounded text-[10px] font-mono ${
                              mine
                                ? "bg-amber-300/15 text-amber-100"
                                : "bg-amber-500/10 text-amber-800"
                            }`}
                            style={{ fontFamily: "Inter, sans-serif" }}
                            data-testid="chat-lpg-auto-note"
                          >
                            {(m.resolved_lpg_ids || []).length > 0 && (
                              <div className="flex items-center gap-1">
                                <RefreshCw className="w-2.5 h-2.5" strokeWidth={2.5} />
                                Auto resent · {(m.resolved_lpg_ids || []).length}{" "}
                                {(m.resolved_lpg_ids || []).length === 1 ? "entry" : "entries"}
                              </div>
                            )}
                            {(m.duplicate_lpg_ids || []).length > 0 && (
                              <div className="flex items-center gap-1">
                                <Copy className="w-2.5 h-2.5" strokeWidth={2.5} />
                                Duplicate removed · {(m.duplicate_lpg_ids || []).length}{" "}
                                {(m.duplicate_lpg_ids || []).length === 1 ? "entry" : "entries"}
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          className={`text-[10px] mt-2 font-mono text-right flex items-center justify-end gap-1.5 ${
                            mine ? "text-[#F5F5F0]/70" : "text-[#6B6B61]"
                          }`}
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          <span>{formatTime(m.created_at)}</span>
                          <BubbleTicks
                            mine={mine}
                            pending={m._pending}
                            deliveredCount={m.delivered_to_count}
                            readCount={m.read_by_count}
                            recipientCount={m.recipient_count}
                          />
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={m.id}>
                  {dateSepEl}
                  <div
                    id={`msg-${m.id}`}
                    data-msg-id={m.id}
                    data-msg-sender={m.sender_id}
                    className={`flex group items-start ${mine ? "justify-end" : "justify-start"}`}
                    data-testid="chat-message-bubble"
                  >
                    {!mine && (
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold text-xs flex-shrink-0 mr-2 mt-1 shadow-sm"
                        style={{ background: avatarColor(m.sender_id) }}
                      >
                        {initialsOf(m.sender_display_name)}
                      </div>
                    )}
                    <div className="relative max-w-[80%] md:max-w-[65%]">
                      <div
                        onContextMenu={(e) => {
                          if (m.deleted) return;
                          e.preventDefault();
                          openMessageContextMenu(m, e);
                        }}
                        onTouchStart={(e) => {
                          if (m.deleted) return;
                          const target = e.currentTarget;
                          const touch0 = e.touches?.[0];
                          const startX = touch0?.clientX ?? 0;
                          const startY = touch0?.clientY ?? 0;
                          const timer = setTimeout(() => {
                            openMessageContextMenu(m, {
                              clientX: startX,
                              clientY: startY,
                            });
                          }, 600);
                          const clear = () => {
                            clearTimeout(timer);
                            target.removeEventListener("touchend", clear);
                            target.removeEventListener("touchmove", clear);
                            target.removeEventListener("touchcancel", clear);
                          };
                          target.addEventListener("touchend", clear);
                          target.addEventListener("touchmove", clear);
                          target.addEventListener("touchcancel", clear);
                        }}
                        className={
                          mine
                            ? "bg-gradient-to-br from-[#3F6753] to-[#2D4A3C] text-[#F5F5F0] p-3 rounded-l-2xl rounded-tr-2xl shadow-md"
                            : "bg-white border border-[#DCDCD3] text-[#1C1C19] p-3 rounded-r-2xl rounded-tl-2xl shadow-sm"
                        }
                      >
                        {m.is_broadcast && (
                          <div
                            className={`text-[10px] font-semibold uppercase tracking-widest rounded-md px-2 py-1 mb-2 flex items-center gap-1.5 ${
                              m.broadcast_priority === "urgent"
                                ? "bg-[#C8553D] text-white animate-pulse"
                                : m.broadcast_priority === "important"
                                ? "bg-[#FFF7E6] text-[#B8791F] border border-[#E0A020]/40"
                                : mine
                                ? "bg-white/15 text-[#F5F5F0]"
                                : "bg-[#385C4B]/10 text-[#385C4B]"
                            }`}
                            data-testid={`chat-broadcast-badge-${m.broadcast_priority || "normal"}`}
                          >
                            📢
                            {m.broadcast_priority === "urgent"
                              ? "URGENT · Broadcast"
                              : m.broadcast_priority === "important"
                              ? "Important · Broadcast"
                              : "Broadcast Notice"}
                          </div>
                        )}
                        {!mine && (
                          <div
                            className="text-[11px] font-semibold mb-1"
                            style={{
                              fontFamily: "Outfit, sans-serif",
                              color: avatarColor(m.sender_id),
                            }}
                          >
                            {m.sender_display_name}
                          </div>
                        )}
                        {m.reply_to && (
                          <div
                            className={`mb-2 pl-2 border-l-2 text-xs ${
                              mine
                                ? "border-[#F5F5F0]/50 bg-[#F5F5F0]/10"
                                : "border-[#385C4B] bg-[#385C4B]/5"
                            } rounded-sm py-1 pr-2`}
                          >
                            <div
                              className={`text-[11px] font-semibold ${
                                mine ? "text-[#F5F5F0]" : "text-[#385C4B]"
                              }`}
                              style={{ fontFamily: "Outfit, sans-serif" }}
                            >
                              ↩ {m.reply_to.sender_display_name}
                            </div>
                            <div
                              className={`truncate ${
                                mine ? "text-[#F5F5F0]/80" : "text-[#6B6B61]"
                              }`}
                            >
                              {m.reply_to.text || "(deleted)"}
                            </div>
                          </div>
                        )}
                        <div
                          className="text-sm whitespace-pre-wrap break-words"
                          style={{ fontFamily: "Work Sans, sans-serif" }}
                        >
                          {renderMessageText(m.text)}
                        </div>
                        {/* Chat file attachments — images render inline, other
                            types show as a download card. Ledger untouched. */}
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {m.attachments.map((a, ai) => {
                              // Token-attached URL so <img src> & fetch()
                              // authenticate (Feb 2026 — plain /api/files
                              // route rejects without token).
                              const url = getFileUrl(a.file_id);
                              const isImg = (a.content_type || "").startsWith("image/");
                              const icon = isImg
                                ? "📷"
                                : (a.name || "").toLowerCase().endsWith(".pdf")
                                ? "📄"
                                : "📊";
                              return (
                                <div
                                  key={a.file_id || `attach-${ai}`}
                                  data-testid={`chat-attachment-${a.file_id}`}
                                  className={`block rounded-lg overflow-hidden border ${
                                    mine ? "border-white/20" : "border-[#DCDCD3]"
                                  }`}
                                >
                                  {isImg ? (
                                    <button
                                      type="button"
                                      onClick={() => downloadAttachment(a, url)}
                                      className="block w-full"
                                      title="Tap to download"
                                    >
                                      <img
                                        src={url}
                                        alt={a.name}
                                        className="max-w-[280px] max-h-[280px] object-cover"
                                        loading="lazy"
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => downloadAttachment(a, url)}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                                        mine ? "bg-white/10 hover:bg-white/15" : "bg-[#F5F5F0] hover:bg-[#EBEBE1]"
                                      } transition-colors`}
                                    >
                                      <span className="text-2xl">{icon}</span>
                                      <div className="flex-1 min-w-0">
                                        <div
                                          className={`text-xs font-semibold truncate ${
                                            mine ? "text-white" : "text-[#1C1C19]"
                                          }`}
                                        >
                                          {a.name}
                                        </div>
                                        <div
                                          className={`text-[10px] ${
                                            mine ? "text-white/70" : "text-[#6B6B61]"
                                          }`}
                                        >
                                          {((a.size || 0) / 1024).toFixed(0)} KB · tap to download
                                        </div>
                                      </div>
                                      <Download
                                        className={`w-4 h-4 flex-shrink-0 ${
                                          mine ? "text-white/70" : "text-[#385C4B]"
                                        }`}
                                        strokeWidth={2}
                                      />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {m.is_complaint && !m.complaint_resolved_at && (
                          <div
                            data-testid="chat-message-complaint-badge"
                            className={`mt-2 inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded ${
                              mine
                                ? "bg-[#C8553D]/30 text-[#FFE4DC] border border-[#FFE4DC]/30"
                                : "bg-[#C8553D]/10 text-[#C8553D] border border-[#C8553D]/30"
                            }`}
                          >
                            <Flag className="w-3 h-3" strokeWidth={2} /> Complaint
                          </div>
                        )}
                        {m.is_complaint && m.complaint_resolved_at && (
                          <div
                            data-testid="chat-message-complaint-resolved"
                            className={`mt-2 inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded ${
                              mine
                                ? "bg-[#385C4B]/40 text-[#E6F0EA] border border-[#E6F0EA]/30"
                                : "bg-[#385C4B]/10 text-[#385C4B] border border-[#385C4B]/30"
                            }`}
                          >
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Resolved
                          </div>
                        )}
                        <div
                          className={`text-[10px] mt-1.5 font-mono flex items-center gap-1.5 ${
                            mine ? "text-[#F5F5F0]/70 justify-end" : "text-[#6B6B61] justify-end"
                          }`}
                        >
                          <span>{formatTime(m.created_at)}</span>
                          {m._pending && m.status === "pending" && (
                            <span
                              title="Sending..."
                              data-testid="chat-message-pending"
                              className="inline-flex items-center gap-0.5"
                            >
                              <Clock className="w-3 h-3" strokeWidth={2} />
                            </span>
                          )}
                          {m._pending && m.status === "failed" && (
                            <span
                              data-testid="chat-message-failed"
                              className="inline-flex items-center gap-1 text-red-300"
                            >
                              <AlertCircle className="w-3 h-3" strokeWidth={2} />
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  retryFailed(m);
                                }}
                                className="underline hover:no-underline"
                              >
                                retry
                              </button>
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  discardFailed(m);
                                }}
                                className="underline hover:no-underline"
                              >
                                discard
                              </button>
                            </span>
                          )}
                          {!m._pending && (
                            <BubbleTicks
                              mine={mine}
                              pending={m._pending}
                              deliveredCount={m.delivered_to_count}
                              readCount={m.read_by_count}
                              recipientCount={m.recipient_count}
                            />
                          )}
                        </div>
                      </div>

                      {/* Reactions chips */}
                      {m.reactions && Object.keys(m.reactions).length > 0 && (
                        <div
                          className={`flex flex-wrap gap-1 mt-1 ${
                            mine ? "justify-end" : "justify-start"
                          }`}
                        >
                          {Object.entries(m.reactions).map(([emoji, ids]) =>
                            ids && ids.length > 0 ? (
                              <button
                                key={emoji}
                                onClick={() => toggleReaction(m, emoji)}
                                data-testid="chat-reaction-chip"
                                className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 border transition-colors ${
                                  ids.includes(user?.id)
                                    ? "bg-[#385C4B]/15 border-[#385C4B]/50 text-[#385C4B]"
                                    : "bg-white border-[#DCDCD3] text-[#6B6B61] hover:border-[#385C4B]/40"
                                }`}
                              >
                                <span>{emoji}</span>
                                <span
                                  className="font-mono text-[10px]"
                                  style={{ fontFamily: "Inter, sans-serif" }}
                                >
                                  {ids.length}
                                </span>
                              </button>
                            ) : null
                          )}
                        </div>
                      )}

                      {/* Emoji picker popover */}
                      {emojiPickerFor === m.id && (
                        <div
                          className={`absolute z-20 ${
                            mine ? "right-0" : "left-0"
                          } -top-12 bg-white border border-[#DCDCD3] rounded-full shadow-lg flex items-center gap-1 px-2 py-1.5`}
                          data-testid="chat-emoji-picker"
                        >
                          {QUICK_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => toggleReaction(m, emoji)}
                              className="text-lg hover:scale-125 transition-transform p-0.5"
                              data-testid="chat-emoji-option"
                            >
                              {emoji}
                            </button>
                          ))}
                          <button
                            onClick={() => setEmojiPickerFor(null)}
                            className="ml-1 p-1 rounded-full hover:bg-black/5 text-[#6B6B61]"
                          >
                            <X className="w-3 h-3" strokeWidth={2} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  </React.Fragment>
                );
              })}
              <div ref={msgEndRef} />
            </div>

            {/* Floating scroll-to-bottom pill */}
            {!nearBottom && (
              <button
                type="button"
                onClick={scrollToBottom}
                data-testid="chat-scroll-to-bottom"
                className="absolute right-6 bottom-28 bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] rounded-full shadow-lg flex items-center gap-2 pl-3 pr-4 py-2 transition-colors z-10"
              >
                <ArrowDown className="w-4 h-4" strokeWidth={1.75} />
                <span className="text-xs font-mono">
                  {newCount > 0 ? `${newCount} new` : "Latest"}
                </span>
              </button>
            )}

            {/* Reply banner */}
            {replyingTo && (
              <div
                className="px-3 sm:px-4 py-2 bg-[#EBEBE6] border-t border-[#DCDCD3] flex items-center gap-3"
                data-testid="chat-reply-banner"
              >
                <Reply className="w-4 h-4 text-[#385C4B] flex-shrink-0" strokeWidth={1.75} />
                <div className="flex-1 min-w-0 border-l-2 border-[#385C4B] pl-2">
                  <div
                    className="text-[11px] font-semibold text-[#385C4B] truncate"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    Replying to {replyingTo.sender_display_name}
                    {replyingTo.reply_selection_text && (
                      <span className="ml-1 text-[9px] uppercase tracking-widest text-[#6B6B61] font-mono">
                        · selection
                      </span>
                    )}
                  </div>
                  <div
                    className="text-xs text-[#6B6B61] truncate"
                    data-testid="chat-reply-banner-preview"
                  >
                    {replyingTo.deleted
                      ? "(deleted)"
                      : replyingTo.reply_selection_text || replyingTo.text}
                  </div>
                </div>
                <button
                  onClick={() => setReplyingTo(null)}
                  data-testid="chat-reply-cancel-button"
                  className="p-1.5 rounded-md text-[#6B6B61] hover:text-[#1C1C19] hover:bg-black/5 transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
            )}

            {/* Mention autocomplete */}
            {mentionState && mentionState.candidates.length > 0 && (
              <div
                className="absolute bottom-[72px] sm:bottom-[80px] left-3 sm:left-6 right-3 sm:right-auto sm:max-w-md bg-white border border-[#DCDCD3] rounded-lg shadow-xl z-30 overflow-hidden"
                data-testid="chat-mention-dropdown"
              >
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] px-3 py-2 border-b border-[#DCDCD3] bg-[#F5F5F0]">
                  Mention a member
                </div>
                {mentionState.candidates.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => insertMention(c)}
                    data-testid="chat-mention-option"
                    className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-[#385C4B]/10 transition-colors"
                  >
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center text-[#F5F5F0] font-semibold text-xs flex-shrink-0"
                      style={{ background: avatarColor(c.id) }}
                    >
                      {initialsOf(c.display_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-semibold text-[#1C1C19] truncate"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {c.display_name}
                      </div>
                      <div
                        className="text-xs font-mono text-[#6B6B61]"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        @{c.username}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={handleSend}
              className="p-3 sm:p-4 pr-4 sm:pr-6 pb-4 sm:pb-6 border-t border-[#DCDCD3] bg-white flex flex-col gap-2"
            >
              {/* Live upload progress rows — one per in-flight file with a
                  thin green bar and % counter. Removed when the upload
                  finishes (success or fail). */}
              {uploadProgress.length > 0 && (
                <div className="flex flex-col gap-1.5" data-testid="upload-progress-list">
                  {uploadProgress.map((p) => (
                    <div
                      key={p.key}
                      className="flex items-center gap-2 bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg px-3 py-1.5 text-xs"
                      data-testid={`upload-progress-${p.key}`}
                    >
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#385C4B] flex-shrink-0" />
                      <span className="max-w-[180px] truncate font-mono">{p.name}</span>
                      <div className="flex-1 h-1.5 bg-[#DCDCD3] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#385C4B] transition-all"
                          style={{ width: `${p.pct}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-mono tabular-nums text-[#385C4B] w-8 text-right">
                        {p.pct}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* Pending attachment chips */}
              {pendingAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="pending-attachments">
                  {pendingAttachments.map((a, idx) => (
                    <div
                      key={a.file_id || `pending-${idx}`}
                      className="flex items-center gap-2 bg-[#F5F5F0] border border-[#DCDCD3] rounded-full pl-2 pr-1 py-1 text-xs"
                    >
                      <span className="font-mono">
                        {a.content_type?.startsWith("image/") ? "📷" : a.name.endsWith(".pdf") ? "📄" : "📊"}
                      </span>
                      <span className="max-w-[180px] truncate">{a.name}</span>
                      <span className="text-[10px] text-[#6B6B61]">
                        {(a.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => removePendingAttachment(idx)}
                        data-testid={`remove-attachment-${idx}`}
                        className="w-5 h-5 rounded-full bg-white hover:bg-[#C8553D] hover:text-white text-[#6B6B61] text-xs flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex-1 border border-[#DCDCD3] rounded-2xl bg-[#F5F5F0] px-4 py-2 focus-within:ring-2 focus-within:ring-[#385C4B] focus-within:border-[#385C4B] focus-within:bg-white transition-all">
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={text}
                    onChange={onTextChange}
                    onKeyDown={(e) => {
                      // Enter sends, Shift+Enter inserts a newline (so the user
                      // can paste multi-line LPG dumps without losing rows).
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    placeholder="Type a message… use @ to mention · Shift+Enter for new line"
                    data-testid="chat-message-input"
                    className="w-full bg-transparent outline-none text-[#1C1C19] placeholder:text-[#A7A79D] resize-none leading-snug max-h-40"
                    style={{ fontFamily: "Work Sans, sans-serif" }}
                  />
                </div>
                {/* Attach file button — hidden native input triggered by paperclip icon */}
                <input
                  ref={attachInputRef}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.csv,.txt,.xlsx,.xls,image/*,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={handlePickFile}
                  className="hidden"
                  data-testid="chat-attach-file-input"
                />
                {/* Camera capture input — mobile-only visual, uses the
                    device rear camera to grab a photo directly. On
                    desktop the browser falls back to a file picker
                    (harmless). */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePickFile}
                  className="hidden"
                  data-testid="chat-camera-input"
                />
                <button
                  type="button"
                  onClick={() => attachInputRef.current?.click()}
                  disabled={uploadingAttachment}
                  data-testid="chat-attach-file-button"
                  title="Attach image / PDF / Excel / CSV (multi-select · max 10 MB each · ledger untouched)"
                  className="bg-white border border-[#DCDCD3] hover:border-[#385C4B]/40 text-[#385C4B] w-12 h-12 rounded-full transition-all disabled:opacity-50 flex items-center justify-center shadow-sm flex-shrink-0"
                >
                  {uploadingAttachment ? (
                    <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <Paperclip className="w-5 h-5" strokeWidth={1.75} />
                  )}
                </button>
                {/* Mobile camera capture — icon renders only on small
                    screens (sm:hidden) since desktops don't have a
                    reliable rear camera and the paperclip already lets
                    them upload from disk. */}
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadingAttachment}
                  data-testid="chat-camera-button"
                  title="Take a photo (mobile camera)"
                  className="sm:hidden bg-white border border-[#DCDCD3] hover:border-[#385C4B]/40 text-[#385C4B] w-12 h-12 rounded-full transition-all disabled:opacity-50 flex items-center justify-center shadow-sm flex-shrink-0"
                >
                  <Camera className="w-5 h-5" strokeWidth={1.75} />
                </button>
                <button
                  type="submit"
                  disabled={sending || (!text.trim() && pendingAttachments.length === 0)}
                  data-testid="chat-send-button"
                  className="bg-gradient-to-br from-[#3F6753] to-[#2D4A3C] hover:from-[#2D4A3C] hover:to-[#1F3528] text-[#F5F5F0] w-12 h-12 rounded-full transition-all disabled:opacity-50 flex items-center justify-center shadow-md hover:shadow-lg flex-shrink-0"
                >
                  {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" strokeWidth={2} />
                )}
              </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center p-8">
            <div>
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#385C4B] text-[#F5F5F0] text-sm shadow-md"
              >
                <Menu className="w-4 h-4" strokeWidth={1.75} />
                Open Groups
              </button>
              <MessageCircle
                className="w-12 h-12 mx-auto text-[#385C4B]/30 mb-4"
                strokeWidth={1.25}
              />
              <div
                className="text-xl font-semibold text-[#1C1C19] mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Select a group to start chatting
              </div>
              <div className="text-sm text-[#6B6B61]">
                {user?.role === "admin"
                  ? "Or create a new group from the sidebar."
                  : "Ask your admin to add you to a group."}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-[#8A6046]/10 border border-[#8A6046]/40 text-[#8A6046] text-sm px-4 py-2 rounded-md"
            data-testid="chat-error"
            onClick={() => setError("")}
          >
            {error} · click to dismiss
          </div>
        )}
      </section>

      {showCreate && (
        <CreateGroupModal
          onClose={() => setShowCreate(false)}
          onCreated={(g) => {
            setGroups((prev) => [g, ...prev]);
            setActiveGroupId(g.id);
            setShowCreate(false);
          }}
        />
      )}
      {/* WhatsApp-style Delete dialog — sender sees both options, non-sender
          only "Delete for me". Requested by user (Feb 2026). */}
      {deleteDialogMsg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDeleteDialogMsg(null)}
          data-testid="delete-message-dialog"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="text-lg font-semibold mb-2 text-[#1C1C19]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Delete message?
            </div>
            <div className="text-sm text-[#6B6B61] mb-5 leading-relaxed">
              {deleteDialogMsg.sender_id === user?.id
                ? "Aap sender ho — sabke chats se hata sakte ho, ya sirf apne se."
                : "Sirf aapke phone se hidden hoga. Baaki members ke pass message rahega."}
            </div>
            <div className="space-y-2">
              {deleteDialogMsg.sender_id === user?.id && (
                <button
                  type="button"
                  onClick={() => doDeleteForEveryone(deleteDialogMsg)}
                  data-testid="delete-for-everyone-btn"
                  className="w-full px-4 py-2.5 rounded-md bg-[#C8553D] hover:bg-[#B04A34] text-white text-sm font-mono uppercase tracking-wider"
                >
                  Delete for Everyone
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  hideMessageForMe(deleteDialogMsg.id);
                  setDeleteDialogMsg(null);
                  toast.success("Deleted for you (only)");
                }}
                data-testid="delete-for-me-btn"
                className="w-full px-4 py-2.5 rounded-md bg-white border border-[#DCDCD3] hover:border-[#385C4B]/40 text-[#1C1C19] text-sm font-mono uppercase tracking-wider"
              >
                Delete for Me
              </button>
              <button
                type="button"
                onClick={() => setDeleteDialogMsg(null)}
                data-testid="delete-cancel-btn"
                className="w-full px-4 py-2.5 rounded-md text-[#6B6B61] text-xs font-mono uppercase tracking-wider hover:bg-[#F5F5F0]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddMember && activeGroup && (
        <ManageMembersModal
          group={activeGroup}
          currentUserId={user?.id}
          onClose={() => setShowAddMember(false)}
          onUpdated={(g) => {
            setGroups((prev) => prev.map((x) => (x.id === g.id ? g : x)));
          }}
        />
      )}
      {showStaffSheets && (
        <StaffSheetsModal
          sheets={staffSheets}
          groupName={activeGroup?.name || ""}
          groupId={activeGroup?.id || ""}
          onClose={() => setShowStaffSheets(false)}
        />
      )}
      {showLpgSearch && (
        <LpgSearchModal
          onClose={() => setShowLpgSearch(false)}
          onJumpTo={(hit) => {
            setShowLpgSearch(false);
            // Switch to the hit's group. Chat loads its messages;
            // we also stash a highlight target so the specific
            // delivery card flashes + auto-scrolls once rendered.
            if (hit?.group_id) {
              setActiveGroupId(hit.group_id);
              setHighlightMsgId(hit.message_id || "");
            }
          }}
        />
      )}
      {showBulkComplaint && activeGroup && (
        <BulkComplaintModal
          groupId={activeGroup.id}
          groupName={activeGroup.name}
          onClose={() => setShowBulkComplaint(false)}
        />
      )}
      {showResend && activeGroup && (
        <ResendModal
          groupId={activeGroup.id}
          groupName={activeGroup.name}
          onClose={() => setShowResend(false)}
        />
      )}
      {showResendCreate && (
        <ResendCreateModal onClose={() => setShowResendCreate(false)} />
      )}
      {bulkResendOpen && (
        <ResendBulkPasteModal
          onClose={() => setBulkResendOpen(false)}
          onCommitted={() => setBulkResendOpen(false)}
          title="Chat · Bulk Resend Paste (Bharat Gas)"
          subtitle="Attribute rows to their sub-staff in one paste"
        />
      )}
      {/* WhatsApp-style right-click / long-press context menu */}
      {ctxMenu && (
        <MessageContextMenu
          anchor={{ x: ctxMenu.x, y: ctxMenu.y }}
          hasSelection={!!ctxMenu.selection}
          canFlag={
            !ctxMenu.message.lpg_entries &&
            !ctxMenu.message.deleted &&
            (user?.role === "admin" || ctxMenu.message.sender_id === user?.id)
          }
          canDelete={
            !ctxMenu.message.deleted &&
            (user?.role === "admin" || ctxMenu.message.sender_id === user?.id)
          }
          isComplaint={!!ctxMenu.message.is_complaint}
          onClose={() => setCtxMenu(null)}
          onReact={(emoji) => toggleReaction(ctxMenu.message, emoji)}
          onReply={() =>
            openReplyWithSelection(ctxMenu.message, ctxMenu.selection)
          }
          onCopy={() =>
            handleCopyMessage(ctxMenu.message, ctxMenu.selection)
          }
          onFlag={() => handleToggleComplaint(ctxMenu.message)}
          onInfo={() => setInfoMsgId(ctxMenu.message.id)}
          onDelete={() => handleDeleteMessage(ctxMenu.message)}
        />
      )}
      {infoMsgId && (
        <MessageInfoModal
          messageId={infoMsgId}
          onClose={() => setInfoMsgId(null)}
        />
      )}
      {descEditorOpen && activeGroup && (
        <GroupDescriptionEditor
          group={activeGroup}
          onClose={() => setDescEditorOpen(false)}
          onSaved={(newDesc) =>
            setGroups((prev) =>
              prev.map((g) =>
                g.id === activeGroup.id ? { ...g, description: newDesc } : g
              )
            )
          }
        />
      )}
    </div>
  );
}

/**
 * BharatGas LPG IDs are 17 digits prefixed with "10000000". Operators only
 * care about the *meaningful* tail — the leading zeros after the prefix are
 * just padding from the IndianOil/BharatGas portal export. We keep storing
 * the full 17 digits internally (tick/untick, dedup, message ID, etc.) and
 * only shorten the *display*.
 *
 * Examples:
 *   "10000000035316245" → "35316245"   (prefix stripped, 1 leading zero removed)
 *   "10000000121936083" → "121936083"  (prefix stripped, no leading zeros)
 *   "7100000003872993"  → "7100000003872993"  (IndianOil — left alone)
 *   "706805"            → "706805"     (HPCL short ID — left alone)
 */
function displayLpgId(lpg) {
  if (!lpg || typeof lpg !== "string") return lpg || "";
  if (/^10000000\d{9}$/.test(lpg)) {
    return lpg.slice(8).replace(/^0+/, "") || "0";
  }
  return lpg;
}



// StaffSheetsModal was extracted to /app/frontend/src/components/StaffSheetsModal.jsx (Feb 2026).
// Imported at the top of this file. Business logic unchanged — pure code move.


function ManageMembersModal({ group, currentUserId, onClose, onUpdated }) {
  const [allUsers, setAllUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState("");

  const reload = async () => {
    try {
      const [users, ms] = await Promise.all([
        api.get("/admin/users"),
        api.get(`/groups/${group.id}/members`),
      ]);
      setAllUsers(users.data);
      setMembers(ms.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  // Filter out role=team_staff — those users are OWN-side team members who
  // dispatch bookings via Bulk Paste and must NEVER appear inside an agency
  // chat. Hard restriction requested by user (Feb 2026). Backend also
  // rejects any request that tries to add them anyway.
  const nonMembers = allUsers.filter(
    (u) => !members.some((m) => m.id === u.id) && u.role !== "team_staff"
  );

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (selected.size === 0) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/groups/${group.id}/members`, {
        member_ids: Array.from(selected),
      });
      onUpdated(data);
      setSelected(new Set());
      await reload();
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member) => {
    if (member.role === "admin") {
      setError("Admins can't be removed from groups");
      return;
    }
    const ok = window.confirm(
      `Remove ${member.display_name || member.username} from ${group.name}?\n\nThey will lose access to this group's chat.`
    );
    if (!ok) return;
    setRemoving(member.id);
    setError("");
    try {
      const { data } = await api.delete(
        `/groups/${group.id}/members/${member.id}`
      );
      onUpdated(data);
      await reload();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      data-testid="manage-members-modal"
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl border border-[#DCDCD3] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h3
            className="text-lg font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Manage Members · {group.name}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#F5F5F0] rounded"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[#6B6B61]" strokeWidth={1.75} />
          </button>
        </div>
        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex-shrink-0">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Current members */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B6B61] font-mono mb-2">
              Current ({members.length})
            </div>
            <div className="border border-[#DCDCD3] rounded-md divide-y divide-[#F0F0EA]">
              {members.length === 0 && (
                <div className="text-center py-4 text-sm text-[#6B6B61]">
                  No members yet.
                </div>
              )}
              {members.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-3 px-3 py-2"
                  data-testid={`current-member-${m.username}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1C1C19] truncate flex items-center gap-1.5">
                      {m.display_name || m.username}
                      {m.role === "admin" && (
                        <span className="text-[9px] uppercase tracking-wider font-mono bg-[#385C4B]/10 text-[#385C4B] px-1.5 py-0.5 rounded">
                          admin
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#6B6B61] font-mono truncate">
                      @{m.username}
                    </div>
                  </div>
                  {m.role !== "admin" && m.id !== currentUserId && (
                    <button
                      onClick={() => handleRemove(m)}
                      disabled={removing === m.id}
                      data-testid={`remove-member-${m.username}`}
                      title="Remove from group"
                      className="p-1.5 rounded text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      {removing === m.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <UserMinus className="w-3.5 h-3.5" strokeWidth={2} />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add new */}
          {nonMembers.length > 0 && (
            <form onSubmit={handleAdd}>
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B61] font-mono mb-2">
                Add new ({nonMembers.length} available)
              </div>
              <div className="border border-[#DCDCD3] rounded-md max-h-48 overflow-y-auto">
                {nonMembers.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-[#F5F5F0] cursor-pointer border-b border-[#F0F0EA] last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggle(u.id)}
                      data-testid={`add-member-checkbox-${u.username}`}
                      className="w-4 h-4 accent-[#385C4B]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1C1C19] truncate">
                        {u.display_name || u.username}
                      </div>
                      <div className="text-[11px] text-[#6B6B61] font-mono truncate">
                        @{u.username} · {u.role}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <button
                type="submit"
                disabled={saving || selected.size === 0}
                data-testid="add-member-submit"
                className="mt-3 w-full px-4 py-2 text-sm bg-[#385C4B] text-white rounded-md hover:bg-[#2D4A3C] disabled:opacity-40"
              >
                {saving ? "Adding..." : `Add ${selected.size || ""} member${selected.size === 1 ? "" : "s"}`}
              </button>
            </form>
          )}
        </div>

        <div className="mt-4 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm border border-[#DCDCD3] rounded-md hover:bg-[#F5F5F0]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin/users")
      .then(({ data }) => {
        // Hide team_staff role users from the create-group member picker
        // — they must NEVER be added to an agency chat (backend also
        // rejects them). Requested by user (Feb 2026).
        setUsers((data || []).filter((u) => u.role !== "team_staff"));
      })
      .catch((e) => setError(formatApiError(e)));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/groups", {
        name: name.trim(),
        member_ids: Array.from(selected),
      });
      onCreated(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="create-group-modal"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="text-2xl font-semibold tracking-tight text-[#1C1C19] mb-5"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          New Group
        </h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block">
              Group name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="create-group-name-input"
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B]"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block">
              Members ({selected.size})
            </label>
            <div className="border border-[#DCDCD3] rounded-md max-h-56 overflow-y-auto">
              {[...users]
                .sort((a, b) =>
                  String(a.display_name || a.username || "").localeCompare(
                    String(b.display_name || b.username || ""),
                    "en",
                    { numeric: true, sensitivity: "base" }
                  )
                )
                .map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-[#F5F5F0] cursor-pointer border-b border-[#DCDCD3] last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    data-testid="create-group-member-checkbox"
                    className="accent-[#385C4B]"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#1C1C19]">{u.display_name}</div>
                    <div className="text-xs font-mono text-[#6B6B61]">
                      @{u.username} · {u.role}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {error && (
            <div className="text-sm text-[#8A6046] bg-[#8A6046]/10 border border-[#8A6046]/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-[#6B6B61] hover:text-[#1C1C19] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="create-group-submit"
              className="bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] px-5 py-2 rounded-md transition-colors disabled:opacity-60"
            >
              {saving ? "Creating…" : "Create Group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================================================
// LpgSearchModal — quick "did we already DAC this LPG ID?" lookup. Accepts
// any 4+ digit substring (but typically full 16-digit LPG ID). Returns every
// hit across the viewer's visible groups with agency + date + DAC + status.
// ===========================================================================
// LpgSearchModal + SingleHitList were extracted to /app/frontend/src/components/LpgSearchModal.jsx (Feb 2026).


// Bulk result — Matched section (with details) + Not Found section (LPG IDs only)
// BulkResultView was extracted alongside LpgSearchModal (Feb 2026) — see components/LpgSearchModal.jsx.



// ===========================================================================
// BulkComplaintModal — agency owner pastes a list of LPG IDs that have wrong
// DACs; backend matches each against THIS group's data and auto-flags
// matches as complaints (same as the per-entry "wrong" toggle, so emails /
// sheet sync / WS broadcast all fire). Unmatched IDs are surfaced as
// "admin esko nahi diya".
// ===========================================================================
function BulkComplaintModal({ groupId, groupName, onClose }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  // Accept any 6-20 digit number — covers every operator + BharatGas tails:
  //   • HPCL short        → 6 digits (e.g. 615411)
  //   • BharatGas tail    → 8-9 digits (e.g. 58857460, 119329072) — backend
  //                          auto-expands to the 17-digit full ID before lookup
  //   • IndianOil         → 16 digits (e.g. 7100000003872993)
  //   • BharatGas full    → 17 digits (e.g. 10000000119329072)
  const detected = (q.match(/\d{6,20}/g) || []).length;

  const run = async () => {
    const numbers = (q.match(/\d{6,20}/g) || []);
    if (!numbers.length) {
      setErr("Kam se kam 1 LPG ID paste karein (6+ digits)");
      return;
    }
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const { data } = await api.post(
        `/groups/${groupId}/lpg/bulk-complaint`,
        { lpg_ids: numbers }
      );
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    run();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
      onClick={onClose}
      data-testid="bulk-complaint-modal"
    >
      <div
        className="bg-white rounded-lg w-full max-w-3xl border border-[#DCDCD3] shadow-xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#DCDCD3]">
          <div>
            <h2
              className="text-lg font-semibold text-[#1C1C19] flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <Flag className="w-5 h-5 text-red-700" />
              Bulk Complaint · {groupName}
            </h2>
            <p className="text-xs text-[#6B6B61] mt-0.5">
              LPG IDs paste karein (jin ka DAC galat aaya hai). Jo IDs is
              agency ke admin record me milengi unka complaint{" "}
              <b>auto-mark</b> ho jayega (Wrong DAC). Jo nahi milti — wo{" "}
              <b>skip</b> ho jayengi (admin ne abhi nahi bheja).
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#6B6B61] hover:text-[#1C1C19] p-1"
            data-testid="bulk-complaint-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 border-b border-[#DCDCD3] flex flex-col gap-2">
          <textarea
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setResult(null);
            }}
            placeholder={`7200000027903025\n7500000075091335\n7200000038577676\n7000000011426273`}
            rows={6}
            data-testid="bulk-complaint-input"
            className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono resize-y focus:outline-none focus:border-red-700"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-[#6B6B61]">
              Detected: <b>{detected}</b> LPG ID(s)
            </span>
            <button
              type="submit"
              disabled={busy || !q.trim()}
              data-testid="bulk-complaint-submit"
              className="px-4 py-2 bg-red-700 text-white text-sm rounded hover:bg-red-800 disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
              File Complaints
            </button>
          </div>
          {err && (
            <div
              className="text-xs text-red-700 bg-red-500/10 border border-red-500/30 rounded px-3 py-1.5"
              data-testid="bulk-complaint-error"
            >
              {err}
            </div>
          )}
        </form>

        <div className="overflow-y-auto p-4 space-y-3" style={{ minHeight: "120px" }}>
          {!result && !busy && (
            <div className="text-center text-sm text-[#6B6B61] py-8" data-testid="bulk-complaint-empty">
              Result yahan dikhega — Complaints filed, Already-flagged, Not-dispatched.
            </div>
          )}
          {result && (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <SummaryPill
                  label="Filed"
                  count={result.complaint_created?.length || 0}
                  cls="bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
                  testid="bulk-complaint-filed-count"
                />
                <SummaryPill
                  label="Already Flagged"
                  count={result.already_complaint?.length || 0}
                  cls="bg-amber-500/15 text-amber-700 border-amber-500/30"
                  testid="bulk-complaint-already-count"
                />
                <SummaryPill
                  label="Not Dispatched"
                  count={result.not_dispatched?.length || 0}
                  cls="bg-red-500/15 text-red-700 border-red-500/30"
                  testid="bulk-complaint-skipped-count"
                />
              </div>

              {result.complaint_created?.length > 0 && (
                <ResultSection
                  title="✅ Complaints Filed (Wrong DAC marked)"
                  cls="border-emerald-500/30 bg-emerald-500/5"
                  testid="bulk-complaint-filed-section"
                >
                  {result.complaint_created.map((row) => (
                    <ResultRow
                      key={row.lpg_id}
                      lpgId={row.lpg_id}
                      dac={row.dac_code}
                      date={row.date}
                    />
                  ))}
                </ResultSection>
              )}

              {result.already_complaint?.length > 0 && (
                <ResultSection
                  title="⚠️ Duplicate — Complaint already open (skipped)"
                  cls="border-amber-500/30 bg-amber-500/5"
                  testid="bulk-complaint-already-section"
                >
                  {result.already_complaint.map((row) => {
                    const since = row.already_open_since || row.marked_at;
                    const sinceStr = since
                      ? `since ${String(since).slice(0, 10)}`
                      : "";
                    const byStr = row.marked_by ? `by ${row.marked_by}` : "";
                    const extra = [byStr, sinceStr].filter(Boolean).join(" · ");
                    return (
                      <ResultRow
                        key={row.lpg_id}
                        lpgId={row.lpg_id}
                        dac={row.dac_code}
                        date={row.date}
                        extra={extra}
                      />
                    );
                  })}
                </ResultSection>
              )}

              {result.not_dispatched?.length > 0 && (
                <ResultSection
                  title="❌ Admin ne abhi nahi bheja (Not Dispatched)"
                  cls="border-red-500/30 bg-red-500/5"
                  testid="bulk-complaint-skipped-section"
                >
                  {result.not_dispatched.map((lpg) => (
                    <div
                      key={lpg}
                      className="text-xs font-mono text-[#1C1C19] py-1"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {lpg}
                    </div>
                  ))}
                </ResultSection>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryPill({ label, count, cls, testid }) {
  return (
    <div
      className={`border rounded px-3 py-2 ${cls}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-bold">{count}</div>
    </div>
  );
}

function ResultSection({ title, cls, testid, children }) {
  return (
    <div
      className={`border rounded p-3 ${cls}`}
      data-testid={testid}
    >
      <div className="text-xs font-semibold text-[#1C1C19] mb-2">{title}</div>
      <div className="space-y-1 max-h-60 overflow-y-auto">{children}</div>
    </div>
  );
}

function ResultRow({ lpgId, dac, date, extra }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2 text-xs py-1 border-b border-black/5 last:border-b-0">
      <span
        className="font-mono text-[#1C1C19] font-semibold"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {lpgId}
      </span>
      {dac && (
        <span
          className="font-mono text-[#6B6B61]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          DAC {dac}
        </span>
      )}
      {date && <span className="text-[#6B6B61]">· {date}</span>}
      {extra && <span className="text-[#6B6B61]">· {extra}</span>}
    </div>
  );
}


/**
 * Resend DAC modal — reads pending resend rows from the global Resend
 * Google Sheet (configured in Admin → Sheets), filtered to this agency by
 * fuzzy/alias agency-name match on the backend. Agency confirms with
 * checkbox + Select All; Tick → instant webhook back to the sheet.
 */
// ResendModal was extracted to /app/frontend/src/components/ResendModal.jsx (Feb 2026).
// Imported at the top of this file. Business logic unchanged.



/**
 * "+ New Resend" modal — staff submits a fresh resend DAC request straight
 * from the portal. Agency-name typeahead shows live match preview from
 * `/api/resend/resolve`; spelling-mismatch warning ("Did you mean X?")
 * requires explicit confirm. On success the backend posts a system card
 * into the matched agency's chat AND fires a sheet append.
 */
// ResendCreateModal was extracted to /app/frontend/src/components/ResendCreateModal.jsx (Feb 2026).

